import { correctionRu, correctionUz } from './installation-correction-labels.js';
/**
 * i18n через react-i18next. Текущие языки: ru (default) / uz.
 * Переключение в Profile sheet — мгновенное (live rerender компонентов
 * через useTranslation hook).
 *
 * Стратегия плюрализации — стандартный i18next-плюрал-resolver,
 * для русского и узбекского суффиксы _one/_few/_many.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { persistent } from './storage.js';
export type Lang = 'ru' | 'uz';
const ru = {
  applicationSign: {
    signAndComplete: 'Подписать и оформить', completed: 'Заявка подписана, заказ оформлен',
    requiredHint: 'Для оформления заказа нужна подпись клиента. Проверьте заявку и подпишите её. Если закрыть окно, заказ останется неоформленным.',
    draw: 'Подпишите здесь', clear: 'Очистить подпись', title: 'Подпись заявки', sign: 'Подписать заявку', saved: 'Заявка подписана, PDF сохранён',
    hint: 'Покажите клиенту заявку к договору, затем попросите расписаться на экране.',
    consent: 'Состав, размеры и стоимость заказа проверены. С условиями публичной оферты ознакомлен и согласен.',
    preview: 'Посмотреть PDF заявки', download: 'Скачать подписанную заявку',
    signedAt: 'Заявка подписана {{date}} (Ташкент). Сохранён оригинал на момент подписания.',
    unavailable: 'Подпись доступна назначенному замерщику после оформления заказа и до завершения монтажа.',
    retry: 'Повторить',
    needsNetwork: 'Подпись заявки требует интернета: клиент подписывает документ, который формирует сервер. Изделия и фото уже сохранены — оформите заказ, когда появится связь.',
    offlineHint: 'Нет связи с сервером. Покажите клиенту состав, размеры и цены ниже — он подписывает эту сводку. При появлении сети сервер сверит её с заявкой: если что-то изменилось (например, курс), подпись попросят повторить.',
    offlinePriceMissing: 'Нет связи, а цена части изделий неизвестна (прайс не загружен на телефон). Оформите заказ, когда появится интернет.',
    offlineQueued: 'Подпись сохранена — заказ оформится, когда появится сеть',
    total: 'Итого',
  },
  clientRequestsPaused: { title: 'Заказ через приложение', message: 'Раздел временно недоступен', hint: 'Запрос расчёта и оформление новых заказов через приложение приостановлены.', back: 'На главную' },
  plannedMeasureArrival: 'приезд ~{{time}}',
  installationCorrection: correctionRu,
  rejectMeasureDialog: {
    reasonGoingToClient: 'Иду к клиенту {{customer}} ({{code}})',
    savedOffline: 'Сохранено офлайн — отправим при сети',
  },
  signActSheet: {
    noSignatureTitle: 'Нет подписи',
    noSignatureBody: 'Попроси клиента расписаться на экране.',
    signedTitle: 'Акт подписан',
    signedBody: 'PDF сохранён — можно отправить клиенту.',
    saveFailed: 'Не удалось сохранить',
    downloadFailed: 'Не удалось скачать',
    alreadySignedTitle: 'Акт уже подписан',
    downloadPdf: 'Скачать PDF',
    alreadySignedBody:
      'Клиент {{customer}} уже подписал акт по заказу {{order}}. Скачайте PDF и отправьте клиенту в WhatsApp или Telegram.',
    resign: 'Подписать заново',
    signTitle: 'Подпись клиента · {{order}}',
    saving: 'Сохранение…',
    doneSigned: 'Готово · подписан',
    hint: 'Дайте клиенту подписаться пальцем или стилусом. После — мы сразу сгенерируем PDF акта приёмки и его можно будет отправить.',
    orderLabel: 'Заказ {{order}}',
    signedAndCompletedTitle: 'Заказ завершён',
    signedAndCompletedBody: 'Акт подписан, заказ завершён.',
    signAndComplete: 'Подписать и завершить',
  },
  acceptPaymentSheet: {
    title: 'Способ оплаты',
    toPay: 'К оплате:',
    workDuration: 'Срок работы:',
    daysUnit: '{{days}}',
    placingOrder: 'Оформляю заказ…',
    confirmAndPlace: 'Подтвердить и оформить',
    pickMethod: 'Выберите способ оплаты',
    hintLine1: 'Это пожелание по оплате — менеджер свяжется',
    hintLine2: 'и подтвердит окончательные условия.',
    orderPlacedTitle: 'Заказ оформлен',
    orderPlacedDesc: 'Менеджер свяжется по поводу замера и установки',
    failed: 'Не удалось',
    methodCashTitle: 'Наличными при установке',
    methodCashSubtitle: 'Оплата мастеру в день монтажа',
    methodCardTitle: 'Картой онлайн',
    methodCardSubtitle: 'Менеджер пришлёт ссылку Payme/Click',
    methodTransferTitle: 'Банковский перевод',
    methodTransferSubtitle: 'Реквизиты выдаст менеджер',
    methodUzumTitle: 'Оплата через UZUM',
    methodUzumSubtitle: 'До 24 месяцев без переплат',
    methodAlifTitle: 'Оплата через Alif Nasiya',
    methodAlifSubtitle: 'Без банка, по паспорту',
  },
  quoteDetailPage: {
    appBarTitle: 'Запрос',
    notFound: 'Запрос не найден',
    statusPendingLabel: 'Ждёт ответа менеджера',
    statusPendingHint: 'Менеджер свяжется с расчётом',
    statusQuotedLabel: 'Расчёт готов',
    statusQuotedHint: 'Ожидаем решение клиента',
    statusAcceptedLabel: 'Принят',
    statusAcceptedHint: 'Создан заказ',
    statusRejectedLabel: 'Отказ',
    statusRejectedHint: 'Клиент отказался',
    statusExpiredLabel: 'Истёк',
    statusExpiredHint: '7 дней без ответа',
    clientBlock: 'Клиент',
    nameLabel: 'Имя',
    phoneLabel: 'Телефон',
    addressBlock: 'Адрес установки',
    itemsBlock: 'Позиции · {{count}}',
    categoryFallback: 'Категория',
    mmUnit: 'мм',
    clientNoteBlock: 'Комментарий клиента',
    managerQuoteBlock: 'Расчёт менеджера',
    totalSum: 'Итоговая сумма',
    workTerm: 'Срок работы',
    daysUnit: 'день',
    quotedAtValidUntil: 'Расчёт от {{from}} · действителен до {{until}}',
    orderCreatedBlock: 'Создан заказ',
    openOrder: 'Открыть заказ →',
    quoteSentToast: 'Расчёт отправлен клиенту',
    adjustQuote: 'Скорректировать расчёт',
    respondToClient: 'Ответить клиенту',
    totalSumField: 'Итоговая сумма (сум)',
    workDaysField: 'Срок работы (рабочих дней)',
    commentField: 'Комментарий (необязательно)',
    commentPlaceholder: 'Дополнительные детали, условия…',
    sending: 'Отправляю…',
    updateQuote: 'Обновить расчёт',
    sendToClient: 'Отправить клиенту',
    clientWillSee: 'Клиент увидит:',
    rejectAcceptedToast: 'Отказ принят',
    failedToast: 'Не удалось',
    rejectReasonBlock: 'Причина отказа (необязательно)',
    rejectPlaceholder: 'Слишком дорого, нашёл дешевле…',
    rejectButton: 'Отказаться',
    acceptAndOrder: 'Принять и оформить · {{sum}}',
    notSuitable: 'Не подходит',
    afterAcceptHint: 'После принятия будет создан заказ. Менеджер свяжется по оплате.',
  },
  quotesListPage: {
    title: 'Запросы клиентов',
    pendingCountOne: '{{count}} новый запрос',
    pendingCountFew: '{{count}} новых запроса',
    pendingCountMany: '{{count}} новых запросов',
    awaitYourReply: 'ждут вашего ответа',
    allHandled: 'Все обработано',
    tabNew: 'Новые',
    tabWaitingClient: 'Ждут клиента',
    tabAccepted: 'Принятые',
    tabRejected: 'Отказы',
    tabAll: 'Все',
    emptyTitle: 'Пусто',
    emptyAllHandled: 'Все запросы обработаны.',
    emptyNoneInSection: 'Запросов в этом разделе нет.',
    statusPendingReview: 'Ждёт ответа',
    statusQuoted: 'Ждёт решения клиента',
    statusAccepted: 'Принят',
    statusRejected: 'Отказ',
    statusExpired: 'Истёк',
    itemsOne: 'позиция',
    itemsFew: 'позиции',
    itemsMany: 'позиций',
    daysOne: 'день',
    daysFew: 'дня',
    daysMany: 'дней',
  },
  dealerOrderPage: {
    appBarTitle: 'Заказ',
    acceptedToastTitle: 'Заказ принят',
    acceptedToastBody: 'Спасибо за подтверждение!',
    acceptFailed: 'Не удалось',
    shippingLabel: 'Доставка',
    trackLabel: 'трек',
    paidOfTotal: 'Оплачено {{paid}} из {{total}}',
    debtLabel: 'долг',
    itemsTitle: 'Позиции',
    mm: 'мм',
    quantityLabel: 'Кол-во',
    pricePerPiece: '{{price}} за шт.',
    confirmReceiptBtn: 'Подтвердить получение',
    confirmReceiptTitle: 'Подтвердить получение?',
    confirmReceiptDesc: 'Заказ будет закрыт. Это действие нельзя отменить.',
    recordPaymentBtn: 'Записать оплату',
    paymentSheetTitle: 'Записать оплату',
    paymentAmountLabel: 'Сумма (сум)',
    paymentMethodLabel: 'Способ оплаты',
    paymentMethodCash: 'Наличные',
    paymentMethodCard: 'Карта',
    paymentMethodTransfer: 'Перечисление',
    paymentCommentLabel: 'Комментарий (необязательно)',
    paymentSubmit: 'Записать',
    paymentSaving: 'Сохраняю…',
    paymentSavedTitle: 'Оплата записана',
    paymentSavedBody: 'Бухгалтер проверит поступление средств.',
    paymentFailed: 'Не удалось записать оплату',
    paymentAmountInvalid: 'Введите сумму больше нуля',
    paymentNote:
      'Оплата отметится как заявленная вами — бухгалтер подтвердит фактическое поступление.',
    statusDraft: 'Черновик',
    statusOrdered: 'Оформлен',
    statusInProduction: 'В производстве',
    statusReady: 'Готов',
    statusShipped: 'Отправлен — ждём подтверждения',
    statusAccepted: 'Получен',
    statusRefused: 'Отменён',
    productRoll: 'Ролло',
    productCombo: 'Комбо',
    productPlisse: 'Плиссе',
    productRoman: 'Римские',
    productTextile: 'Текстильные',
    productHorizontal: 'Горизонтальные',
    productMosquito: 'Москитные',
    productBamboo: 'Бамбуковые',
    productWood: 'Деревянные',
    productCustom: 'Прочее',
  },
  calcFlat: {
    selectPlaceholder: '— выбрать —',
    selectRequired: 'Выберите',
    expand: 'развернуть',
    collapse: 'свернуть',
    noneOption: '— не выбирать —',
    pcs: 'шт',
    sizePreview: '{{w}} × {{h}}',
  },
  calcWizard: {
    addedToRooms: 'Добавлено в {{count}} комнат',
    savedOffline: 'Сохранено офлайн — отправим при сети',
  },
  step2Size: {
    unitArea: 'м²',
    unitPerimeter: 'м',
  },
  step4Ribbon: {
    pricePerMeter: '{{price}} / м',
  },
  step5Mechanism: {
    pricePerItem: '{{sum}} за изделие',
  },
  step6Cornice: {
    pricePerMeter: '{{price}} / м',
  },
  step7Total: {
    corniceColorLabel: 'Цвет карниза',
    corniceColorPlaceholder: 'Белый, серебро, коричневый…',
    windowPhotoLabel: 'Фото окна',
    notesLabel: 'Комментарий к изделию',
    notesPlaceholder: 'Особенности, пожелания…',
    maxDiscountNote: 'Макс. скидка {{pct}}%',
    sumUnit: 'сум',
    copyToRoomsLabel: 'Добавить это же изделие в комнаты',
    currentRoomFallback: 'текущую',
    copyToRoomsSummary:
      'Сохранится в «{{name}}» + ещё {{count}}. Одинаковые параметры — для разных комнат.',
    uploadFailed: 'Не удалось загрузить',
    removePhoto: 'Убрать фото',
    uploading: 'Загружаю…',
    attachWindowPhoto: 'Прикрепить фото окна',
  },
  clientPaymentSheet: {
    title: 'Оплата заказа',
    paidFully: 'Оплачено полностью',
    remaining: 'К оплате',
    paidOfTotal: 'Оплачено {{paid}} из {{total}}',
    paymentHistory: 'История оплат',
    methodCash: 'Наличные',
    methodCard: 'Карта',
    methodTransfer: 'Перевод',
    payToTitle: 'Куда оплатить',
    copy: 'Скопировать реквизиты',
    copied: 'Скопировано',
    helpTitle: 'Справка по оплате',
    helpBody: 'После оплаты укажите сумму и прикрепите фото чека. Заявка появится в «Мои заявки». Бухгалтер сверит поступление и подтвердит оплату; до этого остаток заказа не меняется.',
    amountLabel: 'Сумма оплаты, сум',
    sendClaim: 'Отправить заявку',
    enterAmount: 'Укажите сумму оплаты',
    attachReceiptFirst: 'Прикрепите фото чека оплаты',
    claimSent: 'Заявка отправлена',
    claimSentHint: 'Бухгалтер сверит поступление денег и подтвердит оплату.',
    claimHistory: 'Мои заявки',
    claimPending: 'На проверке',
    claimConfirmed: 'Подтверждена',
    claimRejected: 'Отклонена',
    claimRejectReason: 'Причина',
    claimLoadError: 'Не удалось загрузить заявки',
    uploadedReceipts: 'Мои загруженные чеки',
    openReceipt: 'Открыть загруженный чек',
  },
  orderPhotoBlock: {
    photoAdded: 'Фото добавлено',
    photosAdded: 'Добавлено {{count}} фото',
    compressionNote: 'было {{was}}, экономия {{pct}}%',
    offlineNote: 'Загрузим, когда появится сеть',
    addPhotoError: 'Не удалось добавить фото',
    deleteError: 'Не удалось удалить',
    finishedWorkPhotos: 'Фото готовой работы',
    photos: 'Фото',
    whatToPhotograph: 'Что фотографировать',
    photoHelpTitle: 'Справка по фото',
    kindFacade: 'фасад',
    kindObject: 'объект',
    kindMeasure: 'замер',
    kindDone: 'готово',
    kindReceipt: 'чек оплаты',
    kindDefect: 'брак',
    receiptAttachLabel: 'Чек / скрин оплаты',
    receiptAttached: 'Чек прикреплён',
    previousClaimReceipts: 'Чеки предыдущей заявки. Если загрузка не удалась, повторите её при сети или удалите чек.',
    retryUpload: 'Повторить',
    kindFacadeHint: 'подъезд / вход. Помогает коллегам найти адрес при следующем визите.',
    kindObjectHint: 'окна / помещения, куда ставим жалюзи.',
    kindMeasureHint: 'чертежи и пометки с размерами карандашом.',
    kindDoneHint: 'фото установленных жалюзи после монтажа.',
    badgeHint: 'Тип каждой фотографии виден бейджем в её углу.',
    uploadFailed: 'ошибка',
    uploading: 'загрузка',
    deletePhoto: 'Удалить фото',
    emptyEditHint: 'Снимите окно/объект — поможет монтажникам.',
    noPhotosYet: 'Фото пока нет.',
    clientEmptyDoneTitle: 'Установщик пока не загрузил фото',
    clientEmptyPendingTitle: 'Фото появятся после завершения работы',
    clientEmptyDoneHint:
      'Если в течение пары дней фото так и не появились — обратитесь к менеджеру.',
    clientEmptyPendingHint:
      'Когда монтажники закончат установку, вы увидите здесь фото готовой работы.',
  },
  paymentSheet: {
    savedOffline: 'Сохранено офлайн — отправим при сети',
    sum: 'сум',
    payerInn: 'ИНН',
    payerFill: 'Заполнить',
    payerName: 'Наименование',
    payerNotFound: 'По этому ИНН ничего не найдено',
    payerLookupFailed: 'Не удалось найти по ИНН, попробуйте позже',
    payerLookupOffline: 'Для поиска по ИНН нужен интернет',
    noAccountsWarning:
      'Нет подходящих счетов. Попросите администратора добавить счёт получения.',
    noCardsHint:
      'Нет подходящих карт. Добавьте личную в «Профиль → Мои карты» или попросите администратора добавить карту фирмы.',
    receivingAccount: 'Счёт получения',
    account: 'Счёт',
    accountNotSpecified: 'Счёт не указан',
    personalCash: 'Личная касса · {{name}}. Сдадите деньги финотделу.',
    originalAccount: 'Прежний счёт',
    originalAccountKept: 'сохраняется при правке суммы',
    groupReceiverCards: 'Карты получателя',
    groupBankAccounts: 'Банковские счета фирмы',
    groupTerminals: 'Терминалы',
    groupMyCards: 'Мои карты (в мою кассу)',
    groupCompanyCards: 'Карты фирмы',
    custodyMine: '✓ В вашу кассу — сдадите вместе с налом.',
    custodyReceiver: 'В кассу получателя — сотрудник сдаст деньги финотделу.',
    custodyFirm: 'Уйдёт напрямую фирме — не в вашу кассу. Бухгалтер подтвердит поступление.',
    selectAccountOption: '— выбери {{target}} —',
    targetCardAcc: 'карту',
    targetTerminalAcc: 'терминал',
    targetAccountAcc: 'счёт',
    method_CASH: 'Наличные',
    method_CARD_P2P: 'Перевод на карту',
    method_CARD_TERMINAL: 'Карта (терминал)',
    method_TRANSFER: 'Перечисление',
    method_UZUM_NASIYA: 'Оплата через UZUM',
    method_ALIF_NASIYA: 'Оплата через Alif Nasiya',
  },
  problemDialog: {
    savedOffline: 'Сохранено офлайн — отправим при сети',
  },
  orderDetailPage: {
    pendingSync: 'Ждёт сети',
    installationHistoryView: 'Только просмотр',
    installationHistoryHint: 'Ваш отчёт о монтаже сохранён. Следующий выезд назначается отдельно.',
    clientQr: 'QR для клиента',
    clientQrTitle: 'Показать клиенту QR для отслеживания заказа',
    callAttemptLogged: 'Попытка дозвона зафиксирована',
    willSendWhenOnline: 'Будет отправлено, когда появится сеть',
    formalizeNeedsAdvance: 'Сначала примите аванс не меньше {{pct}}% — не хватает {{sum}}',
    formalizeModeLoading: 'Проверяем режим оформления заказа…',
    formalizeModeUnavailable:
      'Не удалось проверить режим оформления. Подключитесь к интернету и повторите.',
    formalizePickRooms:
      'Выберите нужные замеры галочками в разделе «Расчёт» — минимальная сумма появится после выбора.',
    paymentEditTitle: 'Исправить платёж',
    paymentEditSaved: 'Платёж исправлен',
    paymentEditHint:
      'Поправьте сумму, способ оплаты или счёт получения, если ошиблись при вводе. Изменение попадёт в историю (аудит).',
    paymentEditAmount: 'Сумма, сум',
    orderProgress: 'Прогресс заказа',
    rescheduleMeasure: 'Перенести замер',
    overdueUnassignHint:
      'Замер просрочен более {{hours}} ч и не начат — он снят с вас и передан в распределение. Действия по нему недоступны.',
    chosenByClient: 'Выбран клиентом',
    openPayment: 'Оплата и заявки',
    resultWorkTitle: 'Готовая работа',
    resultWorkHint: 'При желании добавьте фото готовой работы. Клиент сможет их посмотреть.',
    measureRescheduled: 'Замер перенесён',
    managerNotified: 'Менеджеру отправлено уведомление',
    rescheduleFailed: 'Не удалось перенести',
    rescheduleTitle: 'Перенос замера',
    rescheduleHint:
      'Выбери новый день. «Окно времени» — заказ уйдёт в авто-распределение; «Точное время» — закрепит слот. Менеджеру придёт уведомление с твоей причиной.',
    newDateTime: 'Новая дата и время',
    newDate: 'Новая дата',
    timeWindow: 'Окно времени',
    periodAnytime: 'Любое',
    periodMorning: 'Утро 8–13',
    periodAfternoon: 'День 13–17',
    periodEvening: 'Вечер 17–22',
    helpButton: 'Справка',
    reschedModeWindow: 'Окно времени',
    reschedModeExact: 'Точное время',
    exactTimeLabel: 'Время',
    assignSelf: 'Поеду сам',
    assignAnyone: 'Отправить любого',
    whoAssign: 'Кто поедет',
    exactHint:
      'Точное время закрепится за заказом. «Поеду сам» — замер останется за тобой; «Отправить любого» — уйдёт в авто-распределение.',
    reasonOptional: 'Причина (опционально)',
    reasonRequired: 'Причина переноса',
    reasonPlaceholder: 'Например: клиент попросил перенести на завтра',
    reasonRequiredHint: 'Укажи причину, чтобы перенести',
    shortStart: 'Начать',
    shortContinue: 'Продолжить',
    shortReject: 'Не подходит',
    shortProblem: 'Проблема',
    shortEdit: 'Изменить',
    shortNoAnswer: 'Не дозвон.',
    shortPayment: 'Оплата',
    shortComplete: 'Завершить',
    saving: 'Сохраняем…',
    rescheduleAndNotify: 'Перенести и уведомить менеджера',
    editAddress: 'Изменить адрес',
    editAddressCardHint:
      'Адрес неточный? «📍 Изменить адрес» — поправить текст. «🛰️ Изменить геолокацию» — передвинуть точку на карте для GPS-навигации монтажника.',
    editAddressTitle: 'Уточнение адреса',
    editAddressHint:
      'Если фактический адрес отличается от указанного — впиши точный. Менеджеру придёт уведомление в Telegram.',
    addressLabel: 'Адрес',
    addressPlaceholder: 'Город, район, улица, дом, кв.',
    cityLabel: 'Город',
    cityPlaceholder: 'Ташкент',
    citySelectPlaceholder: '— выберите —',
    districtSelectPlaceholder: '— выберите —',
    districtSelectPickCity: 'Сначала город',
    streetLabel: 'Улица',
    streetPlaceholder: 'ул. Шахрисабзская',
    houseLabel: 'Дом',
    housePlaceholder: '14',
    aptLabel: 'Кв.',
    aptPlaceholder: '55',
    entranceLabel: 'Подъезд',
    entrancePlaceholder: '1',
    floorLabel: 'Этаж',
    floorPlaceholder: '5',
    districtLabelOptional: 'Район (опц.)',
    districtPlaceholder: 'Мирабад',
    saveAddressAndNotify: 'Сохранить и уведомить менеджера',
    addressUpdated: 'Адрес обновлён',
    addressUpdateFailed: 'Не удалось обновить адрес',
    editLocation: 'Изменить геолокацию',
    editLocationTitle: 'Уточнение локации',
    editLocationHint:
      'amoCRM присылает примерную точку на карте. Встаньте у входа клиента и нажмите «Я на месте» — телефон возьмёт точные GPS-координаты. Менеджеру придёт уведомление в Telegram.',
    locationDragHint: 'Точку можно поправить вручную — перетащите 📍 по карте.',
    locationCurrent: 'Текущая точка',
    locationNone: 'Координаты ещё не заданы',
    locationOnMap: 'Открыть на карте',
    locationGrab: '📍 Я на месте — взять GPS',
    locationRegrab: 'Взять координаты заново',
    locationGrabbing: 'Определяем GPS…',
    locationCaptured: 'Координаты получены',
    locationAccuracyM: 'точность ±{{m}} м',
    locationSave: 'Сохранить локацию и уведомить менеджера',
    locationSaved: 'Локация обновлена',
    locationSaveFailed: 'Не удалось сохранить локацию',
    locationGpsDenied:
      'Нет доступа к геолокации. Разрешите доступ приложению в настройках телефона.',
    locationGpsFailed: 'Не удалось получить GPS. Выйдите на открытое место и попробуйте снова.',
    locationGpsUnsupported: 'Телефон не поддерживает геолокацию.',
    downloadFailed: 'Не удалось скачать',
    preparingPdf: 'Готовлю PDF…',
    downloadOrderPdf: 'Скачать PDF заявки',
    contractorListButton: 'Список для подрядчика',
    contractorListTitle: 'Список для подрядчика',
    contractorListLoading: 'Готовим список…',
    contractorListError: 'Не удалось загрузить список',
    contractorListRetry: 'Повторить',
    contractorListClose: 'Закрыть',
    advanceAccepted: 'Аванс принят',
    savedOffline: 'Сохранено офлайн — отправим при сети',
    advanceFailed: 'Не удалось принять аванс',
    paymentReceipts: 'Чеки оплат',
    paymentReceiptsHint: 'Приложите чек или скриншот оплаты (карта, перечисление).',
    advance: 'Аванс',
    acceptAdvance: 'Принять аванс',
    acceptAdditionalPayment: 'Принять доплату',
    additionalPayment: 'Доплата',
    advanceAmount: 'Сумма аванса',
    remainder: 'Остаток',
    remainderDue: 'Остаток к оплате:',
    minPayment: 'Не хватает для отправки ({{pct}}%):',
    advanceType: 'Тип аванса',
    payerInnPlaceholder: 'ИНН плательщика (9–14 цифр)',
    selectCard: '— выбери карту —',
    selectTerminal: '— выбери терминал —',
    selectAccount: '— выбери счёт —',
    notePlaceholder: 'Примечание (необязательно)',
    savingShort: 'Сохраняю…',
    receiptDownloadFailed: 'Не удалось скачать чек',
    downloadReceiptPdf: 'Скачать PDF-чек',
    dimensions: '{{width}} × {{height}}',
    perPiece: '/ шт',
    slatSize: 'планка {{m}} м',
    bortSize: 'борт {{mm}} мм',
    bahromaShort: 'с бахромой',
    bahromaShortNo: 'без бахромы',
    itemSpecToggle: 'Описание изделия',
    spec_slat: 'Планка',
    spec_bort: 'Борт',
    spec_bahroma: 'Бахрома',
    spec_bahroma_yes: 'С бахромой',
    spec_bahroma_no: 'Без бахромы',
    spec_notes: 'Примечание',
    copyItemToRoom: 'Копировать изделие в комнату',
    copyToRoom: 'Копировать в комнату',
    roomFallback: 'комнату',
    roomActions: 'Комната',
    addItem: 'Добавить изделие',
    selectRoom: 'Выбрать комнату',
    roomLocked: 'В производстве — правка закрыта',
    roomNameLabel: 'Название комнаты',
    roomSave: 'Сохранить',
    roomDelete: 'Удалить комнату',
    roomDeleteTitle: 'Удалить комнату?',
    roomDeleteConfirm: 'Комната «{{name}}» и все её изделия будут удалены.',
    roomRenamed: 'Комната переименована',
    roomDeleted: 'Комната удалена',
    roomActionFailed: 'Не удалось изменить комнату',
    itemEdit: 'Изменить изделие',
    itemEditFull: 'Изменить (тип, ткань, размеры…)',
    itemWidth: 'Ширина, мм',
    itemHeight: 'Высота, мм',
    itemQuantity: 'Количество',
    itemPriceRecalcHint: 'Цена пересчитается автоматически по новым размерам.',
    itemDelete: 'Удалить изделие',
    itemDeleteTitle: 'Удалить изделие?',
    itemDeleteConfirm: 'Изделие будет удалено из комнаты.',
    itemSaved: 'Изделие изменено',
    itemDeleted: 'Изделие удалено',
    itemActionFailed: 'Не удалось изменить изделие',
    copied: 'Скопировано',
    copyFailed: 'Не удалось скопировать',
    copyItemTitle: 'Копировать изделие',
    copying: 'Копирую…',
    copy: 'Скопировать',
    sameParamsToRoom: '— те же параметры, в выбранную комнату.',
    toRoom: 'В комнату',
    currentRoom: '(текущая)',
    howManyCopies: 'Сколько копий',
  },
  weekPage: {
    today: 'Сегодня',
    next: 'Вперёд',
    month: 'Месяц',
    week: 'Неделя',
    day: 'День',
    scopeMine: 'Мои',
    scopeAll: 'Все',
    noTasksInline: '— нет задач',
    noTasksTitle: 'Нет задач',
    noTasksHint: 'В этот день у тебя ничего не запланировано.',
  },
  loginPage: {
    createPin: 'Создать PIN',
    pinMismatch: 'PIN не совпадает',
    demoMode: 'Быстрый вход',
    demoHint: 'Выберите аккаунт — вход без Telegram-кода (для разработки/демо).',
    signIn: 'Войти',
    roleMeasurer: 'Замерщик',
    roleInstaller: 'Установщик',
    roleManager: 'Менеджер',
    roleProduction: 'Цех',
    roleAdmin: 'Администратор',
    roleClientTest: 'Клиент (тест)',
    codeFromTelegram: 'Код из Telegram',
    changeNumber: 'Сменить номер',
    savePin: 'Сохранить PIN',
    pinCreateLabel: 'Придумайте PIN — 4 цифры',
    pinRepeatLabel: 'Повторите',
    pinNote:
      'PIN ускорит вход на этом устройстве. Если забудете — войдёте по Telegram-коду заново.',
    noAccount: 'Нет аккаунта?',
    register: 'Регистрация',
    version: 'версия {{version}}',
    codeWillArrive: 'Код придёт в Telegram',
    pinSubtitle: 'PIN ускорит вход — 4 цифры',
  },
  pinPage: {
    recognizing: 'Распознаю…',
    loginFaceId: 'Войти через Face ID',
    biometricOnlyPrompt: 'Вход по Face ID / Touch ID',
    resetTitle: 'Сбросить PIN?',
    resetDescription:
      'Сейчас отправим код в ваш Telegram. После подтверждения PIN сбросится, и вы зададите новый при следующем входе.',
    sendCode: 'Отправить код',
    enterCode: 'Введите код',
    codeSent: 'Код отправлен в Telegram',
    checking: 'Проверяю…',
    resetPin: 'Сбросить PIN',
  },
  registerPage: {
    titleRegister: 'Регистрация',
    titleConfirm: 'Подтверждение',
    titleEnterCode: 'Введите код',
    subtitlePhone: 'Введите ваш номер — отправим код через Telegram',
    subtitleTelegram: 'Откройте Telegram, чтобы бот прислал вам код активации',
    subtitleCode: 'Код пришёл в Telegram. Введите его ниже.',
    yourNumber: 'Ваш номер',
    getCode: 'Получить код',
    step1OpenTelegram: 'Нажмите кнопку — откроется Telegram',
    step2PressStartPrefix: 'В чате с ботом нажмите',
    step2StartButton: '«Старт»',
    step3BotSendsCode: 'Бот пришлёт вам код активации',
    step4ReturnAndSend: 'Вернитесь сюда и нажмите «Прислать код»',
    openTelegram: 'Открыть Telegram',
    sending: 'Отправляю…',
    confirmedSendCode: 'Я подтвердил → прислать код',
    changeNumber: 'Сменить номер',
    codeFromTelegram: 'Код из Telegram (4 цифры)',
    verifying: 'Проверяю…',
    login: 'Войти',
    alreadyRegistered: 'Уже зарегистрированы?',
    version: 'версия {{version}}',
  },
  dailyClosesSection: {
    title: 'Кассовая смена',
    subtitle:
      'Деньги на руках — принятые вами платежи, ещё не сданные в офис. Не путать с выручкой по установкам выше.',
    closeShift: 'Сдать смену',
    noUnclosedPayments: 'Нет несданных платежей',
    myShifts: 'Мои смены',
    statusApproved: '✓ Одобрено',
    statusRejected: '⛔ Отклонено',
    statusPending: '⏳ Ждёт менеджера',
    paymentsCount_one: '{{count}} платёж',
    paymentsCount_few: '{{count}} платежа',
    paymentsCount_many: '{{count}} платежей',
    toastSubmittedTitle: 'Смена сдана',
    toastSubmittedBody: 'Менеджер увидит её в админке.',
    toastSplitSubmittedBody: 'Платежи разделены на смены по филиалам. Менеджер увидит их в админке.',
    toastErrorTitle: 'Не удалось сдать смену',
    previewChanged: 'Список платежей изменился. Проверьте обновлённую сумму и сдайте смену ещё раз.',
    methodCash: 'Наличные',
    methodCardP2p: 'На карту (p2p)',
    methodCardTerminal: 'Карта (терминал)',
    methodTransfer: 'Перечисление',
    sheetTitle: 'Сдать смену',
    sending: 'Отправка…',
    submitToManager: 'Сдать менеджеру',
    total: 'Итого',
    byBranch: 'По филиалам',
    unassignedBranch: 'Без филиала',
    splitByBranchHint: 'Для каждого филиала будет создана отдельная смена. Общая сумма останется той же.',
    byMethods: 'По методам',
    paymentsHeading: 'Платежи ({{count}})',
    notePlaceholder: 'Комментарий менеджеру (необязательно)',
    confirmTitle: 'Сдать {{sum}}?',
    confirmDescription:
      'После подтверждения платежи привяжутся к этой смене. Менеджер увидит сводку и одобрит.',
    confirmSplitDescription:
      'Платежи разных филиалов будут привязаны к отдельным сменам. Менеджер увидит каждую смену и одобрит.',
    submit: 'Сдать',
  },
  catalogCategoryPage: {
    appBarTitle: 'Каталог',
    notFound: 'Категория не найдена.',
    addedToQuote: 'Добавлено в расчёт',
    priceFromPerSqM: 'от {{sum}} / м²',
    priceFromPerItem: 'от {{sum}} / шт',
    priceByComponents: 'Индивидуальный расчёт по компонентам',
    chooseFabric: 'Выберите ткань',
    whichRoom: 'В какой комнате?',
    roomPlaceholder: 'Гостиная, спальня, кухня…',
    windowCount: 'Количество окон',
    approxSizes: 'Примерные размеры (необязательно)',
    approxSizesHint:
      'Этот шаг можно пропустить — наши сотрудники сами замерят, когда придут. Но если вы примерно знаете размеры окна, это поможет точнее посчитать.',
    widthPlaceholder: 'Ширина, мм',
    heightPlaceholder: 'Высота, мм',
    comment: 'Комментарий (необязательно)',
    commentPlaceholder: 'Цвет, особенности, пожелания…',
    addToQuote: 'Добавить в расчёт',
  },
  catalogPage: {
    title: 'Каталог жалюзи',
    subtitle: 'Выберите типы жалюзи. Менеджер посчитает точную сумму и срок.',
    draftBarAdded: 'В расчёт добавлено · {{n}} {{noun}}',
    posOne: 'позиция',
    posFew: 'позиции',
    posMany: 'позиций',
    priceFromPerSqM: 'от {{price}} / м²',
    priceFromPerItem: 'от {{price}} / шт',
    priceIndividual: 'индивидуально',
  },
  quoteDraftPage: {
    title: 'Мой расчёт',
    emptyTitle: 'Расчёт пуст',
    emptyHint: 'Откройте каталог и добавьте жалюзи, которые хотите рассчитать.',
    toCatalog: 'В каталог',
    itemsCount_one: '{{count}} позиция.',
    itemsCount_few: '{{count}} позиции.',
    itemsCount_many: '{{count}} позиций.',
    managerWillCalc: 'Менеджер посчитает точную сумму и срок установки после получения запроса.',
    addressLabel: 'Адрес установки',
    addressHint: 'Куда привозить замерщика',
    addressPlaceholder: 'Ташкент, Чиланзар, ул. Бунёдкор 14, кв. 47',
    districtPlaceholder: 'Район (необязательно)',
    noteLabel: 'Комментарий менеджеру',
    noteHint: 'Бюджет, сроки, особые пожелания',
    notePlaceholder: 'Хочу к Новому году, бюджет ~3 млн сум…',
    submitting: 'Отправляю…',
    submit: 'Отправить менеджеру',
    remove: 'Убрать',
    toastTitle: 'Запрос {{action}}',
    toastUpdated: 'обновлён',
    toastSent: 'отправлен',
    toastBody: 'Код: {{code}}. Менеджер свяжется в ближайшее время.',
    toastErrTitle: 'Не удалось отправить',
  },
  dealerCalcPage: {
    title: 'Новый заказ',
    eyebrow: 'ДИЛЕРСКИЙ КАЛЬКУЛЯТОР',
    orderCreated: 'Заказ создан',
    orderCreatedHint: 'Отправлен в обработку',
    submitFailed: 'Не удалось оформить',
    profileNotFound: 'Профиль не найден',
    cartEmpty: 'Корзина пуста',
    cartEmptyHint: 'Добавьте первую позицию — выберите вид жалюзи, размеры, материалы.',
    notesLabel: 'Заметки к заказу (необязательно)',
    notesPlaceholder: 'Пожелания, спецификация, способ отгрузки…',
    addItem: 'Добавить позицию',
    totalItems: 'Итого {{count}} {{items}}',
    submitting: 'Оформляю…',
    submit: 'Оформить заказ',
    cancelOrderTitle: 'Отменить заказ?',
    cancelOrderDesc: 'Все добавленные позиции пропадут.',
    cancelOrderConfirm: 'Отменить',
    discountLabel: 'Ваша скидка от розницы:',
    discountNote: 'На некоторые позиции может быть зафиксирована индивидуальная цена.',
    mm: 'мм',
    pcs: 'шт',
    fabricLabel: 'Ткань',
    mechanismLabel: 'Механизм',
    addToCart: 'Добавить в корзину',
    withDiscount: '(с учётом скидки {{pct}}%)',
    pbVidHint:
      'Этот вид считается по Прайс-листу. Здесь показана ориентировочная цена по компонентам — точная цена рассчитается при оформлении заказа.',
    rowType: 'Тип жалюзи',
    rowSize: 'Размеры',
    rowQuantity: 'Количество',
    rowFabric: 'Ткань',
    rowRibbon: 'Лента',
    rowMechanism: 'Механизм',
    rowCornice: 'Карниз',
    itemTotal: 'Итого по позиции',
  },
  dealerHomePage: {
    title: 'Дилерский кабинет',
    subtitle: 'Ваши заказы и баланс. Можно оформить заказ самому или через менеджера.',
    profileNotFound: 'Профиль не найден',
    newOrder: '+ Новый заказ',
    orders: 'Заказы',
    noOrders: 'Заказов пока нет',
    noOrdersHint: 'Нажмите «+ Новый заказ» — соберите позиции и оформите.',
    yourDebt: 'Ваш долг',
    overpayment: 'Переплата',
    balance: 'Баланс',
    nameDiscount: '{{name}} · скидка {{pct}}%',
    creditExceeded: '⚠ Кредитный лимит превышен',
    itemsCount: '{{count}} позиций',
    debtLabel: 'долг',
    reconTitle: 'Акт сверки',
    reconHint: 'Выберите период и скачайте PDF взаиморасчётов.',
    reconFrom: 'С',
    reconTo: 'По',
    reconDownload: 'Скачать акт сверки',
    reconPreparing: 'Готовлю PDF…',
    reconError: 'Не удалось скачать акт',
    statusDraft: '📝 Черновик',
    statusOrdered: '✓ Оформлен',
    statusInProduction: '⚙️ В производстве',
    statusReady: '✅ Готов',
    statusShipped: '🚚 Отправлен',
    statusAccepted: '✓ Получен',
    statusRefused: '⛔ Отменён',
  },
  notificationDetailSheet: {
    orderLabel: 'Заказ',
    loadOrderError: 'Не удалось загрузить заказ. {{message}}',
    openOrder: 'Открыть заказ',
    quickJumpTitle: 'Быстрый переход к заказу',
    openShort: 'Открыть',
    typeNewMeasureAssigned: 'Назначен замер',
    typeOrderReadyForInstall: 'Заказ готов к монтажу',
    typeProductionCompleted: 'Производство завершено',
    typePaymentReceived: 'Получен платёж',
    typeClientRescheduled: 'Клиент перенёс',
    typeOrderRefused: 'Клиент отказался',
    typeGeneric: 'Уведомление',
  },
  orderCard: {
    callAttempts: 'Попыток дозвона: {{count}}',
    flagsCount: 'Пометок: {{count}}',
  },
  orderProgressTimeline: {
    stepNew: 'Заявка',
    stepMeasure: 'Замер',
    stepOrdered: 'Оформлен',
    stepProduction: 'Производство',
    stepInstall: 'Установка',
    stepCompleted: 'Готов',
    badgeRefused: '✕ Отказ',
    badgeReturned: '↩ Возврат',
  },
  personalDataSheet: {
    title: 'Личные данные',
    hint: 'Можно изменить только имя. Телефон меняет администратор.',
    nameLabel: 'Имя',
    namePlaceholder: 'Иван Иванов',
    phoneLabel: 'Телефон',
    roleLabel: 'Роль',
    saving: 'Сохраняю…',
    nameUpdated: 'Имя обновлено',
    saveFailed: 'Не удалось сохранить',
  },
  myCards: {
    title: 'Мои карты',
    hint: 'Карты, на которые ты сам принимаешь оплату. Приём на свою карту идёт в твою кассу — сдаёшь вместе с налом. Виден только тебе.',
    empty: 'Пока нет карт. Добавь свою — сможешь принимать «перевод на карту» на неё.',
    labelPlaceholder: 'Название (напр. Моя Uzcard)',
    numberPlaceholder: 'Номер карты (можно последние 4)',
    add: 'Добавить карту',
    remove: 'Удалить',
    saving: 'Сохраняю…',
    saved: 'Карты сохранены',
    saveFailed: 'Не удалось сохранить',
  },
  profileSheet: {
    pinSet: 'PIN установлен — можно изменить',
    pinNotSet: 'PIN отключён — вход по Telegram-коду',
    pinOffBiometric: 'Отключён — вход по Face ID / Touch ID',
    faceIdLastTitle: 'Это последний способ входа',
    faceIdLastHint: 'Сначала задайте PIN, потом удаляйте устройство',
    deviceDeleteFailed: 'Не удалось удалить устройство',
    logoutDescBefore: 'Сессия закроется на этом устройстве. При следующем входе нужен будет',
    logoutDescCode: 'Telegram-код',
    logoutDescAfter: ', не PIN.',
    logoutConfirm: 'Выйти',
    tileMore: '{{label}}: подробнее',
    faceIdConnected: 'Face ID подключён',
    devicesCount: 'Устройств: {{count}}',
    faceIdConnectFailed: 'Не удалось подключить Face ID',
    faceIdRegistering: 'Регистрирую…',
    faceIdConnectedCount: 'Подключено · устройств: {{count}}',
    faceIdHint: 'Войти быстро, без ввода PIN',
    deviceDeleted: 'Устройство удалено',
    biometricListHint:
      'Эти устройства могут разблокировать приложение по биометрии. PIN остаётся как резервный способ.',
    deviceFallback: 'Устройство',
    deviceAdded: 'Добавлено {{date}}',
    deviceLastUsed: ' · использовалось {{date}}',
    addDevice: '+ Добавить ещё устройство',
    todayWorkUntil: 'Сегодня работаю до {{time}}',
    todayFullDay: 'Сегодня — полный день',
    saveFailed: 'Не удалось сохранить',
    alwaysUntil: 'Постоянно до {{time}}',
    shiftReset: 'Постоянная смена сброшена',
    myAvailability: 'Моя доступность',
    todayWorkUntilLabel: 'Сегодня работаю до',
    todayWorkUntilDesc: 'Разово на сегодня. Пусто = полный день.',
    everyDayUntilLabel: 'Каждый день до',
    everyDayUntilDesc: 'Постоянная смена. Пусто = полный день.',
    homeSaved: 'Дом сохранён',
    homeSavedHint: 'Маршрут дня будет заканчиваться ближе к дому',
    myHome: '🏠 Мой дом',
    myHomeDesc: 'Старт и финиш маршрута дня — последний замер ставится ближе к дому.',
    homeAddressPlaceholder: 'Домашний адрес',
    latPlaceholder: 'широта (41.31)',
    lngPlaceholder: 'долгота (69.24)',
    useGps: 'Взять с GPS',
    locating: 'Определяю…',
    gpsFilled: 'Координаты с GPS подставлены',
    gpsFilledHint: 'Проверьте и нажмите «Сохранить дом»',
    gpsUnsupported: 'GPS недоступен на этом устройстве',
    gpsDenied: 'Доступ к геолокации запрещён',
    gpsFailed: 'Не удалось определить координаты',
    saving: 'Сохраняю…',
    saveHome: 'Сохранить дом',
    emptyFileError: 'Пустой файл — кроп не сработал',
    uploadFailed: 'Не удалось загрузить ({{status}})',
  },
  reserveCard: {
    title: 'Резерв',
    perMonth: 'за месяц',
    debtHint: 'Долг по штрафам — гасится наценкой со следующих продаж.',
    zeroHint: 'Баланс на нуле. Закрывайте сделки с наценкой — резерв будет расти.',
    sheetTitle: 'Резерв · {{period}}',
    tileBalance: 'Баланс',
    tileAccrued: 'Начислено за месяц',
    tilePenalty: 'Штрафы за месяц',
    historyCount: 'История ({{count}})',
  },
  syncBadge: {
    measurements: 'Неотправленные замеры',
    measurementsHint: 'Сохранённый ввод остался на устройстве. Откройте актуальный заказ и перенесите изменения в нужные окна; исходный ввод можно скачать.',
    noMeasurements: 'Неотправленных замеров нет. Остальные ошибки относятся к другим действиям или фото.',
    saveDraft: 'Скачать сохранённый ввод',
    openOrder: 'Открыть заказ',
    meters: 'м',
    syncErrors: '{{count}} ошибок синхронизации',
    inQueue: '{{count}} в очереди',
    online: 'Онлайн',
    offline: 'Офлайн',
  },
  themeToggle: {
    ariaLabel: 'Текущая: {{current}}. Переключить на: {{next}}',
    switchTo: 'Переключить на: {{next}}',
  },
  cashboxPage: {
    title: 'Касса',
    loadFailed: 'Не удалось загрузить',
    periodDay: 'День',
    periodMonth: 'Месяц',
    periodYear: 'Год',
    todayLabel: 'Сегодня',
    installsCount: '{{n}} установок',
    toReceive: 'К получению',
    totalForInstalls: 'всего по установкам',
    received: 'Получено',
    completedCount: '{{n}} завершено',
    remainder: 'Остаток',
    stillAhead: 'ещё предстоит',
    ordersLabel: 'Заказов',
    closedToday: 'закрыто сегодня',
    closedMonth: 'закрыто за месяц',
    closedYear: 'закрыто за год',
  },
  clientTrackerPage: {
    errInvalidLink: 'Ссылка некорректна',
    errLinkExpired: 'Срок действия ссылки истёк. Свяжитесь с менеджером.',
    errLinkRevoked: 'Ссылка недействительна или была отозвана.',
    errLoadFailed: 'Не удалось загрузить заказ. Попробуйте позже.',
    errNoServer: 'Нет связи с сервером.',
    refresh: 'Обновить',
    refreshing: 'Обновляем…',
    updatedAt: 'Обновлено в {{time}}',
    staleData: 'Не удалось обновить статус. Показаны последние полученные данные. Попробуйте обновить ещё раз.',
    stages: 'Этапы заказа',
    history: 'История заказа',
    currentStage: 'Текущий этап',
    paused: 'На паузе',
    measureDate: 'Замер назначен:',
    installDate: 'Установка назначена:',
    agreedInstall: 'Согласованная установка:',
    installedCount: 'Установлено изделий: {{installed}} из {{total}}',
    subtitle: 'ТРЕКЕР ЗАКАЗА',
    loading: 'Загружаю…',
    autoUpdate: 'Пока страница открыта, статус обновляется каждые 30 секунд.',
    questionsHint: 'Вопросы? Позвоните менеджеру по номеру выше.',
    orderCode: 'Заказ {{code}}',
    expectedInstall: 'Желаемая дата установки (уточняется):',
    address: 'Адрес',
    downloadPdf: 'Скачать PDF заявки',
    downloadPdfHint: 'Состав заказа и сумма к оплате',
    finishedWork: 'Готовая работа',
    yourManager: 'Ваш менеджер',
    reviewTitle: 'Оцените нашу работу',
    reviewCommentPh: 'Комментарий (по желанию)',
    reviewSubmit: 'Отправить отзыв',
    reviewThanks: 'Спасибо за вашу оценку!',
    reviewError: 'Не удалось отправить. Попробуйте позже.',
    tgSubTitle: 'Ваш заказ в Telegram',
    tgSubHint:
      'Откройте карточку заказа в боте. Нажимайте «Обновить», чтобы видеть текущий этап.',
    tgSubBtn: 'Открыть в Telegram',
  },
  dispatchPage: {
    title: 'Диспетчер',
    subtitle: 'Распределение заказов по исполнителям',
    autopilotOn:
      'Автопилот включён — распределяет сам. Ниже — то, что нужно назначить вручную (исключения).',
    autopilotOff: 'Автопилот выключен — распределяй вручную.',
    resyncBtn: 'Прогнать автопилот сейчас',
    queueAttention: 'Требуют внимания',
    emptyAutopilot: 'Всё под контролем автопилота',
    emptyAutopilotHint: 'Автопилот распределяет заказы сам. Ручных исключений нет.',
    tabMeasure: 'Замеры',
    tabInstall: 'Монтажи',
    autoBtn: 'Авто-распределить день',
    autoBusy: 'Распределяю…',
    autoDone: 'День распределён',
    autoFailed: 'Не удалось распределить',
    autoPartialTitle: 'Распределено не всё',
    autoPartialMeasure: 'В очереди ещё {{count}} — пока нет свободных замерщиков',
    autoPartialInstall: 'В очереди ещё {{count}} — пока нет свободных монтажников',
    autoPartialOverdue:
      'В очереди {{count}} — окно клиента прошло. Автопилот их не назначает: позвоните клиенту и задайте новую дату («Назначить»).',
    autoPartialMixed:
      'В очереди ещё {{blocked}} просрочено (нужна новая дата) + {{waiting}} ждут свободного замерщика.',
    overdueBadge: 'Окно прошло — назначьте новую дату',
    queueTitle: 'К распределению',
    noDate: 'без даты',
    assignCta: 'Назначить',
    errorTitle: 'Не удалось загрузить',
    emptyTitle: 'Очередь пуста',
    emptyHint: 'Нераспределённых заказов нет.',
    assignTitle: 'Назначить исполнителя',
    whenLabel: 'Дата и время визита',
    dayLabel: 'Дата визита',
    windowLabel: 'Окно времени',
    window_MORNING: 'Утро',
    window_AFTERNOON: 'День',
    window_EVENING: 'Вечер',
    window_ANYTIME: 'Любое',
    exactTimeLabel: 'Точное время',
    modeAuto: 'Автопилот',
    modeManual: 'Вручную',
    modeLockedAuto:
      'Автопилот включён в админке — вы задаёте дату и время, исполнителя подбирает он сам. Ручной режим выключен.',
    modeLockedManual: 'Автопилот выключен в админке — назначайте исполнителя вручную.',
    autopilotHint:
      'Исполнителя подберёт автопилот при планировании дня — с учётом маршрутов и загрузки.',
    measurersTitle: 'Замерщики',
    installersTitle: 'Монтажники',
    futureDistanceNote:
      'Замер не на сегодня — кто будет ближе, заранее неизвестно (зависит от маршрута того дня). Выбирайте по опыту/загрузке или доверьте автопилоту.',
    handToAutopilot: 'Отдать автопилоту — подберёт замерщика',
    handToAutopilotInstall: 'Отдать автопилоту — подберёт монтажника',
    orManual: 'или назначить вручную',
    autopilotTakeoverTitle: 'Отдано автопилоту',
    autopilotTakeoverDesc:
      'Дата обновлена — автопилот подберёт исполнителя. Можно нажать «Прогнать автопилот».',
    suggestTitle: 'Подходящие исполнители',
    suggestManualTitle: 'Или выбрать замерщика вручную',
    suggesting: 'Подбираю по близости и загрузке…',
    noExecutors: 'Нет доступных исполнителей',
    assigned: 'Назначен: {{name}}',
    assignFailed: 'Не удалось назначить',
    recommended: 'Лучший',
    blockedNoTravel: 'Не успеет доехать',
    blockedBusy: 'Занят',
    kmEta: '{{km}} км · {{eta}} мин',
    noGps: 'без GPS',
  },
  installationWorkspace: {
    scheduleTitle: 'Управление монтажами',
    scheduleSubtitle: 'Очередь, автопилот и ручное назначение установщиков',
    defectsTitle: 'Склад и брак',
    defectsSubtitle: 'Комментарий ОТК, причина брака и выбор дальнейшего маршрута',
    fastDecisionSubtitle:
      'Сначала быстро запустите переделку или замену. Деньги разбираются отдельно.',
    active: 'в работе',
    closed: 'закрыто',
    loadFailed: 'Не удалось загрузить дела',
    noDefects: 'Активных дел о браке нет',
    noClosedDefects: 'Закрытых дел пока нет',
    priceBasis: 'Стоимость изделия',
    client: 'Клиент',
    reportedReason: 'Что произошло',
    warehouse: 'Склад и движение',
    resolutionLabel: 'Решение руководителя установки',
    causeLabel: 'Причина брака',
    qcCauseLocked: 'Указано ОТК · изменить нельзя',
    qcResponsibleMissing:
      'Источник виновника не найден. Маршрут можно сохранить, но финансовое распределение будет заблокировано до исправления данных.',
    driverRole: 'Водитель',
    contractorRole: 'Подрядчик',
    installerComment: 'Комментарий установщика',
    initialQcComment: 'Первичный комментарий ОТК',
    reporterComment: 'Первичный комментарий',
    qcComment: 'Комментарий ОТК',
    approved: 'Утверждено',
    correct: 'Исправить решение',
    repeatVisit: 'Доплата за повторный выезд',
    correctTitle: 'Исправить виновников',
    classifyTitle: 'Определить виновников',
    saving: 'Сохраняю…',
    saveDecision: 'Сохранить решение',
    resolutionTitle: 'Что делать с изделием',
    resolutionHint:
      'Изучите комментарий ОТК, укажите причину брака и выберите дальнейшую судьбу изделия.',
    confirmResolution: 'Подтвердить решение',
    resolutionSaved: 'Решение запущено',
    supplier: 'Поставщик',
    dueAt: 'Плановая дата готовности',
    driver: 'Водитель для возврата',
    selectDriver: 'Выберите водителя',
    driverAssignedAutomatically:
      'Водитель назначится автоматически по цеху. Выбирать его в деле о браке не нужно.',
    preliminaryResponsible: 'Предварительно отвечает',
    noResponsibleYet: 'Виновник пока не указан',
    noResponsibleAllowed: 'Можно продолжить без виновника и определить его позже во время разбора.',
    selectResponsible: 'Выберите хотя бы одного предполагаемого виновника.',
    addResponsible: 'Добавить ответственного',
    removeResponsible: 'Убрать ответственного',
    continueWithoutResponsible: 'Продолжить без виновника',
    otherCauseNote: 'Опишите другую причину',
    qcPassedBefore: 'Изделие ранее принято ОТК',
    qcPassedPossibleEscape: 'Изделие ранее принято ОТК — учтите возможный пропуск при разборе',
    moneyMovedToReview:
      'Штрафы, распределение стоимости и доплата за повторный выезд оформляются отдельно в разделе «Разбор и расчёты».',
    manufacturingNoCharge:
      '«{{contractor}}» получает своё бракованное изделие обратно и бесплатно переделывает или заменяет его. Виновник, удержание, штраф и финансовое распределение не создаются.',
    whatToDo: 'Что сделать с изделием',
    newSupplier: 'Новый подрядчик',
    selectSupplier: 'Выберите подрядчика',
    ownWorkshop: 'Наш цех',
    routeChoice: {
      REWORK: {
        title: 'Исправить текущее изделие у прежнего изготовителя',
        hint: 'Бракованное изделие вернётся прежнему изготовителю на исправление без списания.',
      },
      NEW_SAME: {
        title: 'Изготовить новое у прежнего изготовителя',
        hint: 'Бракованное изделие вернётся прежнему изготовителю, а взамен будет создано новое.',
      },
      NEW_SAME_OWN: {
        title: 'Списать брак и изготовить новое в прежнем цехе',
        hint: 'Брак своего цеха будет списан, а этот же цех изготовит новое изделие.',
      },
      NEW_SAME_EXTERNAL: {
        title: 'Вернуть брак и изготовить новое у прежнего изготовителя',
        hint: 'Водитель вернёт брак прежнему подрядчику, и он изготовит новое изделие.',
      },
      NEW_OTHER: {
        title: 'Переизготовить у другого изготовителя',
        hint: 'Сразу выберите другого подрядчика или свой цех; новая передача останется в этой же партии.',
      },
      NEW_OTHER_OWN: {
        title: 'Списать брак и изготовить заново у другого изготовителя',
        hint: 'Брак своего цеха будет списан, а выбранное изделие заново изготовит другой цех.',
      },
      NEW_OTHER_EXTERNAL: {
        title: 'Вернуть брак и изготовить заново у другого изготовителя',
        hint: 'Брак вернётся прежнему подрядчику, а выбранное изделие заново изготовит другой цех.',
      },
    },
    retryAssignedDriver: 'Повторить назначение',
    managerResolution: {
      REWORK_SAME_CONTRACTOR: {
        title: 'Переделать текущее',
        hint: 'Старое изделие возвращается тому же производству. Списания не будет.',
      },
      NEW_ORDER: {
        title: 'Новое изготовление',
        hint: 'Отдельный заказ не создаётся: та же партия запускается заново у выбранного изготовителя.',
      },
    },
    installer: 'Установщик',
    selectEmployee: 'Выберите сотрудника',
    amountSum: 'Сумма доплаты, сум',
    note: 'Комментарий',
    clientAssessment: 'Оценка отказа клиента',
    regulationFollowed: 'Регламент соблюдён — ответственность компании',
    clientMisinformed: 'Клиенту неверно объяснили — ответственность замерщика',
    priceBasisSum: 'Стоимость изделия, сум',
    noResponsibleReason: 'Почему нет виновного',
    responsible: 'Основной виновник',
    company: 'Компания',
    qcEscape: 'ОТК пропустил дефект (необязательно)',
    noQcEscape: 'Нет дополнительной ответственности ОТК',
    qcFineSum: 'Штраф ОТК, сум',
    classified: 'Виновники сохранены',
    corrected: 'Решение исправлено',
    repeatDone: 'Доплата за повторный выезд начислена',
    rollback: 'Отменить последний этап',
    rollbackTitle: 'Отмена последнего этапа',
    rollbackHint:
      'Система отменит только последний проведённый этап и сохранит это в истории дела.',
    rollbackReason: 'Причина отмены',
    rollbackDone: 'Последний этап отменён',
    actionFailed: 'Не удалось выполнить действие',
    qualityTitle: 'Контроль ОТК',
    qualitySubtitle: 'Проверка, возвраты брака и готовые к монтажу изделия',
    checking: 'На проверке',
    qcDefect: 'Брак',
    passed: 'Принято',
    noQualityItems: 'В этой очереди ничего нет',
    waitingDriver: 'Нужен водитель для возврата',
    returnInProgress: 'Возврат: {{status}}',
    returnStatus: {
      WAITING_DRIVER: 'ожидает водителя',
      RETURNING: 'в пути',
      RETURNED: 'возвращено',
    },
    pickDriver: 'Выберите водителя',
    assignDriver: 'Назначить водителя',
    driverAssigned: 'Водитель назначен',
    step: {
      WAIT_WAREHOUSE: 'Ожидает склада',
      WAIT_QC: 'Ожидает комментария ОТК',
      DECIDE: 'Нужно выбрать решение',
      TRACKING: 'Решение выполняется',
      DONE: 'Готово',
    },
    action: {
      DECIDE: 'Выбрать: переделать или создать новое',
    },
    confirm: {
      APPROVE: 'Утвердить финансовое решение?',
      EXECUTE: 'Запустить переделку или замену?',
      CLOSE: 'Закрыть дело о браке?',
    },
    done: {
      approve: 'Решение утверждено',
      execute: 'Решение запущено',
      close: 'Дело закрыто',
    },
  },
  warehouseWorkspace: {
    finishedTitle: 'Готовая продукция',
    finishedSubtitle: 'Реестр готовой продукции по ответственным складам',
    autoReceiptHint:
      'После готовности своего цеха или одобрения ОТК продукция приходуется автоматически.',
    autoPosted: 'Оприходовано автоматически',
    pending: 'Принять',
    received: 'Принято',
    order: 'ЗАКАЗ',
    ownWorkshopReady: 'Наш цех · без водителя и ОТК',
    qcPassed: 'ОТК пройден',
    accept: 'Принять',
    targetWarehouse: 'Склад: {{name}}',
    warehouseNotConfigured:
      'Склад для этого изготовителя и филиала не настроен. Обратитесь к администратору.',
    warehouseConflict:
      'У изготовителей указаны разные склады. Администратор должен выбрать один склад.',
    cancelAct: 'Отменить акт',
    noPending: 'Вся готовая продукция принята',
    noReceived: 'Проведённых актов пока нет',
    loadFailed: 'Не удалось загрузить очередь',
    receiptDone: 'Готовая продукция принята',
    receiptCancelled: 'Акт приёмки отменён',
    cancelTitle: 'Отменить акт приёмки?',
    cancelHint: '{{code}} вернётся в очередь приёмки. После назначения монтажа отмена недоступна.',
    saving: 'Провожу…',
    actionFailed: 'Не удалось выполнить действие',
    defectsTitle: 'Склад брака',
    defectsSubtitle: 'Фактическая приёмка, маршрут изделия и списание',
    noDefects: 'Активных дел на складе брака нет',
    handedOverBy: 'Кто передал изделие',
    handedOverAt: 'Фактическое время передачи',
    selectEmployee: 'Выберите сотрудника',
    storageType: 'Место приёмки',
    defectPoint: 'Пункт брака',
    defectWarehouse: 'Склад брака',
    rack: 'Стеллаж',
    cell: 'Ячейка',
    resolution: 'Куда направить изделие',
    targetContractor: 'Новый подрядчик',
    selectContractor: 'Выберите подрядчика',
    returnDriver: 'Водитель для возврата',
    selectDriver: 'Выберите водителя',
    dueAt: 'Срок переделки',
    writeOffReason: 'Причина списания',
    note: 'Комментарий',
    receivedAt: 'Принято на склад',
    rollbackShort: 'Исправить',
    rollback: 'Отменить этап',
    rollbackTitle: 'Отмена складского этапа',
    rollbackHint:
      'Будет отменён только последний этап этого дела, выполненный в вашем складском контуре.',
    rollbackReason: 'Причина отмены',
    rollbackDone: 'Последний складской этап отменён',
    step: {
      RECEIVE: 'Ожидает физической приёмки',
      DISPOSITION: 'Нужно определить маршрут',
      WRITE_OFF: 'Ожидает списания',
      DONE: 'Складской этап завершён',
    },
    action: {
      RECEIVE: 'Принять',
      DISPOSITION: 'Определить маршрут',
      WRITE_OFF: 'Списать',
      DONE: 'Готово',
    },
    done: {
      RECEIVE: 'Изделие принято на склад брака',
      DISPOSITION: 'Маршрут изделия сохранён',
      WRITE_OFF: 'Списание проведено',
    },
  },
  defectWorkspace: {
    cause: {
      MEASUREMENT_ERROR: 'Ошибка замера',
      MANUFACTURING_ERROR: 'Ошибка изготовления',
      INSTALLER_DAMAGE: 'Повреждение установщиком',
      DRIVER_DAMAGE: 'Повреждение водителем',
      CLIENT_REJECTION: 'Отказ клиента',
      OTHER: 'Другая причина',
    },
    resolution: {
      REWORK_SAME_CONTRACTOR: 'Переделать у того же подрядчика',
      REWORK_OTHER_CONTRACTOR: 'Переделать у другого подрядчика',
      NEW_ORDER: 'Новое изготовление',
      WRITE_OFF: 'Списать',
      HOLD: 'Оставить на хранении',
      NO_ACTION: 'Без дальнейшего движения',
    },
  },
  driverCommon: {
    pickupConfirmed: 'Забор подтверждён',
    pickupRejected: 'Отказ отправлен менеджеру',
    returnCompleted: 'Возврат подтверждён',
    ttn: 'ТТН',
    ttnFailed: 'Не удалось открыть ТТН',
    itemsN: '{{count}} изд.',
    searchPlaceholder: 'Поиск: номер заказа или клиент',
    notFoundTitle: 'Ничего не найдено',
    notFoundHint: 'Проверьте номер заказа или имя клиента.',
  },
  driverPickup: {
    title: 'Забор из цеха',
    subtitle: 'Подтвердите или отклоните назначенный забор',
    emptyTitle: 'Забирать нечего',
    emptyHint:
      'После назначения менеджером заявка появится здесь с кнопками «Забрал» и «Не заберу».',
    statusFilterLabel: 'Фильтр по статусу забора',
    statusFilters: { PENDING: 'Ожидают', TAKEN: 'Забраны', REJECTED: 'Отказаны', ALL: 'Все' },
    statusEmptyHints: {
      PENDING: 'Нет заявок, ожидающих вашего решения.',
      TAKEN: 'В истории пока нет принятых заборов.',
      REJECTED: 'В истории пока нет отказов.',
      ALL: 'После назначения менеджером заявка появится здесь с кнопками «Забрал» и «Не заберу».',
    },
    sectionConfirm: 'К забору',
    sectionReturnConfirm: 'Подтвердить возврат брака',
    sectionHistory: 'История решений',
    historyAttempts: 'История попыток · {{count}}',
    historyAttempt: 'Попытка {{count}}',
    historyTaken: 'Забрал',
    historyRejected: 'Отказано',
    historyQcHandoffPending: 'Ожидает сдачи в ОТК',
    historyQcHandoffDone: 'Сдал в ОТК',
    historyDefectReturn: 'Возврат брака',
    historyProductionPickup: 'Забор из цеха',
    historyDetailTitle: 'Состав {{code}}',
    historyDetailFailed: 'Не удалось загрузить состав',
    historyProducts: 'Изделия и комплектующие',
    historyProductQty: '{{count}} шт.',
    historyDimensions: '{{width}} × {{height}}',
    historyMaterials: 'Материалы',
    historyNoMaterials: 'Материалы не указаны',
    historyTtnDownload: 'Скачать ТТН',
    historyDocumentStatus: 'Статус документа',
    historyAccepted: 'Принято',
    productTypes: {
      ROLL: 'Ролло',
      COMBO: 'Комбо',
      PLISSE: 'Плиссе',
      ROMAN: 'Римские шторы',
      TEXTILE: 'Текстиль',
      HORIZONTAL: 'Горизонтальные жалюзи',
      MOSQUITO: 'Москитная сетка',
      BAMBOO: 'Бамбуковые жалюзи',
      WOOD: 'Деревянные жалюзи',
      CUSTOM: 'Изделие',
      ACCESSORY: 'Комплектующая',
    },
    materialKinds: {
      FABRIC: 'Ткань',
      RIBBON: 'Лента',
      CORNICE: 'Карниз',
      MECHANISM: 'Механизм',
      SLAT: 'Планка',
      BORT: 'Борт',
      BAHROMA: 'Бахрома',
      ACCESSORY: 'Наименование',
    },
    materialValues: { without: 'Без', with: 'С', corniceType: 'Тип карниза', mm: 'мм' },
    confirmPickupTitle: 'Подтвердить забор {{code}}?',
    confirmPickupDesc: 'Менеджер отметил, что вы забрали заказ. Подтвердите.',
    confirmTaken: 'Забрал',
    rejectTaken: 'Не заберу',
    rejectPickupTitle: 'Не будете забирать {{code}}?',
    rejectPickupDesc: 'Заказ вернётся в статус «Готово», а менеджер назначит другого водителя.',
    confirmAssignmentTakenTitle: 'Подтвердить забор {{code}}?',
    confirmAssignmentTakenDesc: 'Подтверждайте только когда партия действительно уже у вас.',
    rejectAssignmentTakenTitle: 'Не будете забирать {{code}}?',
    rejectAssignmentTakenDesc:
      'Партия вернётся в статус «Готово», а менеджер назначит другого водителя.',
    confirmReturnTaken: 'Да, забрал брак',
    rejectReturn: 'Не забирал',
    confirmReturnTitle: 'Забрали брак {{code}}?',
    confirmAssignmentReturnTitle: 'Забрали брак {{code}}?',
    confirmReturnDesc: 'Подтверждайте только когда изделие действительно уже у вас.',
    rejectReturnTitle: 'Вы не забирали брак {{code}}?',
    rejectAssignmentReturnTitle: 'Вы не забирали брак {{code}}?',
    rejectReturnDesc: 'Менеджер получит отказ и сможет назначить другого водителя.',
    defectPoint: 'Пункт брака',
    returnPickupPlace: 'Забрать по адресу: {{address}}',
  },
  driverDeliver: {
    title: 'Сдать',
    subtitle: 'Передайте привезённое в ОТК',
    emptyTitle: 'Сдавать нечего',
    emptyHint: 'Забранные заказы появятся здесь для передачи в ОТК.',
    sectionToQc: 'Сдать в ОТК',
    toQc: 'В ОТК →',
    confirmToQcTitle: 'Передать {{code}} в ОТК?',
    confirmToQcDesc: 'ОТК проверит и пропустит или вернёт в цех.',
    confirmPartToQcTitle: 'Передать {{code}} · {{contractor}} в ОТК?',
    confirmPartToQcDesc:
      'Будет сдана только эта часть подрядчика. Остальные части останутся у своих водителей до отдельной сдачи.',
    sectionDefect: 'Возврат брака',
    defectBadge: '⚠ Брак',
    returnDestination: 'Везти: {{destination}}',
    completeReturn: 'Возвратил',
    completeReturnTitle: 'Подтвердить возврат {{code}}?',
    completeReturnDesc: 'Нажимайте только после передачи изделия на склад или поставщику.',
  },
  driverSummary: {
    title: 'Сводка',
    subtitle: 'Сколько сделано за период и что сейчас в работе',
    picked: 'Забрано',
    handed: 'Сдано в ОТК',
    defect: 'Брак',
    inWork: 'Сейчас в работе',
    emptyInWorkTitle: 'Всё сдано',
    emptyInWorkHint: 'Активных заказов на руках нет.',
  },
  qcCheck: {
    title: 'Проверка',
    subtitle: 'Проверьте готовые заказы перед монтажом',
    statusFilterLabel: 'Фильтр проверок',
    statusFilters: {
      PENDING: 'Ожидают',
      PASS: 'Годные',
      DEFECT: 'Брак',
      ALL: 'Все',
    },
    statusEmptyHints: {
      PENDING: 'Когда водитель передаст заказы — они появятся здесь.',
      PASS: 'Признанных годными изделий пока нет.',
      DEFECT: 'Решений с браком пока нет.',
      ALL: 'История решений ОТК пока пуста.',
    },
    sectionChecking: 'На проверке',
    sectionHistory: 'История решений',
    check: 'Проверить',
    supplierWave: 'поставка {{count}}',
    historyPassed: 'Годно',
    historyCancelled: 'Решение отменено',
    historyDefect: 'Брак',
    historyAttempts: 'История проверок · {{count}}',
    historyAttempt: 'Проверка {{count}}',
    historyInspector: 'Проверил: {{name}}',
    historyDetailTitle: 'ОТК · {{code}}',
    historyTitle: 'ОТК',
    historyDestinationPassed: '→ {{name}}',
    historyDestinationPassedFallback: '→ Склад готовой продукции',
    historyDestinationDefect: '→ Склад брака',
    historyDetailFailed: 'Не удалось загрузить проверку',
    historyDocumentStatus: 'Решение ОТК',
    emptyTitle: 'Нечего проверять',
    emptyHint: 'Когда водитель передаст заказы — они появятся здесь.',
  },
  qcDefects: {
    title: 'Брак',
    subtitle: 'Брак из ОТК передаётся сразу. Здесь принимаются возвраты с монтажа',
    sectionDefect: 'Брак — в цех',
    sectionDecision: 'Требует комментария ОТК',
    sectionForwarded: 'Передано дальше',
    sectionCompleted: 'Завершено',
    emptyTitle: 'Брака нет',
    emptyHint: 'Забракованные заказы появятся здесь с причиной.',
    resolve: 'Принять и описать',
    cause: 'Причина брака',
    selectCause: 'Выберите причину',
    comment: 'Комментарий ОТК',
    installerComment: 'Комментарий установщика',
    initialQcComment: 'Первичный комментарий ОТК',
    reporterComment: 'Первичный комментарий',
    outcome: 'Что делать с изделием',
    formHint:
      'Опишите только то, что фактически обнаружили. Причину брака и дальнейший маршрут определит руководитель установки в разделе «Склад и брак».',
    supplier: 'Поставщик',
    noSupplier: 'Не назначен',
    dueAt: 'Срок готовности переделки',
    driver: 'Водитель для возврата',
    selectDriver: 'Выберите водителя',
    confirm: 'Принять и передать руководителю',
    saving: 'Сохраняем…',
    saved: 'Комментарий ОТК передан руководителю установки',
    saveFailed: 'Не удалось сохранить комментарий',
    actFailed: 'Не удалось открыть акт',
    openAct: 'Акт {{number}}',
    causes: {
      MEASUREMENT_ERROR: 'Ошибка замера',
      MANUFACTURING_ERROR: 'Ошибка изготовления',
      INSTALLER_DAMAGE: 'Повреждение установщиком',
      DRIVER_DAMAGE: 'Повреждение водителем',
      CLIENT_REJECTION: 'Отказ клиента',
      OTHER: 'Другая причина',
    },
    resolutions: {
      REWORK_SAME_CONTRACTOR: {
        title: 'Переделать текущее изделие',
        hint: 'Списание не создаётся. Водитель вернёт изделие тому же поставщику.',
      },
      NEW_ORDER: {
        title: 'Изготовить новое изделие',
        hint: 'Старое изделие возвращается или списывается, а эта же партия запускается заново у выбранного изготовителя.',
      },
      WRITE_OFF: {
        title: 'Списать без замены',
        hint: 'Изделие закроется как списанное и получит отдельный акт.',
      },
    },
    status: {
      needsDecision: 'Нужно решение ОТК',
      needsVerdict: 'Нужен комментарий ОТК',
      waitingManager: 'Ждёт решения руководителя установки',
      waitingDriver: 'Ждёт водителя',
      inTransit: 'Водитель везёт поставщику',
      rework: 'Передано на переизготовление',
      replacementCreated: 'Создана замена {{code}} · старое списано',
      writtenOff: 'Списано',
    },
    destination: {
      qc: 'Сейчас: пункт брака ОТК',
      manager: 'Далее: раздел «Склад и брак»',
      supplier: 'Далее: поставщик «{{name}}»',
      replacement: 'Далее: новый заказ в работе',
      writeOff: 'Далее: акт списания',
    },
  },
  qcSummary: {
    title: 'Сводка',
    subtitle: 'Проверено за период и что сейчас на проверке',
    checking: 'На проверке',
    passed: 'Годно',
    defect: 'Брак',
    onCheck: 'Сейчас на проверке',
    emptyTitle: 'Очередь пуста',
    emptyHint: 'Заказов на проверке сейчас нет.',
  },
  qcSheet: {
    compositionFailed: 'Не удалось загрузить состав',
    compositionEmpty: 'В составе нет изделий',
    manufacturer: 'Изготовитель',
    checkAll: 'Отметить все',
    clearAll: 'Снять всё',
    defectPhotoTitle: 'Фото дефекта',
    defectPhotoHint: 'Снимите брак — цеху будет понятно, что исправить.',
    checklistTitle: 'Обязательный чек-лист ОТК',
    checklist: {
      dimensions: 'Размеры соответствуют замеру и заказу',
      specification: 'Цвет, ткань и комплектация по спецификации',
      mechanism: 'Механизм исправен и работает без заеданий',
      appearanceAndKit: 'Нет повреждений, комплект полный',
    },
    passedCheck: 'Пройдено',
    failedCheck: 'Не пройдено',
    evidenceTitle: 'Фото проверки',
    evidenceHint: 'Необязательно — можно принять решение без фото',
    camera: 'Камера',
    gallery: 'Галерея',
    checklistRequired: 'Чек-лист не завершён',
    checklistPassHint: 'Для решения «Годно» должны пройти все четыре проверки.',
    failedCheckRequired: 'Не отмечен дефект',
    failedCheckHint: 'Для брака оставьте минимум один пункт как «Не пройдено».',
    defectItemsTitle: 'Какие изделия с браком',
    defectItemsHint: 'Выберите только те изделия, где ОТК действительно обнаружил проблему.',
    defectItemsSelected: 'Выбрано: {{selected}} из {{total}}',
    defectItemsRequired: 'Выберите изделие',
    defectItemsRequiredHint: 'Отметьте минимум одно конкретное бракованное изделие.',
    defectItemsEmpty: 'В этой передаче нет изделий, которые можно отметить браком.',
    withoutRoom: 'Без комнаты',
    causeTitle: 'Что произошло',
    causeRequired: 'Выберите причину брака',
    causeRequiredHint: 'Укажите: ошибка изготовления или повреждение при перевозке.',
    cause: {
      MANUFACTURING_ERROR: 'Ошибка изготовления',
      DRIVER_DAMAGE: 'Повреждение при перевозке',
    },
    responsiblePreview: 'Ответственный: {{name}} · {{role}}',
    manufacturingNoChargePreview: 'Возврат: {{name}} · бесплатная переделка без штрафа и удержания',
    roleContractor: 'Подрядчик',
    roleCompany: 'Компания',
    roleDriver: 'Водитель',
    manufacturerNotRecorded: 'Изготовитель не зафиксирован — дело сохранится для уточнения',
    driverNotRecorded: 'Водитель не зафиксирован — дело сохранится для уточнения',
    notRecorded: 'не определён',
    confirmDefectCause:
      '{{cause}}. Бракованных изделий: {{count}}. Ответственный: {{responsible}}. После подтверждения только выбранные изделия попадут в дело о браке.',
    confirmManufacturingDefect:
      '{{cause}}. Бракованных изделий: {{count}}. Сразу после подтверждения система поставит закреплённому водителю задачу забрать их из ОТК и сдать изготовителю «{{manufacturer}}». Решение руководителя не задерживает возврат.',
    onlineRequired: 'Нужна сеть',
    onlineRequiredHint: 'Решение ОТК с доказательствами фиксируется на сервере сразу.',
    uploadFailed: 'Не удалось загрузить фото',
    deleteFailed: 'Не удалось удалить фото',
  },
  handoffPage: {
    title: 'Приёмка из цеха',
    titleQc: 'ОТК · приёмка',
    subtitle: 'Забирайте из цеха, передавайте в ОТК',
    subtitleQc: 'Проверяйте готовые заказы перед монтажом',
    statToPickup: 'К забору',
    statInTransit: 'В пути',
    statHandedPeriod: 'В ОТК за период',
    statChecking: 'На проверке',
    statPassedPeriod: 'Годно за период',
    statDefectPeriod: 'Брак за период',
    loadFailed: 'Не удалось загрузить',
    emptyTitle: 'Заказов на приёмку нет',
    emptyTitleQc: 'Нечего проверять',
    emptyHint: 'Когда цех закончит — заказ появится с кнопкой «Забрал из цеха».',
    emptyHintQc: 'Когда водитель передаст заказы — они появятся здесь.',
    statusUpdated: 'Статус обновлён',
    updateFailed: 'Не получилось обновить',
    pickedUp: 'Забрал',
    toQc: 'В ОТК →',
    defectBadge: '⚠ Брак — отдать в цех',
    check: 'Проверить',
    confirmPickupTitle: 'Забрал {{code}} из цеха?',
    confirmQcGiveTitle: 'Передать {{code}} в ОТК?',
    confirmPickupDesc: 'Изделие в офисе. Дальше — в ОТК.',
    confirmQcGiveDesc: 'ОТК проверит и пропустит или вернёт в цех.',
    describeDefect: 'Опишите брак',
    minThreeChars: 'Минимум 3 символа.',
    passed: '✓ Годно',
    defectRecorded: '⚠ Брак зафиксирован',
    failed: 'Не удалось',
    qcSheetTitle: 'ОТК · {{code}}',
    checked: '✓ Проверено',
    sum: 'Сумма',
    defectIntro: 'Брак — заполни причину, заказ вернётся в цех',
    reasonPlaceholder: 'Что не так (кратко)',
    descriptionPlaceholder: 'Описание — какие изделия, какой дефект (необязательно)',
    recordDefect: '⚠ Зафиксировать брак',
    passQuestion: 'Признать изделие годным?',
    recordDefectQuestion: 'Зафиксировать брак?',
    passDesc: 'Когда годны все части заказа, он сам примется на склад ГП и станет «Готово к монтажу».',
    recordDefectDesc: 'Заказ вернётся в цех на доработку.',
    passAction: 'Признать годным',
    recordAction: 'Зафиксировать',
    metaProducedLabel: 'Готовы в цехе',
    metaProducedHint: 'Заберите и привезите в офис',
    metaDeliveredLabel: 'В офисе',
    metaDeliveredHint: 'Передайте на проверку ОТК',
    metaQcCheckingLabel: 'На проверке',
    metaQcCheckingHint: 'Проверьте качество и решите: годно или брак',
    metaQcPassedLabel: 'Готово к монтажу',
    metaQcPassedHint: 'Ожидается приёмка на склад ГП',
    metaQcDefectLabel: 'Брак — в цех',
    metaQcDefectHint: 'Отвезите обратно в цех на доработку',
  },
  homePage: {
    quoteRequests: 'Запросы на расчёт',
    orders: 'Заказы',
  },
  avatarCropper: {
    title: 'Обрежьте фото',
    zoom: 'Масштаб',
    uploading: 'Загружаю…',
  },
  changePinModal: {
    titleSetup: 'Задать PIN',
    titleKnow: 'Изменить PIN',
    titleForgotRequest: 'Сбросить PIN',
    titleForgotVerify: 'Введите код',
    titleDone: 'PIN активен',
    subSetup: 'Понадобится при блокировке сессии.',
    subKnow: 'Введите текущий и новый PIN.',
    subForgotRequest: 'Сброс через Telegram-бот.',
    subForgotVerify: 'И сразу задайте новый PIN.',
    subDone: 'Готово.',
    newPin: 'Новый PIN',
    repeatPin: 'Повторите PIN',
    currentPin: 'Текущий PIN',
    repeatNewPin: 'Повторите новый PIN',
    forgotPinLink: 'Не помню текущий PIN — сбросить через Telegram',
    disablePin: 'Отключить вход по PIN',
    disableTitle: 'Отключить PIN?',
    disableDescription:
      'Блокировка по PIN будет выключена. После полного выхода для следующего входа понадобится Telegram-код.',
    disableCancel: 'Оставить PIN',
    disableConfirm: 'Отключить',
    forgotRequestHint:
      'Отправим код в ваш Telegram-чат с ботом. После подтверждения старый PIN сбросится, и вы сможете задать новый.',
    backArrow: '← Назад',
    sending: 'Отправляю…',
    sendCode: 'Отправить код',
    codeSentTelegram: 'Код отправлен в Telegram',
    codeFromTelegram: 'Код из Telegram',
    resetAndSetNew: 'Сбросить и задать новый',
    doneHint: 'Новый PIN активен. Запомните его — он понадобится при следующей блокировке сессии.',
    done: 'Готово',
    pinMismatch: 'PIN не совпадает',
    pinMustDiffer: 'Новый PIN должен отличаться от текущего',
    saving: 'Сохраняю…',
    errorGeneric: 'Ошибка',
    errorOldPinInvalid: 'Текущий PIN указан неверно',
    errorPinNotSet: 'PIN ещё не задан',
    errorSendCodeFailed: 'Не удалось отправить код',
    errorCodeInvalid: 'Неверный код',
    errorCodeExpired: 'Код истёк, запросите новый',
  },
  clientOrderCard: {
    order: 'Заказ',
    sum: 'Сумма',
    measureLabel: 'Замер:',
    installLabel: 'Установка:',
    newTitle: 'Менеджер свяжется в ближайшее время',
    newDetail: 'Уточнит детали и предложит дату замера',
    measureAssignedTitle: 'Замер назначен',
    measureAssignedDetail: 'Замерщик свяжется по поводу времени',
    measureDoneTitle: 'Замер снят',
    measureDoneDetail: 'Менеджер свяжется для подтверждения заказа',
    orderedTitle: 'Заказ оформлен',
    orderedDetail: 'Готовим изделия в производство',
    productionTitle: 'В производстве',
    productionDetail: 'Цех делает ваше изделие',
    producedTitle: 'Изделие готово',
    producedDetail: 'Назначаем водителя, затем изделие пройдёт ОТК',
    installAssignedTitle: 'Установка назначена',
    installedTitle: 'Установлено',
    installedDetail: 'Финальная приёмка работы',
    completedTitle: '✓ Заказ завершён',
    completedDetail: 'Спасибо что выбрали нас!',
    rescheduledTitle: 'Перенесён',
    rescheduledDetail: 'Согласуем новую дату',
  },
  clientQuoteCard: {
    pendingReviewLabel: 'Ждём ответа',
    quotedLabel: 'Расчёт готов',
    acceptedLabel: 'Принят',
    rejectedLabel: 'Отказ',
    expiredLabel: 'Истёк',
    pendingReviewSubtitle: 'Менеджер скоро посчитает',
    quotedSubtitle: 'Откройте чтобы принять решение',
    acceptedSubtitle: 'Заказ создан',
    expiredSubtitle: 'Создайте новый, если ещё актуально',
    requestLabel: 'ЗАПРОС',
    dayOne: 'день',
    dayFew: 'дня',
    dayMany: 'дней',
    itemOne: 'позиция',
    itemFew: 'позиции',
    itemMany: 'позиций',
  },
  clientShareSheet: {
    title: '📱 Покажите клиенту',
    copied: '✓ Скопировано',
    copyLink: '📋 Скопировать ссылку',
    hintBefore: 'Клиент сканирует камерой телефона — откроется наш',
    hintBot: 'Telegram-бот',
    hintAfter:
      'с карточкой статуса. В любой момент можно нажать «🔄 Обновить» в боте и увидеть актуальное состояние.',
    modeTelegram: 'Telegram',
    modeWeb: 'Веб-ссылка',
    webHint:
      'Клиент сканирует камерой телефона — откроется веб-страница заказа: статус, PDF заказа и оценка работы.',
    creatingLink: 'Создаю ссылку…',
    qrAlt: 'QR код заказа',
    expiresUntil: 'Ссылка действует до {{date}}',
    qrFailed: 'Не удалось создать QR',
    copyFailed: 'Не удалось скопировать',
    copyManual: 'Скопируйте вручную из текста ниже',
  },
  deviceGate: {
    copied: 'Ссылка скопирована. Откройте на телефоне.',
    copyFailed: 'Не удалось скопировать. Скопируйте ссылку вручную из адресной строки.',
    title: 'Откройте на телефоне',
    descMobile:
      'Мобильное приложение оптимизировано под одно-рукий режим — крупные кнопки, карточки заказов, фото — всё удобно листать пальцем.',
    descDesktop:
      'Для работы с компьютера используйте панель управления — она специально сделана под большой экран.',
    copyLink: 'Скопировать ссылку для телефона',
    continueAnyway: 'Всё равно продолжить на компьютере',
    footer: 'Jalousie Brand · Мобильное приложение',
  },
  inboxButton: {
    ariaLabelUnread: 'Уведомления, {{count}} непрочитанных',
    ariaLabel: 'Уведомления',
    title: 'Уведомления',
  },
  inboxSheet: {
    loadError: 'Не удалось загрузить уведомления.',
    justNow: 'только что',
    minutesAgo: '{{count}} минут назад',
    hoursAgo: '{{count}} часов назад',
    daysAgo: '{{count}} дней назад',
  },
  installerDashboardCard: {
    installs: 'Установок',
    sum: 'Сумма',
    debtors: 'Должники',
    perMonth: 'за месяц',
    totalDue: 'Всего к доплате',
    ordersCount: '{{count}} заказов',
    installedOn: 'установлено {{date}}',
    paidOutOf: 'оплачено {{paid}} из {{total}}',
  },
  managerDashboardCard: {
    measures: 'Замеры',
    successful: 'Успешные',
    conversion: 'Конверсия',
    amount: 'Сумма',
  },
  networkBadge: {
    online: 'Онлайн',
    offlineHint: 'Офлайн — изменения синхронизируются позже',
  },
  common: {
    loading: 'Загрузка…',
    retry: 'Повторить',
    cancel: 'Отмена',
    save: 'Сохранить',
    next: 'Далее',
    back: 'Назад',
    close: 'Закрыть',
    done: 'Готово',
    confirm: 'Подтвердить',
    yes: 'Да',
    no: 'Нет',
    ok: 'OK',
    delete: 'Удалить',
    clear: 'Очистить',
  },
  filter: {
    title: 'Фильтр заказов',
    search_placeholder: 'Поиск: ФИО, телефон, код',
    open_filters: 'Открыть фильтры',
    status: 'Статус',
    district: 'Район',
    city: 'Город',
    any: 'Любой',
    all_districts: 'Все районы',
    no_districts: 'У этого города нет районов',
    apply: 'Применить',
    reset: 'Сбросить',
    nothing_found_title: 'Ничего не найдено',
    nothing_found_hint: 'Попробуйте изменить поиск или сбросить фильтр.',
  },
  a11y: {
    main_nav: 'Главная навигация',
    profile_menu: 'Меню профиля, {{name}}',
    change_avatar: 'Сменить фото профиля',
    view_avatar: 'Посмотреть фото профиля',
    add_avatar: 'Добавить фото профиля',
    unread_dot: 'непрочитанное',
    order_meta: 'Метаданные заказа',
    call: 'Позвонить',
    to_point: 'К точке',
    take_photo: 'Сделать фото',
    from_gallery: 'Из галереи',
  },
  push: {
    enabled: 'Push включены',
    failed_subscribe: 'Не удалось подписаться',
    failed_subscribe_hint: 'Что-то пошло не так. Попробуйте позже.',
    denied_in_browser:
      'Уведомления запрещены в настройках браузера. Разрешите их в системных настройках.',
    unsupported: 'Ваш браузер не поддерживает Web Push.',
    no_vapid: 'Push не настроен на сервере (нет VAPID-ключей). Обратитесь к администратору.',
    no_standalone:
      'Чтобы получать уведомления на iPhone — добавьте приложение на главный экран («Поделиться» → «На экран Домой») и откройте его оттуда.',
  },
  avatar: {
    updated: 'Фото обновлено',
    deleted: 'Фото удалено',
    failed: 'Не удалось',
  },
  tab: {
    today: 'Сегодня',
    my_orders: 'Мои заказы',
    week: 'Неделя',
    dashboard: 'Сводка',
    cashbox: 'Касса',
    handoff: 'Приёмка',
    driverPickup: 'Забор',
    driverDeliver: 'Сдать',
    driverSummary: 'Сводка',
    qcCheck: 'Проверка',
    qcDefects: 'Брак',
    qcSummary: 'Сводка',
    dealer: 'Кабинет',
    dispatch: 'Диспетчер',
    installationDefects: 'Брак',
    installationQuality: 'ОТК',
    warehouseFinished: 'Готовое',
    productionOwn: 'Свой цех',
    productionContractors: 'Подрядчики',
    catalog: 'Каталог',
    quotes: 'Запросы',
    referrals: 'Друзья',
    measures: 'Замеры',
    installs: 'Монтажи',
    visits: 'Выезды',
    roles: 'Роли',
    driver: 'Водитель',
    qc: 'ОТК',
    shop: 'Цех',
    cashdesk: 'Касса',
  },
  assignments: {
    active: 'Активные',
    done: 'Завершённые',
    empty: 'Пока нет назначенных заказов.',
    status_waiting: 'Ожидание',
    status_done: 'Оформлено',
    status_overdue: 'Просрочено',
    status_refused: 'Отказано',
    all: 'Все',
    filtered_empty: 'Нет заказов с этим статусом.',
    no_date: 'Без даты',
    collapse_all: 'Свернуть всё',
    expand_all: 'Развернуть всё',
    pick_measurer: 'Замерщик',
    pick_installer: 'Монтажник',
    pick_placeholder: 'Выберите сотрудника',
    pick_hint_measures: 'Выберите замерщика сверху — покажем его замеры.',
    pick_hint_installs: 'Выберите монтажника сверху — покажем его монтажи.',
    pick_driver: 'Водитель',
    pick_hint_driver: 'Выберите водителя сверху — покажем его забор и сдачу. Кнопки работают за него.',
    roles_label: 'Чьё окно открыть',
  },
  clientReferralsPage: {
    title: 'Приведи друга',
    subtitle: 'Друг закажет жалюзи — вам бонус за рекомендацию.',
    subtitleBoth: 'Друг получит скидку на первый заказ, а вы — бонус. Выгодно обоим 🤝',
    friendBonusNote: 'Другу — скидка {{bonus}} на первый заказ. Вам — бонус за рекомендацию.',
    inviteTitle: 'Ваше приглашение',
    inviteHint:
      'Отправьте другу. Пусть при заказе назовёт менеджеру ваше имя или номер — мы свяжем заявку с вами.',
    shareBtn: 'Поделиться',
    copyBtn: 'Скопировать',
    copied: 'Скопировано',
    shareText:
      'Рекомендую Jalousie — жалюзи и шторы! Закажите замер и скажите менеджеру, что вас привёл(а) {{name}} ({{phone}}) — ему начислят бонус за рекомендацию. 🎁',
    shareTextBoth:
      'Рекомендую Jalousie — жалюзи и шторы! Закажите по моей рекомендации и получите скидку {{bonus}} на первый заказ. Скажите менеджеру, что вас привёл(а) {{name}} ({{phone}}). 🎁',
    statInvited: 'Приглашено',
    statRewarded: 'Вознаграждено',
    statEarned: 'Заработано',
    listTitle: 'Мои приглашённые',
    empty: 'Пока никого не привели. Поделитесь приглашением выше — и следите за бонусами здесь.',
    friend: 'Друг',
    status_PENDING: 'ожидает',
    status_REWARDED: 'бонус начислен',
    status_CANCELLED: 'отменён',
    loadFailed: 'Не удалось загрузить',
  },
  greet: {
    night: 'Доброй ночи',
    morning: 'Доброе утро',
    day: 'Добрый день',
    evening: 'Добрый вечер',
    none: 'Заказов пока нет.',
    today_count_one: 'Сегодня {{count}} заказ',
    today_count_few: 'Сегодня {{count}} заказа',
    today_count_many: 'Сегодня {{count}} заказов',
    upcoming_count_one: 'На ближайшие дни {{count}} заказ.',
    upcoming_count_few: 'На ближайшие дни {{count}} заказа.',
    upcoming_count_many: 'На ближайшие дни {{count}} заказов.',
    factory_idle: 'В цехе сейчас пусто.',
    factory_in_progress_one: 'В работе {{count}} заказ',
    factory_in_progress_few: 'В работе {{count}} заказа',
    factory_in_progress_many: 'В работе {{count}} заказов',
  },
  order: {
    empty_today: 'Сегодня заказов нет',
    empty_today_hint: 'Когда менеджер назначит замер или установку — увидите здесь.',
    empty_active_hint:
      'На сегодня замеров нет. Будущие — во вкладке «Неделя», просроченные и отказы — в разделе «Замеры».',
    empty_active_hint_installer:
      'На сегодня монтажей нет. Будущие — во вкладке «Неделя», все монтажи — в разделе «Монтажи».',
    empty_factory: 'В работе сейчас ничего нет',
    empty_factory_hint: 'Когда замерщик отправит расчёт в производство — заказ появится здесь.',
    section: {
      overdue: 'Просрочено',
      today: 'Сегодня',
      tomorrow: 'Завтра',
      week: 'На этой неделе',
      later: 'Позже',
      in_progress: 'В работе',
      done: 'Готовы',
      undated: 'Без даты',
    },
    error_title: 'Не удалось загрузить заказы',
  },
  auth: {
    title: 'Вход',
    subtitle: 'Введите номер, отправим код',
    phone_label: 'Номер телефона',
    phone_hint: 'Код придёт в Telegram, привязанный к этому номеру.',
    request_code: 'Получить код',
    dev_title: 'DEV · Быстрый вход',
    dev_hint: 'Без Telegram-кода. На production не показывается.',
    welcome_back: 'С возвращением, {{name}}',
    offline_pin_wrong: 'Неверный PIN',
    offline_unlock_unavailable: 'Нет связи с сервером. Войти без интернета можно после хотя бы одного входа по PIN онлайн на этом телефоне.',
    pin_prompt: 'Введите PIN, чтобы продолжить',
    pin_forgot: 'Забыл',
    other_phone: 'Войти под другим номером',
    method_password: 'Пароль',
    method_code: 'Код в Telegram',
    subtitle_password: 'Введите номер и пароль',
    password_label: 'Пароль',
    password_hint: 'Пароль выдаёт администратор.',
    show_password: 'Показать пароль',
    hide_password: 'Скрыть пароль',
  },
  profile: {
    menu: 'Меню',
    theme: 'Тема',
    theme_hint: 'Оформление приложения',
    notifications: 'Уведомления',
    notifications_hint: 'Push и Telegram',
    language: 'Язык',
    personal_data: 'Личные данные',
    my_cards: 'Мои карты',
    my_cards_hint: 'Личные карты для приёма оплаты · {{count}}',
    security_pin: 'Безопасность · PIN',
    archive: 'Архив заказов',
    notifications_history: 'История уведомлений',
    help: 'Помощь и инструкция',
    soon: 'скоро',
    delete_photo: 'Удалить фото',
    lock: 'Заблокировать (PIN)',
    logout: 'Выйти полностью',
    logout_confirm_title: 'Выйти полностью?',
    calc_images: 'Картинки в калькуляторе',
    calc_images_hint: 'Эскизы жалюзи, тканей, механизмов и карнизов',
    calc_layout: 'Стиль формы замера',
    calc_layout_hint: 'Как удобнее вам — пошагово или всё на одном экране',
    calc_layout_wizard: 'С фотографиями (пошагово)',
    calc_layout_wizard_desc: 'Классика: 7 шагов с большими карточками-фото',
    calc_layout_linear: 'Линейная форма',
    calc_layout_linear_desc: 'Всё на одном экране, выбор из списка',
    calc_layout_accordion: 'Аккордеон',
    calc_layout_accordion_desc: 'Свёрнутые группы с прогрессом',
    calc_layout_cards: 'Карточки + чипы',
    calc_layout_cards_desc: 'Компактные чипы выбора, удобно для нескольких окон',
    order_layout: 'Вид карточки заказа',
    order_layout_managed: '{{layout}} · Задано администратором',
    order_layout_hint: 'Разделами-гармошкой в одном окне или разбить на вкладки',
    overdue_highlight: 'Подсветка просроченных',
    overdue_highlight_hint:
      'Красная рамка и статичный красный бейдж у просроченных замеров в списке.',
    order_layout_single: 'Единое окно',
    order_layout_single_desc: 'Разделы-гармошки: Заказ · Расчёт · Оплата · Фото, тап раскрывает',
    order_layout_tabs2: '2 вкладки',
    order_layout_tabs2_desc: 'Основное · Расчёт',
    order_layout_tabs3: '3 вкладки',
    order_layout_tabs3_desc: 'Заказ · Расчёт · Фото и оплата',
    order_layout_tabs4: '4 вкладки',
    order_layout_tabs4_desc: 'Заказ · Расчёт · Фото · Оплата',
  },
  inbox: {
    title: 'Уведомления',
    empty: 'Здесь пока пусто',
    empty_hint: 'Когда менеджер отправит заказ или цех отметит готовность — увидите здесь.',
    mark_all_read: 'Прочитать все',
    marking: 'Помечаю…',
    unread_one: '{{count}} непрочитанное',
    unread_few: '{{count}} непрочитанных',
    unread_many: '{{count}} непрочитанных',
    ownGroup: 'Мои уведомления',
    needsDecision: 'Решение',
    filterTitle: 'Типы уведомлений',
    filterAllOn: 'Показать все',
    filterNothing: 'Все типы скрыты — включите хотя бы один.',
    type_NEW_MEASURE_ASSIGNED: 'Назначен замер',
    type_ORDER_READY_FOR_INSTALL: 'Готов к монтажу',
    type_PRODUCTION_COMPLETED: 'Цех завершил',
    type_PAYMENT_RECEIVED: 'Оплата',
    type_CLIENT_RESCHEDULED: 'Перенос',
    type_ORDER_REFUSED: 'Проблема / отказ',
    type_GENERIC: 'Маршрут и прочее',
  },
  dashboard: {
    title: 'Сводка',
    today: 'Сегодня',
    today_hint: 'заказов на сегодня',
    week: 'Неделя',
    week_hint: 'в ближайшие 7 дней',
    in_work: 'В работе',
    in_work_hint: 'активные за период',
    done: 'Завершено',
    done_hint: 'за период',
    sum_week: 'Сумма за неделю',
    sum_week_hint: 'по заказам ближайших 7 дней',
    conversion: 'Конверсия',
    conversion_hint: 'оформлено из обработанных',
    inPeriod: 'За период',
    inPeriod_hint: 'заказов в периоде',
    refused: 'Отказы',
    refused_hint: 'отказы и возвраты',
    sumPeriod: 'Сумма за период',
    sumPeriod_hint: 'по заказам периода',
  },
  logistDash: {
    title: 'Сводка',
    subtitle: 'распределение на сегодня',
    apOn: 'Автопилот',
    apOff: 'Вручную',
    cardAssigned: 'Распределено',
    cardAssignedHint: 'назначен исполнитель',
    cardQueue: 'Не распределено',
    cardQueueHint: 'ждут исполнителя',
    cardOverdue: 'Просрочено',
    cardRefused: 'Отказы',
    cardDone: 'Выполнено',
    distributedTitle: 'Готовность распределения',
    distributedHint: 'назначено {{assigned}} из {{planned}} · выполнено {{done}}',
    openDispatch: 'Раскидать в диспетчере',
    loadTitleMeasure: 'Загрузка замерщиков',
    loadTitleInstall: 'Загрузка монтажников',
    loadUnit: 'зак.',
    free: 'Свободен',
    emptyRoster: 'Активных исполнителей нет',
    emptyTitle: 'На сегодня заказов нет',
    emptyHint: 'Новые заказы появятся здесь, как только попадут в работу.',
  },
  archive: {
    title: 'Архив',
    no_orders: 'без заказов',
    orders_total: '{{count}} заказов · {{sum}}',
    search_placeholder: 'Поиск по коду, имени, адресу…',
    filter_completed: 'Завершённые',
    filter_refused: 'Отказ / возврат',
    filter_all: 'Всё',
    empty_completed: 'Завершённых заказов нет',
    empty_records: 'Записей нет',
    empty_search: 'Ничего не найдено',
    empty_search_hint: 'Попробуйте другой запрос или смените фильтр.',
    empty_hint: 'Закрытые заказы будут появляться здесь по мере работы.',
  },
  role: {
    measurer: 'Замерщик',
    installer: 'Установщик',
    manager: 'Менеджер',
    logist: 'Логист',
    accountant: 'Бухгалтер',
    production: 'Цех',
    admin: 'Администратор',
  },
  status: {
    draft: 'Черновик (amoCRM)',
    new: 'Новый',
    measure_assigned: 'Назначен замер',
    measuring: 'Идёт замер',
    ordered: 'Оформлен',
    on_hold: 'На ожидании',
    production: 'В производстве',
    delivered: 'Водитель забрал',
    qc_checking: 'Проверка ОТК',
    qc_passed: 'Готово к монтажу',
    qc_defect: 'Брак / переделка',
    produced: 'Готово — нужен водитель',
    install_assigned: 'Назначен монтаж',
    installed: 'Установлено',
    completed: 'Завершён',
    rescheduled: 'Перенос',
    refused: 'Отказ',
    returned: 'Возврат',
    measure_lost: 'Снят — просрочка',
  },
  detail: {
    title: 'Заказ',
    cancellationCredit: 'Согласованная отмена изделий',
    cancelledQuantity: 'Отменено клиентом: {{count}} шт.',
    refund: 'Возврат клиенту',
    refunded_by: 'Вернул: {{name}}',
    received_by: 'Принял: {{name}}',
    payment_pending_confirmation: 'Поступление ожидает сверки',
    block_client: 'Клиент',
    block_measure: 'Замер',
    block_install: 'Монтаж',
    installer: 'Установщик',
    block_address: 'Адрес',
    block_notes: 'Пометки',
    block_flags: 'Пометки замерщику',
    block_note: 'Примечание',
    block_calc: 'Расчёт',
    block_payment: 'Оплата',
    block_photo: 'Фото',
    block_history: 'История',
    tab_main: 'Основное',
    tab_order: 'Заказ',
    tab_calc: 'Расчёт',
    tab_media: 'Фото и оплата',
    tab_photos: 'Фото',
    tab_pay: 'Оплата',
    section_calc_pay: 'Расчёт и оплата',
    tab_note: 'Комментарий',
    block_measure_note: 'Примечание для цеха',
    measure_note_hint:
      'Что важно знать производству: особенности объекта, оговорки по замеру, пожелания клиента. Уйдёт вместе с заказом.',
    measure_note_help: 'Зачем это поле',
    measure_note_ph: 'Например: окно за батареей, створка открывается внутрь',
    measure_note_save: 'Сохранить примечание',
    measure_note_saving: 'Сохраняю…',
    measure_note_saved: 'Примечание сохранено',
    measure_note_queued: 'Сохранено офлайн — отправим при сети',
    measure_note_failed: 'Не удалось сохранить примечание',
    expand_all: 'Развернуть всё',
    collapse_all: 'Свернуть всё',
    history_title: 'История заказа',
    history_current: 'Сейчас',
    history_btn: 'История',
    btn_call: 'Звонок',
    btn_route: 'Маршрут',
    measurer: 'Замерщик',
    date_slot: 'Дата / слот',
    measurement_count: 'Количество изделий',
    measurement_plan: 'План {{count}}',
    measurement_actual: 'Факт {{count}}',
    measurement_difference: 'Расхождение {{difference}}',
    items_count_one: '{{count}} изделие',
    items_count_few: '{{count}} изделия',
    items_count_many: '{{count}} изделий',
    total: 'Итого',
    due: 'К оплате осталось',
    paid_full: '✓ Оплачено полностью',
    overpaid: '⚠ Переплата',
    payment_status: 'Статус',
    status_updated: 'Статус обновлён',
    update_failed: 'Не удалось',
    unknown_action: 'Неизвестное действие',
  },
  problem: {
    title: 'Что-то пошло не так?',
    intro: 'Выберите причину — менеджер увидит её и свяжется с клиентом.',
    saved: 'Причина зафиксирована',
    save_failed: 'Не удалось',
    submit: 'Сохранить',
    saving: 'Сохраняю…',
    comment_label: 'Комментарий (необязательно)',
    comment_placeholder: 'Например: клиент попросил перенести на пятницу 15:00',
    reason: {
      client_refused: 'Клиент отказался',
      no_answer: 'Нет ответа',
      on_hold: 'На ожидании (менеджер обработает)',
      reschedule: 'Перенос на другой день',
      wrong_address: 'Неверный адрес',
      wrong_measurement: 'Ошибка в замере',
      other: 'Другое',
    },
    help_btn: 'Что это значит?',
    help: {
      client_refused: 'Клиент отказался от заказа — заказ закрывается.',
      no_answer:
        'Клиент не берёт трубку. Отмечаем попытку дозвона, заказ остаётся у вас — попробуйте позже.',
      on_hold:
        'Клиент думает или не готов сейчас. Заказ уходит менеджеру на паузу — он им займётся.',
      reschedule: 'Замер переносится на другой день (согласуйте новую дату).',
      wrong_address:
        'Адрес неверный. Заказ уходит менеджеру — он уточнит адрес и назначит замер заново.',
      wrong_measurement:
        'В снятом замере ошибка. Заказ вернётся на перемер — комнаты остаются, вы их поправите.',
      other: 'Другая причина — опишите в комментарии. Заказ уходит менеджеру на паузу.',
    },
  },
  calc: {
    price_label: 'Стоимость', price_offline: 'оценка без сети — сервер пересчитает',
    quantity: 'Шт',
    step_type: 'Тип жалюзи',
    step_size: 'Размеры',
    step_fabric: 'Ткань',
    fabric_search: 'Поиск ткани…',
    step_ribbon: 'Лента',
    step_mechanism: 'Механизм',
    mechanism_details: 'Параметры механизма',
    mech_manual: 'Без механизма',
    mech_electric: 'Электронный',
    mech_pick_motor: 'Выберите мотор',
    mech_enter_value: 'Введите значение',
    step_cornice: 'Карниз',
    /** Вариант монтажа = таблица прайса («Со сверлением», «Без сверления Faltex»…). */
    step_mount_type: 'Вид монтажа',
    price_option: 'Вариант расчёта',
    plisse_mount_type: 'Тип монтажа',
    cornice_type: 'Тип карниза',
    cornice_arch: 'Арка',
    price_by_size: 'По размеру',
    price_by_size_hint: 'Цена зависит от размеров изделия',
    fixed_price: 'Фиксированная цена',
    base_cornice_color_hint: 'Цвет выбирается отдельно',
    mount_variant_with_cornice: 'С карнизом',
    no_cornice_color_hint: 'Без выбора цвета',
    price_range: 'от {{from}} до {{to}}',
    base_cornice: 'Базовый',
    plisse_cornice_faltex: 'Без сверления Faltex',
    plisse_cornice_drill: 'Со сверлением',
    plisse_cornice_frame: 'Без сверления на рамочном профиле',
    plisse_cornice_arch: 'Монтаж арки',
    plisse_variant_base: 'Обычный монтаж',
    plisse_opening_direction: 'Сторона открытия',
    plisse_opening_side: 'В бок',
    plisse_opening_up: 'Вверх',
    plisse_measurement: 'Измерение',
    plisse_measurement_inner: 'Внутренние',
    plisse_measurement_outer: 'Наружное',
    auto_value: 'автоматически',
    pb_cornices_count: '{{count}} карниз(а)',
    step_total: 'Итог',
    nav_back: '← Назад',
    nav_next: 'Далее →',
    save_item: '✓ Сохранить изделие',
    // Плюс к строке не добавляем — его рисует Icon в кнопке (иначе «+ + Добавить…»).
    save_add_another: 'Новый замер',
    add_size_row: 'Добавить строку размера',
    size_row_short: 'строка',
    size_row_number: 'Размер {{number}}',
    row_number_short: '№',
    remove_size_row: 'Удалить размер {{number}}',
    saving: 'Сохраняю…',
    saved: 'Изделие сохранено',
    save_failed: 'Не удалось сохранить',
    live_total: 'Текущий расчёт',
    exit_title: 'Закрыть калькулятор?',
    exit_desc: 'Введённые данные не сохранятся.',
    exit_confirm: 'Закрыть',
    room_title: 'В какой комнате?',
    preview_btn: 'Предпросмотр',
    order_preview_title: 'Предпросмотр заказа',
    order_preview_hint: 'Так заказ уйдёт в производство. Проверьте, что всё верно.',
    order_preview_empty: 'В заказе пока нет изделий.',
    order_preview_note: 'Комментарий для цеха',
    order_preview_note_empty: 'Не заполнен',
    room_items_in: 'В этой комнате',
    measurements_count: 'Замеров: {{count}}',
    copy_help_title: 'Как это работает',
    copy_help_1: 'Изделие всегда сохраняется в текущую комнату — её имя в шапке формы.',
    copy_help_2:
      'Отметьте «+ Комната…» — такое же изделие автоматически создастся и там. Удобно, когда одинаковые окна в нескольких комнатах: заполняете один раз.',
    copy_help_3: 'Копии независимые: потом каждую можно изменить или удалить отдельно.',
    pcs: 'шт',
    item_deleted: 'Изделие удалено',
    item_delete_title: 'Удалить изделие?',
    item_delete_confirm: 'Удалить',
    item_deleting: 'Удаляю…',
    item_view: 'Просмотр',
    item_view_empty: 'Только размеры',
    item_amount: 'Сумма',
    item_copy: 'Скопировать',
    item_copied: 'Скопировано',
    item_copied_hint: 'Введите размеры нового окна и сохраните',
    item_edit: 'Редактировать',
    item_edit_started: 'Правка изделия',
    item_edit_hint: 'Измените и сохраните — изделие обновится',
    item_editing: 'Правка изделия',
    item_edit_cancel: 'Отменить',
    save_edit: '✓ Сохранить изменения',
    notes: 'Заметка',
    notes_label: 'Комментарий',
    notes_placeholder: 'Особенности замера, пожелания клиента…',
    item_delete_failed: 'Не удалось удалить',
    preview_hide: 'Скрыть',
    preview_title: 'Что заполнено',
    mm: 'мм',
    cm: 'см',
    m: 'м',
    room_existing: 'Уже есть',
    room_new: 'Новая комната',
    room_placeholder: 'Например: Спальня',
    room_create: 'Создать комнату',
    change_room: '+ Комната',
    room_creating: 'Создаю…',
    room_created: 'Комната «{{name}}» создана',
    room_name_exists:
      'Комната с таким названием уже есть. Выберите её в списке или введите другое название.',
    room_close_title: 'Закрыть без сохранения?',
    room_close_desc: 'Введённое название комнаты не сохранится.',
    room_close_stay: 'Остаться',
    room_close_confirm: 'Закрыть',
    new_room_warn: 'Несохранённые данные по замеру могут удалиться',
    items_count_one: '{{count}} изделие',
    items_count_few: '{{count}} изделия',
    items_count_many: '{{count}} изделий',
    // Step 2 — размеры (замерщик вводит в МЕТРАХ; в базе храним мм)
    width_mm: 'Ширина, м',
    height_mm: 'Высота, м',
    quantity_pcs: 'Количество, шт',
    // Планка у карниза с признаком requiresSlatSize: сначала выбор «С планкой /
    // Без планки», и только при «С планкой» — поле размера.
    slat_choice: 'Планка',
    slat_on: 'С планкой',
    slat_off: 'Без планки',
    slat_size: 'Размер планки, м',
    slat_size_optional: 'Размер планки, м (необязательно)',
    slat_size_ph: 'напр. 0,05',
    bort_size: 'Размер борта, мм',
    bort_size_ph: 'напр. 50',
    // Бахрома: только выбор, размера нет — по нему в цеху понимают, шить её или нет.
    bahroma_choice: 'Бахрома',
    bahroma_on: 'С бахромой',
    bahroma_off: 'Без бахромы',
    slat_title: 'Планка',
    slat_no: 'Без планки',
    slat_yes: 'С планкой',
    area: 'Площадь',
    perimeter: 'Периметр',
    decrease: 'Уменьшить',
    increase: 'Увеличить',
    // Шаги 3-6 — без выбора
    no_fabric: 'Без ткани',
    no_fabric_hint: 'Изделие без полотна — например, только механизм',
    no_ribbon: 'Без ленты',
    no_ribbon_hint: 'Изделие пойдёт без декоративной отделки',
    no_mechanism: 'Без механизма',
    no_cornice: 'Без карниза',
    no_photo: 'Нет фото',
    categories_empty: 'Категории не загружены. Попросите админа заполнить «Прайс-лист».',
    // Step 7 — итог
    summary_size: 'Размер',
    summary_fabric: 'Ткань',
    summary_ribbon: 'Лента',
    summary_mechanism: 'Механизм',
    summary_cornice: 'Карниз',
    summary_base_price: 'Базовая цена',
    summary_total: 'Итого',
    zero_hint_components:
      'Итог 0 сум — для этого типа жалюзи не настроены совместимые ткань/механизм. Попросите администратора привязать их во вкладке «Совместимость».',
    zero_hint_fixed:
      'Итог 0 сум — для этого типа жалюзи не указана базовая цена. Администратор должен задать её в админке (Прайс → Виды жалюзи).',
    markup: 'Наценка',
    discount: 'Скидка',
    help_toggle: 'Что это и зачем?',
    help_hide: 'Скрыть подсказку',
    help_intro:
      'Базовая цена уже посчитана по прайсу (ткань × площадь, лента × периметр, механизм за изделие, карниз × ширина). Наценка и скидка — это финальная корректировка вручную.',
    help_markup_title: 'Наценка — когда поднять цену',
    help_markup_reasons:
      'Дорогой район · сложный объект (высокий этаж без лифта, нестандарт) · срочный заказ · клиент готов платить больше.',
    help_discount_title: 'Скидка — когда уступить',
    help_discount_reasons:
      'Постоянный клиент · крупный заказ (3+ окон) · сезонная акция · торг на месте.',
    help_formula:
      'Формула: Итого = Базовая × (1 + Наценка%) × (1 − Скидка%). Сначала применяется наценка, потом скидка.',
    help_note:
      'Оба поля пишутся в заказ отдельно — руководитель видит, сколько добавили или уступили.',
    // Прайс-режим (pbMode): цена прямо из прайс-листа
    step_option: 'Карниз / вариант',
    pb_search: 'Поиск по прайсу…',
    pb_new: 'новинка',
    pb_colors_count: '{{count}} цв.',
    step_fabric_color: 'Цвет ткани',
    step_ribbon_color: 'Цвет ленты',
    step_cornice_color: 'Цвет карниза',
    pb_pick_fabric: 'Выберите полотно из прайса',
    pb_pick_option: 'Выберите карниз / вариант',
    pb_without_cornice: 'Без карниза',
    pb_without_cornice_hint: 'Только полотно, без карниза — дешевле по прайсу',
    pb_price_missing_short: 'Цена не указана',
    pb_price_incomplete_short: 'Не заполнены все диапазоны',
    pb_price_missing_for_option: 'Сначала укажите в прайсе цену «{{option}}» для «{{row}}».',
    pb_price_ranges_incomplete: 'Для «{{option}}» не заполнены все диапазоны: {{ranges}}.',
    pb_price_calc: 'Считаю цену по прайсу…',
    // Несколько тканей в одном изделии (Текстильные) и ограничения размеров
    pb_extra_fabric: 'Ткань и цвет ткани {{n}}',
    step_fabric_and_color: 'Ткань и цвет ткани',
    pb_extra_fabric_add: 'Ещё ткань',
    pb_extra_fabric_remove: 'Убрать ткань',
    // Раскладка ламелей изделия из двух тканей (модуль «Раскладка ламелей»)
    lamella_layout: 'Раскладка',
    lamella_pick: 'Выбрать',
    lamella_need_width: 'Сначала ширина',
    lamella_kind_EDGES: 'Края + середина',
    lamella_kind_ALTERNATE: 'Через одну',
    lamella_edges: 'По краям',
    lamella_middle_left: 'в середине: {{n}}',
    lamella_step_q: 'По сколько?',
    lamella_step: 'по {{n}}',
    lamella_swap: 'Поменять цвета',
    lamella_done: 'Готово',
    lamella_text_edges: 'края — {{fabric}}, по {{n}} шт',
    lamella_text_middle: 'середина — {{fabric}}, {{n}} шт',
    lamella_text_alternate: 'через одну по {{step}}: {{parts}}',
    lamella_text_part: '{{fabric}} — {{n}} шт',
    pb_size_limit_width: 'Ширина {{value}} м больше максимума {{max}} м',
    pb_size_limit_height: 'Высота {{value}} м больше максимума {{max}} м',
    pb_size_limit_area: 'Площадь {{value}} м² больше максимума {{max}} м²',
    step_lamella: 'Ламели',
    step_lamella_color: 'Цвет ламелей',
    interglass_system: 'Система',
    summary_option: 'Карниз / вариант',
    summary_pb_price: 'Цена по прайсу',
    summary_pb_no_cornice: 'без карниза',
    // Инлайн-карточка «Скидка/наценка» в разделе «Расчёт» (MarkupDiscountCard)
    mdTitle: 'Скидка / наценка',
    mdHint: 'Клиент этого не видит',
    mdSwitchTitle: 'Изменить условия расчёта?',
    mdSwitchFromMarkup: 'В заказе уже выбрана наценка {{value}}. Переход на скидку заменит её и изменит первоначальные данные расчёта.',
    mdSwitchFromDiscount: 'В заказе уже выбрана скидка {{value}}. Переход на наценку заменит её и изменит первоначальные данные расчёта.',
    mdSwitchAfterSave: 'Изменения применятся только после нажатия «Сохранить».',
    mdSwitchConfirm: 'Переключить',
    mdNone: 'Нет',
    mdSave: 'Сохранить',
    mdSaved: 'Сохранено',
    mdSaveErr: 'Не удалось сохранить',
    mdLimit: 'Скидка больше лимита (макс {{max}}%)',
    mdSubtotal: 'Подытог',
    mdTotal: 'Итого',
    mdEdit: 'Изменить',
    mdDelete: 'Удалить',
    mdMarkupBadge: 'Наценка',
    mdDiscountBadge: 'Скидка',
  },
  payment: {
    title: 'Принять оплату',
    due_label: 'К оплате осталось',
    fill_remaining: 'Заполнить остаток',
    method: 'Метод',
    pick_method: 'Выберите тип оплаты',
    method_required: 'Сначала выберите тип оплаты',
    account_required: 'Сначала выберите счёт получения',
    account_unavailable: 'Этот счёт недоступен. Обновите список и выберите другой.',
    accounts_loading: 'Загружаем счета получения…',
    accounts_load_failed: 'Не удалось загрузить счета. Повторите загрузку.',
    accounts_offline: 'Подключитесь к сети, чтобы загрузить счета получения.',
    payer_inn_invalid: 'ИНН — 9 цифр (ПИНФЛ — 14)',
    amount_required: 'Введите сумму оплаты',
    note_placeholder: 'Комментарий (необязательно)',
    accept: 'Принять',
    saving: 'Сохраняю…',
    accepted: 'Платёж принят',
    accepted_full: 'Оплата принята полностью',
    recordedAwaitingConfirmation: 'Платёж записан · ожидает сверки',
    savedOfflineTitle: 'Платёж сохранён офлайн',
    pendingRecordedLimit: 'Часть остатка уже записана и ждёт сверки. Новый платёж: максимум {{sum}}.',
    failed: 'Не удалось',
    confirm_title: 'Принять {{sum}}?',
    method_cash: 'Наличные',
    method_card: 'Карта',
    method_uzum: 'Оплата через UZUM',
    method_alif: 'Оплата через Alif Nasiya',
    method_transfer: 'Перевод',
    amount_exceeds_due: 'Сумма превышает остаток заказа. Максимум можно принять {{sum}}.',
  },
  completeInstall: {
    compactTitle: 'Завершение монтажа',
    loading: 'Загружаю заказ…',
    progressPhoto: 'Фото',
    progressPay: 'Оплата',
    progressSign: 'Подпись',
    photoStep: 'Фото результата',
    photoCount: 'Фото добавлено: {{n}}',
    photoOptional: 'Необязательно — можно завершить без фото',
    hide: 'Скрыть',
    viewPhotos: 'Фото',
    add: 'Добавить',
    payFull: 'Оплачено полностью',
    payRemaining: 'Осталось принять {{due}}',
    payDoneHint: 'Долг по заказу закрыт',
    payPartialHint: 'Можно принять несколькими платежами: наличными, картой или переводом',
    done: 'Готово',
    takePayment: 'Принять оплату',
    signStep: 'Подпись клиента',
    signDone: 'Акт приёмки подписан',
    signRequired: 'Итоговый акт обязателен для закрытия заказа',
    signAfterInstall: 'Подпись откроется после завершения монтажа',
    afterInstall: 'После монтажа',
    completeOrder: 'Завершить заказ',
    signTake: 'Подписать акт',
    readiness: 'Готовность к завершению',
    blockedDue: 'Осталось принять {{due}}',
    signAfterInstallReady: 'Завершите монтаж — затем откроется подпись клиента',
    signRequiredReady: 'Монтаж сохранён. Подпишите акт для закрытия заказа',
    ready: 'Всё готово',
    doneToast: 'Монтаж завершён',
    completeFailed:
      '\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u044C \u043C\u043E\u043D\u0442\u0430\u0436',
    completing: '\u0417\u0430\u0432\u0435\u0440\u0448\u0430\u0435\u043C\u2026',
    completeInstall:
      '\u0417\u0430\u0432\u0435\u0440\u0448\u0438\u0442\u044C \u043C\u043E\u043D\u0442\u0430\u0436',
  },
  installIssue: {
    contactTitle: 'Нужна помощь связаться с клиентом',
    rescheduleTitle: 'Нужно перенести монтаж',
    productTitle: 'Проблема с изделием',
    contactEffect: 'Монтаж останется назначенным. Руководитель установки получит задачу помочь связаться с клиентом и подтвердить дальнейший выезд.',
    rescheduleEffect: 'Текущий слот освободится. Заказ будет ожидать согласования новой даты у руководителя установки.',
    contactPending: 'Ожидается связь с клиентом',
    waitingDate: 'Ожидает согласования даты',
    openBtn: 'Не смог установить',
    draftSavedToast: 'Черновик сохранён',
    draftRemovedToast: 'Черновик удалён',
    title: 'Проблема на монтаже',
    editTitle: 'Уточнить ошибки монтажа',
    viewErrorsTitle: 'Ошибки монтажа',
    helpButton: 'Справка',
    helpTitle: 'Как работает эта форма',
    intro:
      'Заказ {{code}}. Выберите причину, уточните обстоятельства и отправьте сообщение.',
    productOnlyIntro:
      'Заказ {{code}}. Для каждого неустановленного окна оформите отдельное обращение. Решение по ошибке замера, изделию или пожеланиям клиента принимает руководитель установки.',
    lockedDefectIntro:
      'Заказ {{code}}. Этап: {{status}}. Это обращение доступно только для просмотра. Если нужна корректировка, обратитесь к руководителю установки.',
    reason: {
      CANT_REACH: { title: 'Не дозвонился клиенту', hint: 'Клиент не берёт трубку' },
      CLIENT_ABSENT: { title: 'Клиента нет на месте', hint: 'Приехал — никого нет / не открывают' },
      NO_ACCESS: { title: 'Нет доступа к объекту', hint: 'Стройка, ремонт, объект не готов' },
      CLIENT_ASKED: { title: 'Клиент просит перенести', hint: 'Просит приехать в другой день' },
      PRODUCT_PROBLEM: {
        title: 'Причина неустановки',
        hint: 'Размеры, комплектация, качество, отказ клиента или изменение его пожеланий',
      },
    },
    productDefectBtn: 'Причина неустановки',
    affectedItemsTitle: 'Выберите изделие',
    affectedItemsHint:
      'Откройте изделие и выберите ситуацию. При ошибке замера сами укажите правильные размеры и комплектацию.',
    defectCommentScopeHint:
      'Комментарий относится только к новой отмеченной группе. Ранее созданная партия и её комментарий не смешиваются с новыми.',
    affectedItemsRequired: 'Выберите хотя бы одно изделие',
    allDefectsWillBeCancelled: 'Все отметки будут сняты, а заявка о браке отменена',
    cancelDefectReport: 'Отменить ошибочную заявку',
    cancelDefectReportTitle: 'Снять все отметки брака?',
    cancelDefectReportDescription:
      'Заявка о браке будет отменена, а партия вернётся к этапу установки. После этого монтаж можно завершить обычным способом.',
    cancelDefectReportConfirm: 'Да, отменить заявку',
    itemQuantity: '{{count}} шт.',
    partialInstallTitle: 'Монтаж частично завершён',
    notInstalledTitle: 'Монтаж не выполнен',
    reinstallTitle: 'Повторный монтаж после переделки',
    partialInstallSummary: 'Установлено: {{installed}} · На переделке: {{defect}}',
    returnDestination: { WAREHOUSE: 'На склад брака', CONTRACTOR: 'Поставщику' },
    returnTransport: { INSTALLER: 'Установщик везёт сам', DRIVER: 'Водитель' },
    installerReturnHint:
      'Отправляйте обращение при фактической сдаче изделия в пункт брака: отправка сразу фиксирует передачу. Отдельно подтверждать возврат не нужно.',
    installedItemsTitle: 'Установлено и принято',
    defectItemsTitle: 'На переделку',
    defectDescriptionTitle: 'Комментарий к изделиям на переделке',
    partialActDownload: 'Скачать частичный акт',
    partialActDownloadFailed: 'Не удалось скачать частичный акт',
    returningTo: 'Возврат: {{destination}}',
    completeReturn: 'Подтвердить возврат',
    returnCompletedToast: 'Возврат подтверждён',
    returnCompleteFailed: 'Не удалось подтвердить возврат',
    defectCommentTitle: 'Комментарий к выбранным изделиям · {{count}}',
    defectCommentPlaceholder: 'Причина обращения по выбранным изделиям (обязательно)',
    showItemComment: 'Комментарий',
    hideItemComment: 'Скрыть комментарий',
    commentPlaceholder: 'Комментарий к причине (необязательно)',
    sendDefect: 'Отправить в брак',
    viewPartialActButton: 'Посмотреть частичный акт',
    partialActShort: 'Частичный акт',
    viewPartialActsButton: 'Посмотреть акты установки',
    partialActsShort: 'Акты',
    partialActsTitle: 'Архив подписанных актов',
    partialActsHint: 'Ранее подписанные документы сохранены для просмотра. Для новых выездов ведётся отчёт, а в конце заказа подписывается один итоговый акт.',
    partialActItem: 'Акт от {{date}}',
    finishInstallShort: 'Завершить',
    addErrorsButton: 'Добавить ошибки',
    addErrorsShort: 'Добавить ошибки',
    viewErrorsButton: 'Посмотреть ошибки',
    viewErrorsShort: 'Ошибки',
    defectStage: {
      REPORTED: 'Заявлено',
      RETURNING_TO_WAREHOUSE: 'Возврат на склад',
      IN_DEFECT_WAREHOUSE: 'На складе брака',
      AWAITING_APPROVAL: 'Ожидает решения',
      IN_RESOLUTION: 'В исправлении',
      READY_FOR_REINSTALL: 'Готово к монтажу',
      CLOSED: 'Закрыто',
      CANCELLED: 'Отменено',
    },
    defectStageShort: {
      REPORTED: 'Заявлено',
      RETURNING_TO_WAREHOUSE: 'Возврат на склад',
      IN_DEFECT_WAREHOUSE: 'На складе брака',
      AWAITING_APPROVAL: 'Ждёт решения',
      IN_RESOLUTION: 'В исправлении',
      READY_FOR_REINSTALL: 'Готово к монтажу',
      CLOSED: 'Закрыто',
      CANCELLED: 'Отменено',
    },
    defectCanChange: 'можно изменить',
    defectChangeLocked: 'изменять нельзя',
    defectWillBeAdded: 'Новая партия',
    defectSeparateFromPrevious: 'отдельно от первой',
    defectWillBeRemoved: 'будет снято',
    partialActSeparateHint:
      'Брак по заказу {{code}} уже зарегистрирован. Теперь клиент подтверждает результат только текущего визита.',
    partialActSignedToast: 'Частичный акт подписан',
    partialActSignFailed: 'Не удалось подписать частичный акт',
    updateDefect: 'Обновить список ошибок',
    updatedToast: 'Список ошибок обновлён',
    continueToPartialAct: 'Продолжить к частичному акту',
    backToIssue: 'Назад к ошибке',
    signAndSubmit: 'Подписать и сообщить о браке',
    submit: 'Отправить',
    sending: 'Отправляю…',
    doneToast: 'Сообщение отправлено',
    failToast: 'Не удалось отправить',
  },
  productionIssue: {
    title: 'Не могу произвести',
    intro:
      'Заказ {{code}}. Опишите проблему — менеджер получит уведомление и разберёт брак в «Распределении».',
    reason: {
      MATERIAL: { title: 'Брак материала', hint: 'Пришёл дефектный материал / полотно' },
      WRONG_SIZE: { title: 'Не тот размер или цвет', hint: 'Размеры/цвет не совпадают с заказом' },
      DAMAGE: {
        title: 'Повреждение при изготовлении',
        hint: 'Испортилось в процессе производства',
      },
      OTHER: { title: 'Другое', hint: 'Опишите ситуацию в комментарии' },
    },
    descriptionPlaceholder: 'Что именно не так (обязательно)…',
    submit: 'Сообщить о браке',
    sending: 'Отправляю…',
    doneToast: 'Брак зафиксирован, менеджер уведомлён',
    failToast: 'Не удалось отправить',
  },
  productionDash: {
    title: 'Цех',
    subtitle: 'Моя история подтверждений готовности',
    scopeLabel: 'Тип производства',
    scopeAll: 'Все',
    scopeOwn: 'Свой цех',
    scopeContractors: 'Подрядчики',
    historyTitle: 'Подтверждено мной',
    historyEmpty: 'За выбранный период подтверждений нет',
    historyEmptyHint: 'Здесь появятся работы, по которым вы нажали «Готово» в Field.',
    completedAt: 'Готово: {{date}}',
  },
  productionQueue: {
    ownTitle: 'Свой цех',
    ownSubtitle: 'Работы внутреннего цеха · {{count}}',
    contractorsTitle: 'Подрядчики',
    contractorsSubtitle: 'Работы внешних исполнителей · {{count}}',
    searchPlaceholder: 'Поиск: номер заказа или клиент',
    searchEmpty: 'Ничего не найдено',
    searchEmptyHint: 'Проверьте номер заказа или имя клиента.',
    loadFailed: 'Не удалось загрузить очередь',
    ownEmpty: 'У своего цеха нет активных работ',
    contractorsEmpty: 'У подрядчиков нет активных работ',
    emptyHint: 'Новые задания появятся после распределения менеджером.',
    customerMissing: 'Клиент не указан',
    contractor: 'Цех: {{name}}',
    branch: 'Филиал: {{name}}',
    branchMissing: 'Филиал не указан',
    deadline: 'Срок: {{date}}',
    driver: 'Водитель: {{name}}',
    telegramOrField: 'Готовность можно подтвердить здесь или в Telegram подрядчика.',
    fieldOnly: 'Готовность подтверждается только здесь.',
    waitingLabel: 'Ожидает:',
    waitProduction: 'подтверждения сотрудника производства',
    waitContractor: '{{name}} · подтверждение готовности',
    waitOtherProduction: '{{names}} · готовность остальных частей',
    waitRemainingDistribution: 'распределения оставшегося состава',
    waitDriverAssignment: 'назначения водителя',
    waitDriver: 'водителя · забор продукции',
    waitDriverNamed: '{{name}} · забор продукции',
    waitWarehouseNamed: '{{names}} · приёмка ГП{{warehouse}}',
    waitWarehouseUnassigned: 'заведующий складом не назначен · приёмка ГП{{warehouse}}',
    waitWarehouseRouteMissing: 'не настроен склад ГП для филиала',
    markReady: 'Готово',
    confirming: 'Подтверждаю…',
    confirmTitle: 'Подтвердить готовность?',
    confirmHint:
      '{{code}} · {{contractor}}. Работа перейдёт на следующий этап, отмена доступна только ответственным сотрудникам.',
    confirm: 'Подтвердить',
    ownReadyDone: 'Готовность своего цеха подтверждена',
    contractorReadyDone: 'Готовность подрядчика подтверждена',
    actionFailed: 'Не удалось подтвердить готовность',
    status: {
      IN_PRODUCTION: 'В работе',
      OWN_READY: 'Своя часть готова',
      READY_NEEDS_DRIVER: 'Готово · нужен водитель',
      READY_WAITING_DRIVER: 'Заявка водителю',
    },
  },
  productionGate: {
    unassigned: 'Заказ ещё не распределён на цех — дождитесь назначения менеджером.',
    defect: 'Брак зафиксирован — ждёт решения менеджера в «Распределении».',
  },
  cashdesk: {
    title: 'Касса',
    target: {
      title: 'Куда поступили деньги',
      account: 'Счёт',
      method: 'Способ оплаты',
      date: 'Дата поступления',
      confirm: 'Подтвердить',
      noAccounts: 'Нет счетов компании. Заведите их в админке: Настройки → Финансы → Счета.',
    },
    subtitle: 'Смены, поступления, заявки',
    tabShifts: 'Смены',
    tabReceipts: 'Поступления',
    tabClaims: 'Заявки',
    filter: { unconfirmed: 'Не подтв.', confirmed: 'Подтв.', all: 'Все' },
    emptyShifts: 'Смен на утверждение нет',
    emptyShiftsHint: 'Когда полевой сдаст кассу — смена появится здесь.',
    emptyReceipts: 'Платежей нет',
    emptyClaims: 'Заявок нет',
    emptyClaimsHint: 'Заявки клиентов об оплате появятся здесь.',
    loadFailed: 'Не удалось загрузить данные',
    rowsCappedQueue: 'Показаны 500 самых старых платежей на сверку. Обработайте их, чтобы увидеть следующие.',
    rowsCappedPeriod: 'Показаны только 500 платежей. Сузьте период, чтобы увидеть остальные.',
    shiftTitle: 'Кассовая смена',
    paymentsN: '{{n}} платежей',
    approve: 'Утвердить',
    reject: 'Отклонить',
    confirm: 'Подтвердить',
    confirmed: 'Подтверждено',
    receivedBy: 'принял: {{name}}',
    rejectTitle: 'Причина отклонения',
    rejectPlaceholder: 'Опишите, что не так…',
    rejectSubmit: 'Отклонить',
    shiftApproved: 'Смена утверждена',
    receiptConfirmed: 'Платёж подтверждён',
    receiptUnconfirmed: 'Подтверждение снято',
    claimConfirmed: 'Заявка подтверждена',
    claimConfirmTitle: 'Подтвердить поступление?',
    claimConfirmHint: 'Сверьте деньги по выписке или кассе. Фото чека само по себе не подтверждает поступление.',
    confirmArrival: 'Деньги поступили',
    orderReceipts: 'Заказ · чеки: {{count}}',
    claimReceipts: 'Чеки заявки: {{count}}',
    unlinkedOrderReceipts: 'Чеки заказа без связи: {{count}}',
    noClaimReceipt: 'Чек заявки не загружен',
    openClaimReceipt: 'Открыть чек заявки',
    rejected: 'Отклонено',
    actionFailed: 'Не удалось выполнить',
    status: { PENDING: 'На проверке', APPROVED: 'Утверждено', REJECTED: 'Отклонено' },
    method: {
      CASH: 'Наличные',
      CARD: 'Карта',
      CARD_P2P: 'Карта P2P',
      CARD_TERMINAL: 'Терминал',
      TRANSFER: 'Перечисление',
      UZUM_NASIYA: 'Оплата через UZUM',
      ALIF_NASIYA: 'Оплата через Alif Nasiya',
    },
  },
  accountantDash: {
    title: 'Финансы',
    subtitle: 'Сводка и очереди на подтверждение',
    shifts: 'Смены',
    receipts: 'Поступл.',
    claims: 'Заявки',
    cashPending: 'На подтверждении',
    cashPendingHint: '{{n}} платежей ждут',
    receivables: 'Дебиторка',
    receivablesHint: '{{n}} с долгом',
    revenue: 'Выручка',
    revenueHint: 'Оплачено за период',
    netProfit: 'Чистая прибыль',
    netProfitHint: 'После расходов',
    confirmedReceipts: 'Подтверждено',
    confirmedReceiptsHint: 'Оплаты за период',
    outflows: 'Расход и возвраты',
    outflowsHint: 'За период',
    loadError: 'Не все финансовые данные загрузились. Обновите экран.',
  },
  measureContact: {
    newDateQuestion: 'Клиент подтвердил новую дату?',
    yes: 'Да', no: 'Нет',
    open: 'Отметить звонок', confirmed: 'Клиент подтвердил',
    awaitingCall: 'Ожидается созвон', visitCancelled: 'Замер отменился',
    NO_ANSWER: 'Не дозвонился', RESCHEDULE_REQUESTED: 'Нужен перенос',
    CLIENT_DECLINED: 'Клиент отказался от замера', WRONG_ADDRESS: 'Неверный адрес', OTHER: 'Нужна помощь',
  },
  action: {
    start_measure: 'Начать замер',
    continue_measure: 'Продолжить замер',
    edit_measure: 'Изменить замеры',
    confirm_measure: 'Выезжаю на замер',
    confirm_measure_confirm_title: 'Выезжаете на замер?',
    confirm_measure_confirm_desc: 'Клиент готов принять — выезжаем по графику.',
    complete_measure: 'Завершить замер',
    complete_measure_confirm_title: 'Отметить «Замер снят»?',
    complete_measure_confirm_desc:
      'Размеры внесены, замер закрывается. Заказ пойдёт дальше в работу.',
    formalize: 'Оформлен',
    formalize_confirm_title: 'Оформить заказ?',
    formalize_confirm_desc:
      'Все комнаты уйдут в работу, правки замера станут недоступны. Оформить можно только после минимального аванса.',
    not_ordered: 'Обратная связь',
    plan_reject: 'Не подходит',
    send_to_production: 'В производство',
    send_to_production_confirm_title: 'Отправить в производство?',
    send_to_production_confirm_desc: 'Замер закрепляется, изменить будет нельзя.',
    accept_install: 'Завершить установку',
    accept_install_confirm_title: 'Завершить установку?',
    reopen_installation: 'Отменить завершение',
    reopen_installation_short: 'Отменить',
    reopen_installation_confirm_title: 'Отменить завершение монтажа?',
    reopen_installation_confirm_desc:
      'Монтаж снова станет активным. Можно исправить результат и провести завершение заново. Оплата, фото, акты и назначенное время сохранятся.',
    reopen_installation_audit_reason: 'Исправление ошибочного завершения установщиком',
    reopen_installation_done: 'Завершение отменено — монтаж снова активен',
    reopen_installation_failed: 'Не удалось отменить завершение',
    client_refusal_title: 'Отменить заказ по отказу клиента?',
    client_refusal_description:
      'Заказ перейдёт в «Отказ» и исчезнет из активного монтажа. Оплата, фото, документы и история сохранятся.',
    client_refusal_reason: 'Причина отказа',
    client_refusal_placeholder: 'Например: клиент передумал и отказался от установки…',
    client_refusal_hint: 'Укажите реальную причину — менеджер увидит её в истории заказа.',
    client_refusal_confirm: 'Подтвердить отказ',
    client_refusal_done: 'Отказ клиента зафиксирован',
    client_refusal_failed: 'Не удалось отменить заказ',
    install_no_answer: 'Не дозвонился',
    install_no_answer_confirm_title: 'Зафиксировать попытку дозвона?',
    install_no_answer_confirm_desc: 'Менеджер увидит, что клиент не отвечает по установке.',
    complete: 'Завершить',
    complete_confirm_title: 'Завершить заказ?',
    complete_confirm_desc: 'После этого вернуть в работу можно только через админа.',
    accept_payment: 'Принять оплату',
    sign_act: 'Подписать акт',
    download_act: 'Скачать акт',
    mark_produced: 'Отметить готовым',
    mark_produced_confirm_title: 'Заказ готов?',
    mark_produced_confirm_desc:
      'Менеджер получит уведомление — можно слать водителя и назначать монтаж.',
    report_defect: '⚠ Не могу произвести (брак)',
    problem: '⚠ Что-то пошло не так',
    problem_install: '⚠ Возврат / проблема',
  },
  reject: {
    title: 'Не подходит?',
    intro:
      'Почему не получится? Если едете к другому клиенту — укажите к какому, менеджер предложит обмен днём.',
    mode_client: 'Иду к другому клиенту',
    mode_other: 'Другое',
    search_client: 'Поиск: клиент, адрес',
    loading: 'Загрузка…',
    nothing: 'Ничего не найдено',
    placeholder: 'Например: в это время уже есть встреча в другом районе',
    submit: 'Отправить отказ',
    saving: 'Отправляю…',
    saved: 'Отказ отправлен менеджеру',
    failed: 'Не удалось отправить',
  },
  errors: {
    money_account_required: 'Выберите счёт получения.',
    money_account_not_found: 'Счёт больше не найден. Обновите список и выберите другой.',
    money_account_archived: 'Счёт архивирован. Обновите список и выберите другой.',
    money_account_method_mismatch: 'Счёт не подходит для этого способа оплаты. Выберите другой.',
    money_account_branch_mismatch: 'Выберите счёт филиала заказа или общий счёт фирмы.',
    money_account_access_denied: 'Этот счёт вам недоступен. Обновите список и выберите другой.',
    company_money_account_required: 'Выберите счёт компании.',
    invalid_receiving_account: 'Счёт зачисления терминала недоступен. Обновите список или выберите другой терминал.',
    installation_proposal_changed: "Предложение установщика изменилось. Откройте актуальную версию и проверьте её заново.",
    installation_proposal_review_required: "Сначала рассмотрите предложение установщика: используйте исправленный вариант или подтвердите исходную комплектацию с пояснением.",
    installation_proposal_correction_required: "Сначала сравните и сохраните исправленную комплектацию.",
    installation_proposal_has_correction: "Уже сохранён исправленный вариант. Проверьте его перед подтверждением решения.",
    installation_customer_change_requires_agreement: "Зафиксируйте согласованные с клиентом условия или результат проверки его пожеланий. Суммы и платежи согласуются отдельно.",
    installation_correction_requires_new_manufacture: "Для увеличения размера или смены вида изделия выберите новое изготовление.",
    installation_proposal_replacement_required: "Для выбранной ситуации укажите исправленный вариант либо выберите «Нужен повторный замер».",
    installation_proposal_photo_invalid: "Фото не найдено в этом заказе. Прикрепите его заново.",
    installation_proposal_sign_separately: "Сначала отправьте ошибку и исправления, затем подпишите отдельный акт частичного монтажа.",
    installation_proposal_requires_product_problem: "Исправления можно приложить только к сообщению о браке изделия.",
    measurement_changed: 'Замер изменён или разделён на отдельные окна. Введённые данные сохранены в форме или очереди; обновите заказ и проверьте нужное окно.',
    invalid_window_count: 'Введите целое количество от 1 до 999.',
    single_window_quantity_locked: 'В сохранённом замере одно окно. Для новых окон используйте копирование.',
    measurement_correction_item_changed: "Параметры изделия изменились. Откройте форму заново и проверьте исправления.",
    measurement_corrections_not_available: "Изменения уже заблокированы решением или подписанным актом. Обратитесь к руководителю.",
    measurement_correction_quantity_locked: "Количество должно совпадать с количеством изделий в этом деле о браке.",
    defect_correction_scope_changed: "Состав или количество изменились. Обновите данные и проверьте выбранные изделия.",
    _unknown: 'Что-то пошло не так',
    _fallback: 'Не получилось выполнить действие. Попробуйте ещё раз или обратитесь к менеджеру.',
    insufficient_role: 'Недостаточно прав для этого действия.',
    daily_close_total_changed: 'Сумма сдачи изменилась, пока вы её проверяли. Обновите и примите заново.',
    not_authenticated: 'Сессия истекла — войдите заново.',
    // Ограничения полей: лимит показываем в тех же единицах, что и поле ввода.
    _maxValue: '{{field}}: не больше {{limit}} {{unit}}',
    _minValue: '{{field}}: не меньше {{limit}} {{unit}}',
    _fieldWidth: 'Ширина',
    _fieldHeight: 'Высота',
    _fieldSlat: 'Размер планки',
    _fieldBort: 'Размер борта',
    _fieldQty: 'Количество',
    _unitM: 'м',
    _unitMm: 'мм',
    _unitPcs: 'шт',
    invalid_credentials: 'Неверный телефон или пароль',
    forbidden: 'Нет прав на это действие',
    no_access: 'Нет доступа к этому разделу',
    not_found: 'Не найдено',
    dev_login_disabled_in_prod: 'Быстрый вход недоступен',
    multibranch_disabled: 'Раздел филиалов выключен',
    user_not_found: 'Сотрудник с таким номером не найден',
    user_deactivated: 'Доступ закрыт. Свяжитесь с администратором.',
    user_inactive: 'Учётная запись заблокирована',
    telegram_not_bound: 'Telegram не привязан. Попросите админа выслать пригласительную ссылку.',
    code_invalid: 'Неверный код',
    code_expired: 'Код истёк, запросите новый',
    too_many_attempts: 'Слишком много попыток. Запросите новый код.',
    pin_invalid: 'Неверный PIN',
    pin_locked: 'PIN заблокирован на 2 минуты после нескольких неудачных попыток',
    session_revoked: 'Эта сессия была отозвана. Войдите заново по телефону.',
    session_expired: 'Сессия истекла. Войдите заново.',
    invalid_transition: 'Этот статус нельзя поменять отсюда',
    result_photos_required: 'Сначала загрузите фото готовой работы — без них монтаж не завершить',
    full_payment_required: 'Перед завершением примите полную оплату заказа',
    order_not_found: 'Заказ не найден',
    room_not_found: 'Комната не найдена',
    room_name_required: 'Введите название комнаты',
    room_name_exists: 'Комната с таким названием уже есть',
    item_not_found: 'Изделие не найдено',
    photo_not_found: 'Фото не найдено',
    order_in_final_status: 'Заказ уже закрыт — изменить нельзя',
    completed_installation_not_reopenable:
      'Этот монтаж уже нельзя вернуть в работу. Обновите карточку.',
    completed_installation_assigned_installer_only:
      'Отменить завершение может только назначенный установщик.',
    installation_assigned_installer_only:
      'Зафиксировать отказ может только назначенный установщик.',
    installation_client_refusal_reason_required: 'Укажите причину отказа клиента.',
    order_calculation_locked: 'Расчёт этого заказа закрыт для изменений',
    order_discount_locked: 'Скидку и наценку можно изменить до завершения заказа и подписания акта',
    discount_below_paid_amount: 'Итог заказа не может быть меньше уже принятой оплаты',
    pb_cell_empty: 'Для выбранного варианта цена не указана.',
    pb_option_incomplete_ranges: 'Для правильного расчёта не указана цена для всех диапазонов.',
    items_required: 'Добавьте хотя бы одно изделие',
    address_required: 'Укажите адрес',
    customer_name_and_phone_required: 'Укажите имя и телефон клиента',
    customer_not_found: 'Клиент не найден',
    refusal_code_required: 'Выберите причину',
    comment_required: 'Добавьте комментарий',
    defect_reason_required: 'Укажите причину брака',
    qc_defect_items_required: 'Выберите минимум одно конкретное бракованное изделие.',
    qc_defect_items_only_for_defect: 'Изделия можно выбирать только для решения «Брак».',
    qc_defect_item_duplicated: 'Одно изделие выбрано несколько раз.',
    qc_defect_item_outside_assignment: 'Выбранное изделие не входит в эту передачу подрядчика.',
    defect_item_not_in_physical_unit: 'Выбранное изделие не входит в проверяемый заказ или партию.',
    installer_defect_comment_required: 'Опишите, что обнаружил установщик (минимум 3 символа).',
    installer_defect_group_locked:
      'Эта группа уже отправлена на исправление и больше не изменяется.',
    partial_installation_act_not_found: 'Частичный акт не найден или уже отменён.',
    partial_installation_act_installer_only:
      'Отменить подпись может только установщик, который оформил акт.',
    partial_installation_act_locked:
      'Решение по браку уже запущено — отменить подпись через Field нельзя.',
    partial_installation_act_stale:
      'Данные дела изменились. Обновите карточку и повторите проверку.',
    acceptance_act_required: 'Нужен подписанный клиентом акт приёмки',
    acceptance_act_not_signed: 'Клиент ещё не подписал акт приёмки',
    invalid_measurer: 'Этого сотрудника нельзя назначить замерщиком',
    measurer_has_open_order: 'У сотрудника уже есть незакрытый замер',
    measure_assignment_horizon: 'Замерщика можно назначить только на сегодня или завтра. На более позднюю дату сохраните замер без исполнителя.',
    measure_assignment_date_required: 'Сначала укажите дату замера.',
    measurer_cash_unsettled: 'Касса за прошлые дни ещё не принята. Назначить новый замер на завтра можно после приёмки кассы в админке.',
    client_quotes_paused: 'Запросы расчёта через приложение временно приостановлены.',
    measurer_has_unpaid_order: 'У сотрудника есть заказ с незакрытой оплатой',
    earlier_install_not_closed: 'Сначала закройте более ранний монтаж',
    cross_branch_assignment: 'Нельзя назначить сотрудника из другого филиала',
    invalid_width: 'Неверная ширина',
    invalid_height: 'Неверная высота',
    invalid_quantity: 'Неверное количество',
    invalid_amount: 'Неверная сумма',
    amount_must_be_positive: 'Сумма должна быть больше нуля',
    payment_exceeds_order_due: 'Сумма превышает остаток заказа. Лишние деньги принять нельзя.',
    amount_must_be_non_negative: 'Сумма не может быть отрицательной',
    order_not_billable:
      'Аванс можно принять только после завершения замера. Сначала нажмите «Завершить замер».',
    invalid_discount: 'Неверная скидка',
    discount_exceeds_limit: 'Скидка превышает допустимый лимит',
    invalid_price: 'Неверная цена',
    no_file: 'Файл не выбран',
    not_an_image: 'Можно загрузить только изображение',
    invalid_extension: 'Неподдерживаемый формат файла',
    invalid_kind: 'Неверный тип фото',
    code_taken: 'Такой код уже используется',
    code_already_used: 'Такой код уже используется',
    name_required: 'Укажите название',
    name_already_used: 'Такое название уже есть',
    name_already_used_in_city: 'Такое название в этом городе уже есть',
    has_dependencies: 'Нельзя удалить: есть связанные записи',
    in_use: 'Нельзя удалить: запись используется',
    cannot_unset_default: 'Нельзя снять «по умолчанию», не назначив другой филиал',
    branch_not_found: 'Филиал не найден',
    city_not_found: 'Город не найден',
    expense_not_found: 'Расход не найден',
    dealer_not_found: 'Дилер не найден',
    defect_case_not_found: 'Дело о браке не найдено.',
    defect_warehouse_item_not_found: 'Складская запись по этому браку не найдена.',
    qc_verdict_requires_qc_or_installation_case:
      'Комментарий ОТК доступен только для брака, найденного на ОТК или монтаже.',
    qc_verdict_already_confirmed: 'Комментарий ОТК уже передан руководителю установки.',
    qc_verdict_required_first: 'Сначала ОТК должен принять изделие и оставить комментарий.',
    qc_role_required: 'Это действие доступно только сотруднику ОТК.',
    manager_resolution_must_be_rework_or_new_order:
      'Выберите переделку текущего изделия или создание нового заказа.',
    manager_resolution_requires_qc_or_installation_case:
      'Это решение доступно только для брака, найденного на ОТК или монтаже.',
    manager_resolution_already_confirmed:
      'Причина и маршрут уже подтверждены руководителем установки.',
    installation_manager_role_required: 'Это действие доступно только руководителю установки.',
    resolution_due_at_must_be_in_future: 'Срок готовности переделки должен быть в будущем.',
    driver_and_due_date_only_for_rework:
      'Водитель и срок указываются только для переделки текущего изделия.',
    defect_item_already_processed: 'Изделие уже передано на следующий этап.',
    defect_item_must_be_received_first: 'Сначала примите изделие на склад брака.',
    defect_item_already_received: 'Изделие уже принято на склад брака.',
    defect_item_not_received: 'Изделие ещё не принято на склад брака.',
    defect_item_must_be_in_warehouse: 'Операция доступна только для изделия на складе брака.',
    defect_storage_location_required: 'Укажите фактическое место хранения изделия.',
    defect_handover_at_invalid: 'Укажите корректные дату и время передачи изделия.',
    defect_handover_at_in_future: 'Время передачи не может быть в будущем.',
    defect_transferor_required: 'Выберите сотрудника, который передал изделие.',
    defect_transferor_invalid: 'Выбранный сотрудник не может передать это изделие.',
    defect_receipt_forbidden:
      'Приёмку брака может провести только руководитель установки или администратор.',
    defect_receipt_operator_invalid: 'Не удалось определить сотрудника, который проводит приёмку.',
    warehouse_manager_required: 'Выберите сотрудника, который принял изделие.',
    warehouse_manager_invalid: 'Выбранный сотрудник не может принять изделие.',
    defect_disposition_already_decided:
      'Маршрут уже проведён. Чтобы изменить его, сначала отмените проведение.',
    defect_driver_return_already_accepted:
      'Водитель уже принял изделие. Отменить или изменить отправку нельзя.',
    defect_driver_return_already_reassigned: 'Задача уже назначена водителю. Обновите список.',
    defect_driver_return_not_waiting: 'Возврат уже не ожидает водителя. Обновите список.',
    defect_driver_return_not_available: 'Для этого решения повторная отправка водителю недоступна.',
    return_driver_required: 'Выберите водителя для возврата изделия подрядчику.',
    return_driver_not_found: 'Выбранный водитель недоступен. Обновите список и выберите другого.',
    resolution_due_at_required: 'Укажите плановую дату готовности переделки.',
    original_contractor_required: 'У изделия не указан первоначальный подрядчик.',
    original_contractor_not_found: 'Первоначальный подрядчик не найден или отключён.',
    target_contractor_required: 'Выберите нового подрядчика.',
    target_contractor_not_found: 'Выбранный подрядчик не найден или отключён.',
    target_contractor_must_be_different: 'Новый подрядчик должен отличаться от первоначального.',
    defect_case_has_no_stage_to_rollback: 'В этом деле нет операции, которую можно отменить.',
    defect_stage_rollback_forbidden: 'У вас нет права отменять текущий этап дела.',
    defect_case_already_approved: 'Решение уже утверждено.',
    defect_case_not_approved: 'Сначала утвердите решение по браку.',
    defect_case_must_be_approved: 'Сначала утвердите решение по браку.',
    defect_case_not_ready_to_close: 'Дело ещё нельзя закрыть: не все обязательные этапы завершены.',
    defect_cause_not_classified: 'Сначала укажите причину брака и ответственных.',
    defect_resolution_missing: 'Сначала выберите маршрут изделия.',
    defect_resolution_already_started: 'Решение уже запущено в работу.',
    defect_product_already_manufactured: 'Продукт уже изготовлен, изменения вносить нельзя.',
    warehouse_disposition_not_selected:
      'Руководитель установки ещё не выбрал дальнейшее движение изделия.',
    write_off_must_be_completed_first: 'Сначала оформите физическое списание изделия.',
    write_off_not_selected: 'Списание не выбрано в маршруте изделия.',
    resolution_not_selected: 'Сначала выберите дальнейшее движение изделия.',
    install_return_not_waiting_driver:
      'Заявка уже изменилась и больше не ожидает водителя. Обновите список.',
    install_return_not_in_transit:
      'Возврат уже завершён или ещё не принят водителем. Обновите список.',
    install_return_assignee_only: 'Подтвердить этот возврат может только назначенный сотрудник.',
    invalid_driver: 'Выберите активного сотрудника с ролью водителя.',
    invalid_warehouse_manager: 'Выберите активного сотрудника склада.',
    client_rejection_assessment_required:
      'Укажите результат проверки регламента при отказе клиента.',
    client_rejection_company_responsibility_required:
      'При соблюдённом регламенте ответственность должна быть назначена компании.',
    client_rejection_measurer_responsibility_required:
      'При ошибочном информировании ответственность должна быть назначена замерщику.',
    contractor_and_due_date_required: 'Укажите подрядчика и плановую дату готовности.',
    defect_execution_snapshot_invalid:
      'Данные запуска решения повреждены. Обратитесь к администратору.',
    defect_execution_snapshot_missing:
      'Не удалось восстановить исходные данные запуска. Обратитесь к администратору.',
    installer_not_found: 'Установщик не найден или недоступен.',
    manufacturing_responsible_required:
      'Для производственного брака укажите ответственного подрядчика или компанию.',
    original_item_must_be_written_off_first: 'Сначала полностью спишите исходное изделие.',
    other_cause_description_required: 'Для другой причины добавьте подробное описание.',
    own_workshop_manufacturing_company_full_responsibility_required:
      'Брак собственного цеха должна полностью покрыть компания.',
    own_workshop_qc_responsibility_not_applicable:
      'Для брака собственного цеха отдельная ответственность ОТК не применяется.',
    qc_fixed_penalty_required: 'Для ответственности ОТК укажите фиксированный штраф.',
    qc_responsibility_requires_qc_employee:
      'Для ответственности ОТК выберите сотрудника с ролью ОТК.',
    replacement_order_must_belong_to_same_customer:
      'Заказ на замену должен принадлежать тому же клиенту.',
    replacement_order_required: 'Выберите или создайте связанный заказ на замену.',
    responsible_contractor_not_found: 'Ответственный подрядчик не найден или отключён.',
    responsible_user_not_found: 'Ответственный сотрудник не найден или отключён.',
    settlement_amount_must_be_positive: 'Сумма начисления должна быть больше нуля.',
    use_correct_decision_for_approved_case:
      'Утверждённое решение изменяется через действие «Исправить решение».',
  },
};
const uz = {
  applicationSign: {
    signAndComplete: 'Imzolash va rasmiylashtirish', completed: 'Ariza imzolandi, buyurtma rasmiylashtirildi',
    requiredHint: 'Buyurtmani rasmiylashtirish uchun mijoz imzosi kerak. Arizani tekshiring va imzolang. Oyna yopilsa, buyurtma rasmiylashtirilmaydi.',
    draw: 'Shu yerga imzo qo‘ying', clear: 'Imzoni tozalash', title: 'Arizani imzolash', sign: 'Arizani imzolash', saved: 'Ariza imzolandi, PDF saqlandi',
    hint: 'Mijozga shartnoma arizasini ko‘rsating, keyin ekranda imzo qo‘yishini so‘rang.',
    consent: 'Buyurtma tarkibi, o‘lchamlari va narxi tekshirildi. Ommaviy oferta shartlari bilan tanishdim va roziman.',
    preview: 'Ariza PDF’ini ko‘rish', download: 'Imzolangan arizani yuklab olish',
    signedAt: 'Ariza {{date}} da imzolangan (Toshkent). Imzolash vaqtidagi asl nusxa saqlangan.',
    unavailable: 'Arizani tayinlangan o‘lchovchi buyurtma rasmiylashtirilgandan keyin va montaj tugashidan oldin imzolata oladi.',
    retry: 'Qayta urinish',
    needsNetwork: 'Arizani imzolash uchun internet kerak: mijoz server tayyorlagan hujjatni imzolaydi. Buyumlar va rasmlar saqlangan — aloqa paydo boʻlganda buyurtmani rasmiylashtiring.',
    offlineHint: 'Server bilan aloqa yoʻq. Mijozga quyidagi tarkib, oʻlcham va narxlarni koʻrsating — u shu roʻyxatni imzolaydi. Aloqa tiklanganda server uni ariza bilan solishtiradi: biror narsa oʻzgargan boʻlsa (masalan, kurs), qayta imzolash soʻraladi.',
    offlinePriceMissing: 'Aloqa yoʻq va ayrim buyumlar narxi nomaʼlum (prays telefonga yuklanmagan). Internet paydo boʻlganda rasmiylashtiring.',
    offlineQueued: 'Imzo saqlandi — aloqa paydo boʻlganda buyurtma rasmiylashtiriladi',
    total: 'Jami',
  },
  clientRequestsPaused: { title: 'Ilova orqali buyurtma', message: 'Boʻlim vaqtincha mavjud emas', hint: 'Ilova orqali narx hisoblash soʻrovlari va yangi buyurtmalarni rasmiylashtirish toʻxtatilgan.', back: 'Bosh sahifaga' },
  plannedMeasureArrival: 'kelish ~{{time}}',
  installationCorrection: correctionUz,
  rejectMeasureDialog: {
    reasonGoingToClient: '{{customer}} ({{code}}) mijozga ketyapman',
    savedOffline: 'Oflayn saqlandi — internet paydo boʻlganda yuboramiz',
  },
  signActSheet: {
    noSignatureTitle: 'Imzo yoʻq',
    noSignatureBody: 'Mijozdan ekranga imzo qoʻyishini soʻrang.',
    signedTitle: 'Dalolatnoma imzolandi',
    signedBody: 'PDF saqlandi — mijozga yuborish mumkin.',
    saveFailed: 'Saqlab boʻlmadi',
    downloadFailed: 'Yuklab boʻlmadi',
    alreadySignedTitle: 'Dalolatnoma allaqachon imzolangan',
    downloadPdf: 'PDFni yuklab olish',
    alreadySignedBody:
      'Mijoz {{customer}} {{order}} buyurtma boʻyicha dalolatnomani allaqachon imzolagan. PDFni yuklab oling va mijozga WhatsApp yoki Telegram orqali yuboring.',
    resign: 'Qayta imzolash',
    signTitle: 'Mijoz imzosi · {{order}}',
    saving: 'Saqlanmoqda…',
    doneSigned: 'Tayyor · imzolandi',
    hint: 'Mijozga barmoq yoki stilus bilan imzo qoʻyishga bering. Soʻngra biz darhol qabul dalolatnomasi PDFini yaratamiz va uni yuborish mumkin boʻladi.',
    orderLabel: 'Buyurtma {{order}}',
    signedAndCompletedTitle: 'Buyurtma yakunlandi',
    signedAndCompletedBody: 'Dalolatnoma imzolandi, buyurtma yakunlandi.',
    signAndComplete: 'Imzolash va yakunlash',
  },
  acceptPaymentSheet: {
    title: 'Toʻlov usuli',
    toPay: 'Toʻlovga:',
    workDuration: 'Ish muddati:',
    daysUnit: 'kun',
    placingOrder: 'Buyurtma rasmiylashtirilmoqda…',
    confirmAndPlace: 'Tasdiqlash va rasmiylashtirish',
    pickMethod: 'Toʻlov usulini tanlang',
    hintLine1: 'Bu toʻlov boʻyicha istak — menejer bogʻlanadi',
    hintLine2: 'va yakuniy shartlarni tasdiqlaydi.',
    orderPlacedTitle: 'Buyurtma rasmiylashtirildi',
    orderPlacedDesc: 'Menejer oʻlchov va oʻrnatish boʻyicha bogʻlanadi',
    failed: 'Amalga oshmadi',
    methodCashTitle: 'Oʻrnatishda naqd pul',
    methodCashSubtitle: 'Montaj kuni ustaga toʻlov',
    methodCardTitle: 'Onlayn karta orqali',
    methodCardSubtitle: 'Menejer Payme/Click havolasini yuboradi',
    methodTransferTitle: 'Bank oʻtkazmasi',
    methodTransferSubtitle: 'Rekvizitlarni menejer beradi',
    methodUzumTitle: 'UZUM orqali toʻlov',
    methodUzumSubtitle: '24 oygacha ortiqcha toʻlovsiz',
    methodAlifTitle: 'Alif Nasiya orqali toʻlov',
    methodAlifSubtitle: 'Banksiz, pasport boʻyicha',
  },
  quoteDetailPage: {
    appBarTitle: 'Soʻrov',
    notFound: 'Soʻrov topilmadi',
    statusPendingLabel: 'Menejer javobini kutmoqda',
    statusPendingHint: 'Menejer hisob-kitob bilan bogʻlanadi',
    statusQuotedLabel: 'Hisob-kitob tayyor',
    statusQuotedHint: 'Mijoz qarorini kutyapmiz',
    statusAcceptedLabel: 'Qabul qilindi',
    statusAcceptedHint: 'Buyurtma yaratildi',
    statusRejectedLabel: 'Rad etildi',
    statusRejectedHint: 'Mijoz rad etdi',
    statusExpiredLabel: 'Muddati oʻtdi',
    statusExpiredHint: '7 kun javobsiz',
    clientBlock: 'Mijoz',
    nameLabel: 'Ism',
    phoneLabel: 'Telefon',
    addressBlock: 'Oʻrnatish manzili',
    itemsBlock: 'Pozitsiyalar · {{count}}',
    categoryFallback: 'Kategoriya',
    mmUnit: 'mm',
    clientNoteBlock: 'Mijoz izohi',
    managerQuoteBlock: 'Menejer hisob-kitobi',
    totalSum: 'Yakuniy summa',
    workTerm: 'Ish muddati',
    daysUnit: 'kun',
    quotedAtValidUntil: 'Hisob-kitob {{from}} · {{until}} gacha amal qiladi',
    orderCreatedBlock: 'Buyurtma yaratildi',
    openOrder: 'Buyurtmani ochish →',
    quoteSentToast: 'Hisob-kitob mijozga yuborildi',
    adjustQuote: 'Hisob-kitobni tuzatish',
    respondToClient: 'Mijozga javob berish',
    totalSumField: 'Yakuniy summa (soʻm)',
    workDaysField: 'Ish muddati (ish kunlari)',
    commentField: 'Izoh (ixtiyoriy)',
    commentPlaceholder: 'Qoʻshimcha tafsilotlar, shartlar…',
    sending: 'Yuborilmoqda…',
    updateQuote: 'Hisob-kitobni yangilash',
    sendToClient: 'Mijozga yuborish',
    clientWillSee: 'Mijoz koʻradi:',
    rejectAcceptedToast: 'Rad etish qabul qilindi',
    failedToast: 'Bajarilmadi',
    rejectReasonBlock: 'Rad etish sababi (ixtiyoriy)',
    rejectPlaceholder: 'Juda qimmat, arzonroq topdim…',
    rejectButton: 'Rad etish',
    acceptAndOrder: 'Qabul qilish va rasmiylashtirish · {{sum}}',
    notSuitable: 'Toʻgʻri kelmaydi',
    afterAcceptHint: 'Qabul qilingach buyurtma yaratiladi. Menejer toʻlov boʻyicha bogʻlanadi.',
  },
  quotesListPage: {
    title: 'Mijozlar soʻrovlari',
    pendingCountOne: '{{count}} ta yangi soʻrov',
    pendingCountFew: '{{count}} ta yangi soʻrov',
    pendingCountMany: '{{count}} ta yangi soʻrov',
    awaitYourReply: 'javobingizni kutmoqda',
    allHandled: 'Hammasi koʻrib chiqildi',
    tabNew: 'Yangi',
    tabWaitingClient: 'Mijozni kutmoqda',
    tabAccepted: 'Qabul qilingan',
    tabRejected: 'Rad etilgan',
    tabAll: 'Hammasi',
    emptyTitle: 'Boʻsh',
    emptyAllHandled: 'Barcha soʻrovlar koʻrib chiqilgan.',
    emptyNoneInSection: 'Bu boʻlimda soʻrovlar yoʻq.',
    statusPendingReview: 'Javob kutilmoqda',
    statusQuoted: 'Mijoz qaroriʼ kutilmoqda',
    statusAccepted: 'Qabul qilingan',
    statusRejected: 'Rad etildi',
    statusExpired: 'Muddati oʻtgan',
    itemsOne: 'pozitsiya',
    itemsFew: 'pozitsiya',
    itemsMany: 'pozitsiya',
    daysOne: 'kun',
    daysFew: 'kun',
    daysMany: 'kun',
  },
  dealerOrderPage: {
    appBarTitle: 'Buyurtma',
    acceptedToastTitle: 'Buyurtma qabul qilindi',
    acceptedToastBody: 'Tasdiqlaganingiz uchun rahmat!',
    acceptFailed: 'Bajarilmadi',
    shippingLabel: 'Yetkazib berish',
    trackLabel: 'trek',
    paidOfTotal: '{{total}} dan {{paid}} toʻlangan',
    debtLabel: 'qarz',
    itemsTitle: 'Pozitsiyalar',
    mm: 'mm',
    quantityLabel: 'Soni',
    pricePerPiece: '{{price}} dona uchun',
    confirmReceiptBtn: 'Qabul qilishni tasdiqlash',
    confirmReceiptTitle: 'Qabul qilishni tasdiqlaysizmi?',
    confirmReceiptDesc: 'Buyurtma yopiladi. Bu amalni bekor qilib boʻlmaydi.',
    recordPaymentBtn: 'Toʻlovni yozish',
    paymentSheetTitle: 'Toʻlovni yozish',
    paymentAmountLabel: 'Summa (soʻm)',
    paymentMethodLabel: 'Toʻlov usuli',
    paymentMethodCash: 'Naqd',
    paymentMethodCard: 'Karta',
    paymentMethodTransfer: 'Oʻtkazma',
    paymentCommentLabel: 'Izoh (ixtiyoriy)',
    paymentSubmit: 'Yozish',
    paymentSaving: 'Saqlanmoqda…',
    paymentSavedTitle: 'Toʻlov yozildi',
    paymentSavedBody: 'Buxgalter mablagʻ tushganini tekshiradi.',
    paymentFailed: 'Toʻlovni yozib boʻlmadi',
    paymentAmountInvalid: 'Noldan katta summa kiriting',
    paymentNote:
      'Toʻlov siz tomonidan eʼlon qilingan deb belgilanadi — buxgalter haqiqiy tushumni tasdiqlaydi.',
    statusDraft: 'Qoralama',
    statusOrdered: 'Rasmiylashtirilgan',
    statusInProduction: 'Ishlab chiqarishda',
    statusReady: 'Tayyor',
    statusShipped: 'Joʻnatildi — tasdiqlash kutilmoqda',
    statusAccepted: 'Qabul qilingan',
    statusRefused: 'Bekor qilingan',
    productRoll: 'Rulonli',
    productCombo: 'Kombo',
    productPlisse: 'Plisse',
    productRoman: 'Rim pardalari',
    productTextile: 'Toʻqimachilik',
    productHorizontal: 'Gorizontal',
    productMosquito: 'Pashshaxonali',
    productBamboo: 'Bambukli',
    productWood: 'Yogʻochli',
    productCustom: 'Boshqa',
  },
  calcFlat: {
    selectPlaceholder: '— tanlang —',
    selectRequired: 'Tanlang',
    expand: 'yoyish',
    collapse: 'yigʻish',
    noneOption: '— tanlamaslik —',
    pcs: 'dona',
    sizePreview: '{{w}} × {{h}}',
  },
  calcWizard: {
    addedToRooms: '{{count}} ta xonaga qoʻshildi',
    savedOffline: 'Oflayn saqlandi — internet paydo boʻlganda yuboramiz',
  },
  step2Size: {
    unitArea: 'm²',
    unitPerimeter: 'm',
  },
  step4Ribbon: {
    pricePerMeter: '{{price}} / m',
  },
  step5Mechanism: {
    pricePerItem: '{{sum}} mahsulot uchun',
  },
  step6Cornice: {
    pricePerMeter: '{{price}} / m',
  },
  step7Total: {
    corniceColorLabel: 'Karniz rangi',
    corniceColorPlaceholder: 'Oq, kumush, jigarrang…',
    windowPhotoLabel: 'Deraza surati',
    notesLabel: 'Mahsulotga izoh',
    notesPlaceholder: 'Xususiyatlar, istaklar…',
    maxDiscountNote: 'Maks. chegirma {{pct}}%',
    sumUnit: 'soʻm',
    copyToRoomsLabel: 'Shu mahsulotni xonalarga qoʻshish',
    currentRoomFallback: 'joriy',
    copyToRoomsSummary:
      '«{{name}}» xonasiga + yana {{count}} ta saqlanadi. Bir xil parametrlar — turli xonalar uchun.',
    uploadFailed: 'Yuklab boʻlmadi',
    removePhoto: 'Suratni olib tashlash',
    uploading: 'Yuklanmoqda…',
    attachWindowPhoto: 'Deraza suratini biriktirish',
  },
  clientPaymentSheet: {
    title: 'Buyurtma toʻlovi',
    paidFully: 'Toʻliq toʻlangan',
    remaining: 'Toʻlash kerak',
    paidOfTotal: '{{total}} dan {{paid}} toʻlangan',
    paymentHistory: 'Toʻlovlar tarixi',
    methodCash: 'Naqd',
    methodCard: 'Karta',
    methodTransfer: 'Oʻtkazma',
    payToTitle: 'Qayerga toʻlash',
    copy: 'Rekvizitlarni nusxalash',
    copied: 'Nusxalandi',
    helpTitle: 'Toʻlov boʻyicha maʼlumot',
    helpBody: 'Toʻlovdan keyin summani kiriting va chek rasmini biriktiring. Ariza «Mening arizalarim» boʻlimida koʻrinadi. Buxgalter tushumni tekshirib, toʻlovni tasdiqlaydi; ungacha buyurtma qoldigʻi oʻzgarmaydi.',
    amountLabel: 'Toʻlov summasi, soʻm',
    sendClaim: 'Arizani yuborish',
    enterAmount: 'Toʻlov summasini kiriting',
    attachReceiptFirst: 'Toʻlov chekining rasmini biriktiring',
    claimSent: 'Ariza yuborildi',
    claimSentHint: 'Buxgalter pul tushumini tekshirib, toʻlovni tasdiqlaydi.',
    claimHistory: 'Mening arizalarim',
    claimPending: 'Tekshirilmoqda',
    claimConfirmed: 'Tasdiqlandi',
    claimRejected: 'Rad etildi',
    claimRejectReason: 'Sabab',
    claimLoadError: 'Arizalarni yuklab boʻlmadi',
    uploadedReceipts: 'Mening yuklangan cheklarim',
    openReceipt: 'Yuklangan chekni ochish',
  },
  orderPhotoBlock: {
    photoAdded: 'Foto qoʻshildi',
    photosAdded: '{{count}} ta foto qoʻshildi',
    compressionNote: 'edi {{was}}, tejaldi {{pct}}%',
    offlineNote: 'Tarmoq paydo boʻlganda yuklaymiz',
    addPhotoError: 'Fotoni qoʻshib boʻlmadi',
    deleteError: 'Oʻchirib boʻlmadi',
    finishedWorkPhotos: 'Tayyor ish fotolari',
    photos: 'Foto',
    whatToPhotograph: 'Nimani suratga olish kerak',
    photoHelpTitle: 'Foto boʻyicha maʼlumot',
    kindFacade: 'fasad',
    kindObject: 'obyekt',
    kindMeasure: 'oʻlchov',
    kindDone: 'tayyor',
    kindReceipt: 'toʻlov cheki',
    kindDefect: 'brak',
    receiptAttachLabel: 'Chek / toʻlov skrini',
    receiptAttached: 'Chek biriktirildi',
    previousClaimReceipts: 'Oldingi ariza cheklari. Yuklanmagan boʻlsa, tarmoqda qayta urinib koʻring yoki chekni oʻchiring.',
    retryUpload: 'Qayta',
    kindFacadeHint:
      'podʼezd / kirish. Hamkasblarga keyingi tashrifda manzilni topishga yordam beradi.',
    kindObjectHint: 'jalyuzi oʻrnatiladigan deraza / xonalar.',
    kindMeasureHint: 'qalam bilan chizmalar va oʻlchamli belgilar.',
    kindDoneHint: 'oʻrnatishdan keyingi tayyor jalyuzi fotolari.',
    badgeHint: 'Har bir fotoning turi uning burchagidagi belgida koʻrinadi.',
    uploadFailed: 'xato',
    uploading: 'yuklanmoqda',
    deletePhoto: 'Fotoni oʻchirish',
    emptyEditHint: 'Deraza/obyektni suratga oling — montajchilarga yordam beradi.',
    noPhotosYet: 'Hozircha foto yoʻq.',
    clientEmptyDoneTitle: 'Oʻrnatuvchi hali foto yuklamagan',
    clientEmptyPendingTitle: 'Fotolar ish tugagach paydo boʻladi',
    clientEmptyDoneHint:
      'Agar bir necha kun ichida fotolar paydo boʻlmasa — menejerga murojaat qiling.',
    clientEmptyPendingHint:
      'Montajchilar oʻrnatishni tugatgach, bu yerda tayyor ish fotolarini koʻrasiz.',
  },
  paymentSheet: {
    savedOffline: 'Oflayn saqlandi — internet paydo boʻlganda yuboriladi',
    sum: 'soʻm',
    payerInn: 'STIR',
    payerFill: 'Toʻldirish',
    payerName: 'Nomi',
    payerNotFound: 'Bu STIR boʻyicha hech narsa topilmadi',
    payerLookupFailed: 'STIR boʻyicha topib boʻlmadi, keyinroq urinib koʻring',
    payerLookupOffline: 'STIR boʻyicha qidirish uchun internet kerak',
    noAccountsWarning:
      'Mos hisoblar yoʻq. Administratordan tushum hisobini qoʻshishni soʻrang.',
    noCardsHint:
      'Mos kartalar yoʻq. «Profil → Mening kartalarim»ga shaxsiy kartani qoʻshing yoki administratordan firma kartasini soʻrang.',
    receivingAccount: 'Tushum hisobi',
    account: 'Hisob',
    accountNotSpecified: 'Hisob koʻrsatilmagan',
    personalCash: 'Shaxsiy kassa · {{name}}. Pulni moliya boʻlimiga topshirasiz.',
    originalAccount: 'Avvalgi hisob',
    originalAccountKept: 'summa tuzatilganda saqlanadi',
    groupReceiverCards: 'Pulni qabul qilgan xodim kartalari',
    groupBankAccounts: 'Firmaning bank hisoblari',
    groupTerminals: 'Terminallar',
    groupMyCards: 'Mening kartalarim (kassamga)',
    groupCompanyCards: 'Firma kartalari',
    custodyMine: '✓ Sizning kassangizga — naqd bilan birga topshirasiz.',
    custodyReceiver: 'Pulni qabul qilgan xodim kassasiga — u moliya boʻlimiga topshiradi.',
    custodyFirm: 'Toʻgʻridan-toʻgʻri firmaga — kassangizga emas. Buxgalter tushumini tasdiqlaydi.',
    selectAccountOption: '— {{target}} tanlang —',
    targetCardAcc: 'kartani',
    targetTerminalAcc: 'terminalni',
    targetAccountAcc: 'hisobni',
    method_CASH: 'Naqd',
    method_CARD_P2P: 'Kartaga oʻtkazma',
    method_CARD_TERMINAL: 'Karta (terminal)',
    method_TRANSFER: 'Pul oʻtkazma',
    method_UZUM_NASIYA: 'UZUM orqali toʻlov',
    method_ALIF_NASIYA: 'Alif Nasiya orqali toʻlov',
  },
  problemDialog: {
    savedOffline: 'Oflayn saqlandi — internet paydo boʻlganda yuboriladi',
  },
  orderDetailPage: {
    pendingSync: 'Tarmoq kutilmoqda',
    installationHistoryView: 'Faqat ko‘rish',
    installationHistoryHint: 'Montaj hisobotingiz saqlandi. Keyingi tashrif alohida tayinlanadi.',
    clientQr: 'Mijoz uchun QR',
    clientQrTitle: 'Buyurtmani kuzatish uchun mijozga QR koʻrsatish',
    callAttemptLogged: 'Qoʻngʻiroq urinishi qayd etildi',
    willSendWhenOnline: 'Tarmoq paydo boʻlganda yuboriladi',
    formalizeNeedsAdvance: 'Avval kamida {{pct}}% avans qabul qiling — {{sum}} yetishmayapti',
    formalizeModeLoading: 'Buyurtmani rasmiylashtirish rejimi tekshirilmoqda…',
    formalizeModeUnavailable:
      'Rasmiylashtirish rejimini tekshirib bo‘lmadi. Internetga ulanib, qayta urinib ko‘ring.',
    formalizePickRooms:
      '«Hisob-kitob» boʻlimida kerakli oʻlchovlarni belgilang — minimal summa tanlovdan keyin koʻrinadi.',
    paymentEditTitle: 'Toʻlovni tuzatish',
    paymentEditSaved: 'Toʻlov tuzatildi',
    paymentEditHint:
      'Kiritishda xato qilsangiz, summa, toʻlov usuli yoki tushum hisobini tuzating. Oʻzgarish tarixga (audit) tushadi.',
    paymentEditAmount: 'Summa, soʻm',
    orderProgress: 'Buyurtma jarayoni',
    rescheduleMeasure: 'Oʻlchovni koʻchirish',
    overdueUnassignHint:
      'Oʻlchov {{hours}} soatdan koʻp kechikdi va boshlanmadi — u sizdan olinib, qayta taqsimotga oʻtkazildi. Unga amallar mavjud emas.',
    chosenByClient: 'Mijoz tanlagan',
    openPayment: 'Toʻlov va arizalar',
    resultWorkTitle: 'Tayyor ish',
    resultWorkHint: 'Istasangiz, tayyor ish suratlarini qo‘shing. Mijoz ularni ko‘ra oladi.',
    measureRescheduled: 'Oʻlchov koʻchirildi',
    managerNotified: 'Menejerga xabar yuborildi',
    rescheduleFailed: 'Koʻchirib boʻlmadi',
    rescheduleTitle: 'Oʻlchovni koʻchirish',
    rescheduleHint:
      'Yangi kun tanlang. «Vaqt oynasi» — buyurtma avto-taqsimotga oʻtadi; «Aniq vaqt» — slotni biriktiradi. Menejerga sababingiz bilan xabar boradi.',
    newDateTime: 'Yangi sana va vaqt',
    newDate: 'Yangi sana',
    timeWindow: 'Vaqt oynasi',
    periodAnytime: 'Istalgan',
    periodMorning: 'Ertalab 8–13',
    periodAfternoon: 'Kunduzi 13–17',
    periodEvening: 'Kechqurun 17–22',
    helpButton: 'Maʼlumot',
    reschedModeWindow: 'Vaqt oynasi',
    reschedModeExact: 'Aniq vaqt',
    exactTimeLabel: 'Vaqt',
    assignSelf: 'Oʻzim boraman',
    assignAnyone: 'Istalganini yuborish',
    whoAssign: 'Kim boradi',
    exactHint:
      'Aniq vaqt buyurtmaga biriktiriladi. «Oʻzim boraman» — oʻlchov sizda qoladi; «Istalganini yuborish» — avto-taqsimotga oʻtadi.',
    reasonOptional: 'Sabab (ixtiyoriy)',
    reasonRequired: 'Koʻchirish sababi',
    reasonPlaceholder: 'Masalan: mijoz ertaga koʻchirishni soʻradi',
    reasonRequiredHint: 'Koʻchirish uchun sababni koʻrsating',
    shortStart: 'Boshlash',
    shortContinue: 'Davom etish',
    shortReject: 'Mos emas',
    shortProblem: 'Muammo',
    shortEdit: 'Oʻzgartirish',
    shortNoAnswer: 'Ulanmadi',
    shortPayment: 'Toʻlov',
    shortComplete: 'Yakunlash',
    saving: 'Saqlanmoqda…',
    rescheduleAndNotify: 'Koʻchirish va menejerga xabar berish',
    editAddress: 'Manzilni oʻzgartirish',
    editAddressCardHint:
      'Manzil notoʻgʻrimi? «📍 Manzilni oʻzgartirish» — matnni toʻgʻrilash. «🛰️ Geolokatsiyani oʻzgartirish» — montajchining GPS-navigatsiyasi uchun xaritadagi nuqtani koʻchirish.',
    editAddressTitle: 'Manzilni oʻzgartirish',
    editAddressHint:
      'Agar haqiqiy manzil koʻrsatilganidan farq qilsa — aniq manzilni yozing. Menejerga Telegram orqali xabar boradi.',
    addressLabel: 'Manzil',
    addressPlaceholder: 'Shahar, tuman, koʻcha, uy, xonadon',
    cityLabel: 'Shahar',
    cityPlaceholder: 'Toshkent',
    citySelectPlaceholder: '— tanlang —',
    districtSelectPlaceholder: '— tanlang —',
    districtSelectPickCity: 'Avval shahar',
    streetLabel: 'Koʻcha',
    streetPlaceholder: 'koʻcha nomi',
    houseLabel: 'Uy',
    housePlaceholder: '14',
    aptLabel: 'Xonadon',
    aptPlaceholder: '55',
    entranceLabel: 'Podʼez',
    entrancePlaceholder: '1',
    floorLabel: 'Qavat',
    floorPlaceholder: '5',
    districtLabelOptional: 'Tuman (ixt.)',
    districtPlaceholder: 'Mirobod',
    saveAddressAndNotify: 'Saqlash va menejerga xabar berish',
    addressUpdated: 'Manzil yangilandi',
    addressUpdateFailed: 'Manzilni yangilab boʻlmadi',
    editLocation: 'Geolokatsiyani oʻzgartirish',
    editLocationTitle: 'Lokatsiyani aniqlashtirish',
    editLocationHint:
      'amoCRM xaritada taxminiy nuqtani yuboradi. Mijoz eshigi oldida turib «Men joydaman» tugmasini bosing — telefon aniq GPS-koordinatalarni oladi. Menejerga Telegram orqali xabar boradi.',
    locationDragHint: 'Nuqtani qoʻlda toʻgʻrilash mumkin — 📍 ni xarita boʻylab suring.',
    locationCurrent: 'Joriy nuqta',
    locationNone: 'Koordinatalar hali belgilanmagan',
    locationOnMap: 'Xaritada ochish',
    locationGrab: '📍 Men joydaman — GPS olish',
    locationRegrab: 'Koordinatalarni qayta olish',
    locationGrabbing: 'GPS aniqlanmoqda…',
    locationCaptured: 'Koordinatalar olindi',
    locationAccuracyM: 'aniqlik ±{{m}} m',
    locationSave: 'Lokatsiyani saqlash va menejerga xabar berish',
    locationSaved: 'Lokatsiya yangilandi',
    locationSaveFailed: 'Lokatsiyani saqlab boʻlmadi',
    locationGpsDenied: 'Geolokatsiyaga ruxsat yoʻq. Telefon sozlamalarida ilovaga ruxsat bering.',
    locationGpsFailed: 'GPS olinmadi. Ochiq joyga chiqib qayta urinib koʻring.',
    locationGpsUnsupported: 'Telefon geolokatsiyani qoʻllab-quvvatlamaydi.',
    downloadFailed: 'Yuklab boʻlmadi',
    preparingPdf: 'PDF tayyorlanmoqda…',
    downloadOrderPdf: 'Ariza PDF’ini yuklab olish',
    contractorListButton: 'Pudratchi uchun roʻyxat',
    contractorListTitle: 'Pudratchi uchun roʻyxat',
    contractorListLoading: 'Roʻyxat tayyorlanmoqda…',
    contractorListError: 'Roʻyxatni yuklab boʻlmadi',
    contractorListRetry: 'Qayta urinish',
    contractorListClose: 'Yopish',
    advanceAccepted: 'Avans qabul qilindi',
    savedOffline: 'Oflayn saqlandi — tarmoq paydo boʻlganda yuboramiz',
    advanceFailed: 'Avansni qabul qilib boʻlmadi',
    paymentReceipts: 'Toʻlov cheklari',
    paymentReceiptsHint: 'Toʻlov chekini yoki skrinshotini biriktiring (karta, oʻtkazma).',
    advance: 'Avans',
    acceptAdvance: 'Avansni qabul qilish',
    acceptAdditionalPayment: 'Qoʻshimcha toʻlovni qabul qilish',
    additionalPayment: 'Qoʻshimcha toʻlov',
    advanceAmount: 'Avans summasi',
    remainder: 'Qoldiq',
    remainderDue: 'Toʻlanadigan qoldiq:',
    minPayment: 'Yuborish uchun yetishmaydi ({{pct}}%):',
    advanceType: 'Avans turi',
    payerInnPlaceholder: 'Toʻlovchi INN (9–14 raqam)',
    selectCard: '— kartani tanlang —',
    selectTerminal: '— terminalni tanlang —',
    selectAccount: '— hisobni tanlang —',
    notePlaceholder: 'Izoh (ixtiyoriy)',
    savingShort: 'Saqlanmoqda…',
    receiptDownloadFailed: 'Chekni yuklab boʻlmadi',
    downloadReceiptPdf: 'PDF chekni yuklab olish',
    dimensions: '{{width}} × {{height}}',
    perPiece: '/ dona',
    slatSize: 'planka {{m}} m',
    bortSize: 'bort {{mm}} mm',
    bahromaShort: 'bahromali',
    bahromaShortNo: 'bahromasiz',
    itemSpecToggle: 'Buyum tavsifi',
    spec_slat: 'Planka',
    spec_bort: 'Bort',
    spec_bahroma: 'Bahroma',
    spec_bahroma_yes: 'Bahromali',
    spec_bahroma_no: 'Bahromasiz',
    spec_notes: 'Izoh',
    copyItemToRoom: 'Buyumni xonaga nusxalash',
    copyToRoom: 'Xonaga nusxalash',
    roomFallback: 'xonaga',
    roomActions: 'Xona',
    addItem: 'Mahsulot qoʻshish',
    selectRoom: 'Xonani tanlash',
    roomLocked: 'Ishlab chiqarishda — tahrirlash yopiq',
    roomNameLabel: 'Xona nomi',
    roomSave: 'Saqlash',
    roomDelete: 'Xonani oʻchirish',
    roomDeleteTitle: 'Xona oʻchirilsinmi?',
    roomDeleteConfirm: '«{{name}}» xonasi va undagi barcha mahsulotlar oʻchiriladi.',
    roomRenamed: 'Xona nomi oʻzgartirildi',
    roomDeleted: 'Xona oʻchirildi',
    roomActionFailed: 'Xonani oʻzgartirib boʻlmadi',
    itemEdit: 'Mahsulotni oʻzgartirish',
    itemEditFull: 'Oʻzgartirish (tur, mato, oʻlcham…)',
    itemWidth: 'Eni, mm',
    itemHeight: 'Boʻyi, mm',
    itemQuantity: 'Soni',
    itemPriceRecalcHint: 'Narx yangi oʻlchamlar boʻyicha avtomatik qayta hisoblanadi.',
    itemDelete: 'Mahsulotni oʻchirish',
    itemDeleteTitle: 'Mahsulot oʻchirilsinmi?',
    itemDeleteConfirm: 'Mahsulot xonadan oʻchiriladi.',
    itemSaved: 'Mahsulot oʻzgartirildi',
    itemDeleted: 'Mahsulot oʻchirildi',
    itemActionFailed: 'Mahsulotni oʻzgartirib boʻlmadi',
    copied: 'Nusxalandi',
    copyFailed: 'Nusxalab boʻlmadi',
    copyItemTitle: 'Buyumni nusxalash',
    copying: 'Nusxalanmoqda…',
    copy: 'Nusxalash',
    sameParamsToRoom: '— xuddi shu parametrlar bilan, tanlangan xonaga.',
    toRoom: 'Xonaga',
    currentRoom: '(joriy)',
    howManyCopies: 'Nechta nusxa',
  },
  weekPage: {
    today: 'Bugun',
    next: 'Oldinga',
    month: 'Oy',
    week: 'Hafta',
    day: 'Kun',
    scopeMine: 'Mening',
    scopeAll: 'Hammasi',
    noTasksInline: '— vazifa yoʻq',
    noTasksTitle: 'Vazifa yoʻq',
    noTasksHint: 'Bu kunga sizda hech narsa rejalashtirilmagan.',
  },
  loginPage: {
    createPin: 'PIN yaratish',
    pinMismatch: 'PIN mos kelmadi',
    demoMode: 'Tezkor kirish',
    demoHint: 'Hisobni tanlang — Telegram kodisiz kirish (ishlab chiqish/demo uchun).',
    signIn: 'Kirish',
    roleMeasurer: 'Oʻlchovchi',
    roleInstaller: 'Oʻrnatuvchi',
    roleManager: 'Menejer',
    roleProduction: 'Sex',
    roleAdmin: 'Administrator',
    roleClientTest: 'Mijoz (test)',
    codeFromTelegram: 'Telegramdan kelgan kod',
    changeNumber: 'Raqamni oʻzgartirish',
    savePin: 'PINni saqlash',
    pinCreateLabel: 'PIN oʻylab toping — 4 raqam',
    pinRepeatLabel: 'Takrorlang',
    pinNote:
      'PIN bu qurilmada kirishni tezlashtiradi. Unutib qoʻysangiz — qaytadan Telegram kodi orqali kirasiz.',
    noAccount: 'Akkaunt yoʻqmi?',
    register: 'Roʻyxatdan oʻtish',
    version: 'versiya {{version}}',
    codeWillArrive: 'Kod Telegramga keladi',
    pinSubtitle: 'PIN kirishni tezlashtiradi — 4 raqam',
  },
  pinPage: {
    recognizing: 'Aniqlanmoqda…',
    loginFaceId: 'Face ID orqali kirish',
    biometricOnlyPrompt: 'Face ID / Touch ID orqali kirish',
    resetTitle: 'PIN tiklansinmi?',
    resetDescription:
      'Hozir Telegramingizga kod yuboramiz. Tasdiqlangach, PIN tiklanadi va keyingi kirishda yangisini belgilaysiz.',
    sendCode: 'Kod yuborish',
    enterCode: 'Kodni kiriting',
    codeSent: 'Kod Telegramga yuborildi',
    checking: 'Tekshirilmoqda…',
    resetPin: 'PINni tiklash',
  },
  registerPage: {
    titleRegister: 'Roʻyxatdan oʻtish',
    titleConfirm: 'Tasdiqlash',
    titleEnterCode: 'Kodni kiriting',
    subtitlePhone: 'Raqamingizni kiriting — Telegram orqali kod yuboramiz',
    subtitleTelegram: 'Telegramni oching, bot sizga aktivatsiya kodini yuboradi',
    subtitleCode: 'Kod Telegramga keldi. Uni quyiga kiriting.',
    yourNumber: 'Raqamingiz',
    getCode: 'Kod olish',
    step1OpenTelegram: 'Tugmani bosing — Telegram ochiladi',
    step2PressStartPrefix: 'Bot bilan chatda bosing',
    step2StartButton: '«Start»',
    step3BotSendsCode: 'Bot sizga aktivatsiya kodini yuboradi',
    step4ReturnAndSend: 'Shu yerga qaytib «Kod yuborish»ni bosing',
    openTelegram: 'Telegramni ochish',
    sending: 'Yuborilyapti…',
    confirmedSendCode: 'Tasdiqladim → kod yuborish',
    changeNumber: 'Raqamni oʻzgartirish',
    codeFromTelegram: 'Telegramdan kelgan kod (4 raqam)',
    verifying: 'Tekshirilyapti…',
    login: 'Kirish',
    alreadyRegistered: 'Allaqachon roʻyxatdan oʻtganmisiz?',
    version: 'versiya {{version}}',
  },
  dailyClosesSection: {
    title: 'Kassa smenasi',
    subtitle:
      'Qoʻldagi pul — siz qabul qilgan, hali ofisga topshirilmagan toʻlovlar. Yuqoridagi oʻrnatish tushumi bilan aralashtirmang.',
    closeShift: 'Smenani topshirish',
    noUnclosedPayments: 'Topshirilmagan toʻlovlar yoʻq',
    myShifts: 'Mening smenalarim',
    statusApproved: '✓ Tasdiqlangan',
    statusRejected: '⛔ Rad etilgan',
    statusPending: '⏳ Menejer kutilmoqda',
    paymentsCount_one: '{{count}} ta toʻlov',
    paymentsCount_few: '{{count}} ta toʻlov',
    paymentsCount_many: '{{count}} ta toʻlov',
    toastSubmittedTitle: 'Smena topshirildi',
    toastSubmittedBody: 'Menejer uni admin panelda koʻradi.',
    toastSplitSubmittedBody: 'Toʻlovlar filiallar boʻyicha alohida smenalarga ajratildi. Menejer ularni admin panelda koʻradi.',
    toastErrorTitle: 'Smenani topshirib boʻlmadi',
    previewChanged: 'Toʻlovlar roʻyxati oʻzgardi. Yangilangan summani tekshirib, smenani qayta topshiring.',
    methodCash: 'Naqd',
    methodCardP2p: 'Kartaga (p2p)',
    methodCardTerminal: 'Karta (terminal)',
    methodTransfer: 'Oʻtkazma',
    sheetTitle: 'Smenani topshirish',
    sending: 'Yuborilmoqda…',
    submitToManager: 'Menejerga topshirish',
    total: 'Jami',
    byBranch: 'Filiallar boʻyicha',
    unassignedBranch: 'Filialsiz',
    splitByBranchHint: 'Har bir filial uchun alohida smena yaratiladi. Umumiy summa oʻzgarmaydi.',
    byMethods: 'Usullar boʻyicha',
    paymentsHeading: 'Toʻlovlar ({{count}})',
    notePlaceholder: 'Menejerga izoh (ixtiyoriy)',
    confirmTitle: '{{sum}} topshirilsinmi?',
    confirmDescription:
      'Tasdiqlangach toʻlovlar shu smenaga bogʻlanadi. Menejer xulosani koʻradi va tasdiqlaydi.',
    confirmSplitDescription:
      'Turli filiallar toʻlovlari alohida smenalarga bogʻlanadi. Menejer har birini koʻrib tasdiqlaydi.',
    submit: 'Topshirish',
  },
  catalogCategoryPage: {
    appBarTitle: 'Katalog',
    notFound: 'Kategoriya topilmadi.',
    addedToQuote: 'Hisob-kitobga qoʻshildi',
    priceFromPerSqM: '{{sum}} dan / m²',
    priceFromPerItem: '{{sum}} dan / dona',
    priceByComponents: 'Komponentlar boʻyicha individual hisob-kitob',
    chooseFabric: 'Matoni tanlang',
    whichRoom: 'Qaysi xonada?',
    roomPlaceholder: 'Mehmonxona, yotoqxona, oshxona…',
    windowCount: 'Derazalar soni',
    approxSizes: 'Taxminiy oʻlchamlar (ixtiyoriy)',
    approxSizesHint:
      'Bu qadamni oʻtkazib yuborishingiz mumkin — xodimlarimiz kelganda oʻzlari oʻlchab beradi. Lekin deraza oʻlchamlarini taxminan bilsangiz, aniqroq hisoblashga yordam beradi.',
    widthPlaceholder: 'Eni, mm',
    heightPlaceholder: 'Boʻyi, mm',
    comment: 'Izoh (ixtiyoriy)',
    commentPlaceholder: 'Rang, xususiyatlar, istaklar…',
    addToQuote: 'Hisob-kitobga qoʻshish',
  },
  catalogPage: {
    title: 'Jalyuzilar katalogi',
    subtitle: 'Jalyuzi turlarini tanlang. Menejer aniq summa va muddatni hisoblab beradi.',
    draftBarAdded: 'Hisob-kitobga qoʻshildi · {{n}} {{noun}}',
    posOne: 'ta',
    posFew: 'ta',
    posMany: 'ta',
    priceFromPerSqM: '{{price}} dan / m²',
    priceFromPerItem: '{{price}} dan / dona',
    priceIndividual: 'individual',
  },
  quoteDraftPage: {
    title: 'Mening hisobim',
    emptyTitle: 'Hisob boʻsh',
    emptyHint: 'Katalogni oching va hisoblamoqchi boʻlgan jalyuzilarni qoʻshing.',
    toCatalog: 'Katalogga',
    itemsCount_one: '{{count}} ta pozitsiya.',
    itemsCount_other: '{{count}} ta pozitsiya.',
    managerWillCalc: 'Menejer soʻrov kelgach aniq summa va oʻrnatish muddatini hisoblab beradi.',
    addressLabel: 'Oʻrnatish manzili',
    addressHint: 'Oʻlchovchini qayerga yuborish kerak',
    addressPlaceholder: 'Toshkent, Chilonzor, Bunyodkor koʻchasi 14, 47-xonadon',
    districtPlaceholder: 'Tuman (ixtiyoriy)',
    noteLabel: 'Menejerga izoh',
    noteHint: 'Byudjet, muddatlar, alohida istaklar',
    notePlaceholder: 'Yangi yilga ulgurmoqchiman, byudjet ~3 mln soʻm…',
    submitting: 'Yuborilmoqda…',
    submit: 'Menejerga yuborish',
    remove: 'Olib tashlash',
    toastTitle: 'Soʻrov {{action}}',
    toastUpdated: 'yangilandi',
    toastSent: 'yuborildi',
    toastBody: 'Kod: {{code}}. Menejer yaqin orada bogʻlanadi.',
    toastErrTitle: 'Yuborib boʻlmadi',
  },
  dealerCalcPage: {
    title: 'Yangi buyurtma',
    eyebrow: 'DILER KALKULYATORI',
    orderCreated: 'Buyurtma yaratildi',
    orderCreatedHint: 'Qayta ishlashga yuborildi',
    submitFailed: 'Rasmiylashtirib boʻlmadi',
    profileNotFound: 'Profil topilmadi',
    cartEmpty: 'Savat boʻsh',
    cartEmptyHint:
      'Birinchi pozitsiyani qoʻshing — jalyuzi turini, oʻlchamlarini va materiallarni tanlang.',
    notesLabel: 'Buyurtmaga izoh (ixtiyoriy)',
    notesPlaceholder: 'Istaklar, spetsifikatsiya, yetkazib berish usuli…',
    addItem: 'Pozitsiya qoʻshish',
    totalItems: 'Jami {{count}} ta pozitsiya',
    submitting: 'Rasmiylashtirilmoqda…',
    submit: 'Buyurtmani rasmiylashtirish',
    cancelOrderTitle: 'Buyurtma bekor qilinsinmi?',
    cancelOrderDesc: 'Barcha qoʻshilgan pozitsiyalar yoʻqoladi.',
    cancelOrderConfirm: 'Bekor qilish',
    discountLabel: 'Chakana narxdan chegirmangiz:',
    discountNote: 'Ayrim pozitsiyalar uchun individual narx belgilangan boʻlishi mumkin.',
    mm: 'mm',
    pcs: 'dona',
    fabricLabel: 'Mato',
    mechanismLabel: 'Mexanizm',
    addToCart: 'Savatga qoʻshish',
    withDiscount: '({{pct}}% chegirma hisobga olingan)',
    pbVidHint:
      'Bu tur Narxnoma boʻyicha hisoblanadi. Bu yerda komponentlar boʻyicha taxminiy narx koʻrsatilgan — aniq narx buyurtma rasmiylashtirilganda hisoblanadi.',
    rowType: 'Jalyuzi turi',
    rowSize: 'Oʻlchamlari',
    rowQuantity: 'Miqdori',
    rowFabric: 'Mato',
    rowRibbon: 'Lenta',
    rowMechanism: 'Mexanizm',
    rowCornice: 'Karniz',
    itemTotal: 'Pozitsiya boʻyicha jami',
  },
  dealerHomePage: {
    title: 'Diler kabineti',
    subtitle:
      'Buyurtmalaringiz va balans. Buyurtmani oʻzingiz yoki menejer orqali rasmiylashtirishingiz mumkin.',
    profileNotFound: 'Profil topilmadi',
    newOrder: '+ Yangi buyurtma',
    orders: 'Buyurtmalar',
    noOrders: 'Hozircha buyurtmalar yoʻq',
    noOrdersHint: '«+ Yangi buyurtma» tugmasini bosing — pozitsiyalarni tanlab, rasmiylashtiring.',
    yourDebt: 'Qarzingiz',
    overpayment: 'Ortiqcha toʻlov',
    balance: 'Balans',
    nameDiscount: '{{name}} · chegirma {{pct}}%',
    creditExceeded: '⚠ Kredit limiti oshib ketdi',
    itemsCount: '{{count}} ta pozitsiya',
    debtLabel: 'qarz',
    reconTitle: 'Solishtirma dalolatnoma',
    reconHint: 'Davrni tanlang va oʻzaro hisob-kitob PDF faylini yuklab oling.',
    reconFrom: 'Dan',
    reconTo: 'Gacha',
    reconDownload: 'Dalolatnomani yuklab olish',
    reconPreparing: 'PDF tayyorlanmoqda…',
    reconError: 'Dalolatnomani yuklab boʻlmadi',
    statusDraft: '📝 Qoralama',
    statusOrdered: '✓ Rasmiylashtirildi',
    statusInProduction: '⚙️ Ishlab chiqarishda',
    statusReady: '✅ Tayyor',
    statusShipped: '🚚 Joʻnatildi',
    statusAccepted: '✓ Qabul qilindi',
    statusRefused: '⛔ Bekor qilindi',
  },
  notificationDetailSheet: {
    orderLabel: 'Buyurtma',
    loadOrderError: 'Buyurtmani yuklab boʻlmadi. {{message}}',
    openOrder: 'Buyurtmani ochish',
    quickJumpTitle: 'Buyurtmaga tezkor oʻtish',
    openShort: 'Ochish',
    typeNewMeasureAssigned: 'Oʻlchov tayinlandi',
    typeOrderReadyForInstall: 'Buyurtma oʻrnatishga tayyor',
    typeProductionCompleted: 'Ishlab chiqarish yakunlandi',
    typePaymentReceived: 'Toʻlov qabul qilindi',
    typeClientRescheduled: 'Mijoz vaqtni koʻchirdi',
    typeOrderRefused: 'Mijoz rad etdi',
    typeGeneric: 'Bildirishnoma',
  },
  orderCard: {
    callAttempts: 'Qoʻngʻiroq urinishlari: {{count}}',
    flagsCount: 'Belgilar: {{count}}',
  },
  orderProgressTimeline: {
    stepNew: 'Ariza',
    stepMeasure: 'Oʻlchov',
    stepOrdered: 'Rasmiylashtirildi',
    stepProduction: 'Ishlab chiqarish',
    stepInstall: 'Oʻrnatish',
    stepCompleted: 'Tayyor',
    badgeRefused: '✕ Rad etildi',
    badgeReturned: '↩ Qaytarildi',
  },
  personalDataSheet: {
    title: 'Shaxsiy maʼlumotlar',
    hint: 'Faqat ismni oʻzgartirish mumkin. Telefonni administrator oʻzgartiradi.',
    nameLabel: 'Ism',
    namePlaceholder: 'Ivan Ivanov',
    phoneLabel: 'Telefon',
    roleLabel: 'Rol',
    saving: 'Saqlanmoqda…',
    nameUpdated: 'Ism yangilandi',
    saveFailed: 'Saqlab boʻlmadi',
  },
  myCards: {
    title: 'Mening kartalarim',
    hint: 'Oʻzingiz toʻlov qabul qiladigan kartalar. Oʻz kartangizga qabul qilsangiz — kassangizga tushadi, naqd bilan birga topshirasiz. Faqat sizga koʻrinadi.',
    empty:
      'Hozircha karta yoʻq. Oʻzingiznikini qoʻshing — «kartaga oʻtkazma»ni unga qabul qila olasiz.',
    labelPlaceholder: 'Nomi (masalan, Mening Uzcard)',
    numberPlaceholder: 'Karta raqami (oxirgi 4 ta boʻlsa ham)',
    add: 'Karta qoʻshish',
    remove: 'Oʻchirish',
    saving: 'Saqlanmoqda…',
    saved: 'Kartalar saqlandi',
    saveFailed: 'Saqlab boʻlmadi',
  },
  profileSheet: {
    pinSet: 'PIN oʻrnatilgan — oʻzgartirish mumkin',
    pinNotSet: 'PIN oʻchirilgan — Telegram-kod orqali kirish',
    pinOffBiometric: 'Oʻchirilgan — Face ID / Touch ID orqali kirish',
    faceIdLastTitle: 'Bu oxirgi kirish usuli',
    faceIdLastHint: 'Avval PIN belgilang, keyin qurilmani oʻchiring',
    deviceDeleteFailed: 'Qurilmani oʻchirib boʻlmadi',
    logoutDescBefore: 'Sessiya shu qurilmada yopiladi. Keyingi kirishda PIN emas,',
    logoutDescCode: 'Telegram-kod',
    logoutDescAfter: ' kerak boʻladi.',
    logoutConfirm: 'Chiqish',
    tileMore: '{{label}}: batafsil',
    faceIdConnected: 'Face ID ulandi',
    devicesCount: 'Qurilmalar: {{count}}',
    faceIdConnectFailed: 'Face ID ulab boʻlmadi',
    faceIdRegistering: 'Roʻyxatdan oʻtkazilmoqda…',
    faceIdConnectedCount: 'Ulangan · qurilmalar: {{count}}',
    faceIdHint: 'PIN kiritmasdan tez kirish',
    deviceDeleted: 'Qurilma oʻchirildi',
    biometricListHint:
      'Bu qurilmalar ilovani biometriya orqali ochishi mumkin. PIN zaxira usul sifatida qoladi.',
    deviceFallback: 'Qurilma',
    deviceAdded: 'Qoʻshilgan {{date}}',
    deviceLastUsed: ' · ishlatilgan {{date}}',
    addDevice: '+ Yana qurilma qoʻshish',
    todayWorkUntil: 'Bugun {{time}} gacha ishlayman',
    todayFullDay: 'Bugun — toʻliq kun',
    saveFailed: 'Saqlab boʻlmadi',
    alwaysUntil: 'Doimiy {{time}} gacha',
    shiftReset: 'Doimiy smena bekor qilindi',
    myAvailability: 'Mening bandligim',
    todayWorkUntilLabel: 'Bugun ishlayman — gacha',
    todayWorkUntilDesc: 'Faqat bugunga. Boʻsh = toʻliq kun.',
    everyDayUntilLabel: 'Har kuni — gacha',
    everyDayUntilDesc: 'Doimiy smena. Boʻsh = toʻliq kun.',
    homeSaved: 'Uy saqlandi',
    homeSavedHint: 'Kunlik marshrut uyga yaqinroq tugaydi',
    myHome: '🏠 Mening uyim',
    myHomeDesc: 'Kunlik marshrutning boshi va oxiri — soʻnggi oʻlchov uyga yaqinroq qoʻyiladi.',
    homeAddressPlaceholder: 'Uy manzili',
    latPlaceholder: 'kenglik (41.31)',
    lngPlaceholder: 'uzunlik (69.24)',
    useGps: 'GPS dan olish',
    locating: 'Aniqlanmoqda…',
    gpsFilled: 'GPS koordinatalari qoʻyildi',
    gpsFilledHint: 'Tekshiring va «Uyni saqlash» ni bosing',
    gpsUnsupported: 'Bu qurilmada GPS mavjud emas',
    gpsDenied: 'Geolokatsiyaga ruxsat berilmagan',
    gpsFailed: 'Koordinatalarni aniqlab boʻlmadi',
    saving: 'Saqlanmoqda…',
    saveHome: 'Uyni saqlash',
    emptyFileError: 'Boʻsh fayl — kesish ishlamadi',
    uploadFailed: 'Yuklab boʻlmadi ({{status}})',
  },
  reserveCard: {
    title: 'Zaxira',
    perMonth: 'oy uchun',
    debtHint: 'Jarimalar boʻyicha qarz — keyingi sotuvlardagi ustama bilan qoplanadi.',
    zeroHint: 'Balans nolda. Bitimlarni ustama bilan yoping — zaxira oʻsib boradi.',
    sheetTitle: 'Zaxira · {{period}}',
    tileBalance: 'Balans',
    tileAccrued: 'Oy uchun hisoblangan',
    tilePenalty: 'Oy uchun jarimalar',
    historyCount: 'Tarix ({{count}})',
  },
  syncBadge: {
    measurements: 'Yuborilmagan o‘lchovlar',
    measurementsHint: 'Kiritilgan ma’lumot qurilmada saqlangan. Buyurtmani ochib, o‘zgarishlarni kerakli oynalarga kiriting; dastlabki ma’lumotni yuklab olish mumkin.',
    noMeasurements: 'Yuborilmagan o‘lchovlar yo‘q. Boshqa xatolar boshqa amallar yoki rasmlarga tegishli.',
    saveDraft: 'Saqlangan ma’lumotni yuklab olish',
    openOrder: 'Buyurtmani ochish',
    meters: 'm',
    syncErrors: '{{count}} ta sinxronlash xatosi',
    inQueue: 'Navbatda {{count}} ta',
    online: 'Onlayn',
    offline: 'Oflayn',
  },
  themeToggle: {
    ariaLabel: 'Joriy: {{current}}. Almashtirish: {{next}}',
    switchTo: 'Almashtirish: {{next}}',
  },
  cashboxPage: {
    title: 'Kassa',
    loadFailed: 'Yuklab boʻlmadi',
    periodDay: 'Kun',
    periodMonth: 'Oy',
    periodYear: 'Yil',
    todayLabel: 'Bugun',
    installsCount: '{{n}} ta oʻrnatish',
    toReceive: 'Olinishi kerak',
    totalForInstalls: 'oʻrnatishlar boʻyicha jami',
    received: 'Olingan',
    completedCount: '{{n}} ta yakunlandi',
    remainder: 'Qoldiq',
    stillAhead: 'hali olinadi',
    ordersLabel: 'Buyurtmalar',
    closedToday: 'bugun yopildi',
    closedMonth: 'oy davomida yopildi',
    closedYear: 'yil davomida yopildi',
  },
  clientTrackerPage: {
    errInvalidLink: 'Havola notoʻgʻri',
    errLinkExpired: 'Havola muddati tugadi. Menejer bilan bogʻlaning.',
    errLinkRevoked: 'Havola yaroqsiz yoki bekor qilingan.',
    errLoadFailed: 'Buyurtmani yuklab boʻlmadi. Keyinroq urinib koʻring.',
    errNoServer: 'Server bilan aloqa yoʻq.',
    refresh: 'Yangilash',
    refreshing: 'Yangilanmoqda…',
    updatedAt: '{{time}} da yangilandi',
    staleData: 'Holatni yangilab boʻlmadi. Oxirgi olingan maʼlumotlar koʻrsatilmoqda. Qayta yangilab koʻring.',
    stages: 'Buyurtma bosqichlari',
    history: 'Buyurtma tarixi',
    currentStage: 'Joriy bosqich',
    paused: 'Vaqtincha toʻxtatilgan',
    measureDate: 'Oʻlchov tayinlandi:',
    installDate: 'Oʻrnatish tayinlandi:',
    agreedInstall: 'Kelishilgan oʻrnatish sanasi:',
    installedCount: 'Oʻrnatilgan mahsulotlar: {{installed}} / {{total}}',
    subtitle: 'BUYURTMA TRAKERI',
    loading: 'Yuklanmoqda…',
    autoUpdate: 'Sahifa ochiq turganda holat har 30 soniyada yangilanadi.',
    questionsHint: 'Savollaringiz bormi? Yuqoridagi raqam orqali menejerga qoʻngʻiroq qiling.',
    orderCode: 'Buyurtma {{code}}',
    expectedInstall: 'Istalgan oʻrnatish sanasi (aniqlashtiriladi):',
    address: 'Manzil',
    downloadPdf: 'Ariza PDF-ni yuklab olish',
    downloadPdfHint: 'Buyurtma tarkibi va toʻlov summasi',
    finishedWork: 'Tayyor ish',
    yourManager: 'Menejeringiz',
    reviewTitle: 'Ishimizni baholang',
    reviewCommentPh: 'Izoh (ixtiyoriy)',
    reviewSubmit: 'Sharh yuborish',
    reviewThanks: 'Bahoyingiz uchun rahmat!',
    reviewError: 'Yuborib boʻlmadi. Keyinroq urinib koʻring.',
    tgSubTitle: 'Buyurtmangiz Telegramda',
    tgSubHint:
      'Botda buyurtma kartasini oching. Joriy bosqichni koʻrish uchun «Обновить» tugmasini bosing.',
    tgSubBtn: 'Telegramda ochish',
  },
  dispatchPage: {
    title: 'Dispetcher',
    subtitle: 'Buyurtmalarni ijrochilarga taqsimlash',
    autopilotOn:
      'Avtopilot yoqilgan — oʻzi taqsimlaydi. Quyida — qoʻlda biriktirish kerak boʻlganlar.',
    autopilotOff: 'Avtopilot oʻchirilgan — qoʻlda taqsimlang.',
    resyncBtn: 'Avtopilotni hozir ishga tushirish',
    queueAttention: 'Eʼtibor talab qiladi',
    emptyAutopilot: 'Hammasi avtopilot nazoratida',
    emptyAutopilotHint: 'Avtopilot buyurtmalarni oʻzi taqsimlaydi. Qoʻlda istisnolar yoʻq.',
    tabMeasure: 'Oʻlchovlar',
    tabInstall: 'Montajlar',
    autoBtn: 'Kunni avto-taqsimlash',
    autoBusy: 'Taqsimlanmoqda…',
    autoDone: 'Kun taqsimlandi',
    autoFailed: 'Taqsimlab boʻlmadi',
    autoPartialTitle: 'Hammasi taqsimlanmadi',
    autoPartialMeasure: 'Navbatda yana {{count}} — hozircha boʻsh oʻlchovchilar yoʻq',
    autoPartialInstall: 'Navbatda yana {{count}} — hozircha boʻsh montajchilar yoʻq',
    autoPartialOverdue:
      'Navbatda {{count}} — mijoz oynasi oʻtdi. Avtopilot ularni tayinlamaydi: mijozga qoʻngʻiroq qilib, yangi sana belgilang («Tayinlash»).',
    autoPartialMixed:
      'Navbatda yana {{blocked}} muddati oʻtgan (yangi sana kerak) + {{waiting}} boʻsh oʻlchovchini kutmoqda.',
    overdueBadge: 'Oyna oʻtdi — yangi sana belgilang',
    queueTitle: 'Taqsimlash uchun',
    noDate: 'sanasiz',
    assignCta: 'Biriktirish',
    errorTitle: 'Yuklab boʻlmadi',
    emptyTitle: 'Navbat boʻsh',
    emptyHint: 'Taqsimlanmagan buyurtmalar yoʻq.',
    assignTitle: 'Ijrochini biriktirish',
    whenLabel: 'Tashrif sanasi va vaqti',
    dayLabel: 'Tashrif sanasi',
    windowLabel: 'Vaqt oynasi',
    window_MORNING: 'Ertalab',
    window_AFTERNOON: 'Kunduzi',
    window_EVENING: 'Kechqurun',
    window_ANYTIME: 'Istalgan',
    exactTimeLabel: 'Aniq vaqt',
    modeAuto: 'Avtopilot',
    modeManual: 'Qoʻlda',
    modeLockedAuto:
      'Avtopilot admin panelida yoqilgan — siz sana va vaqtni belgilaysiz, ijrochini oʻzi tanlaydi. Qoʻlda rejim oʻchirilgan.',
    modeLockedManual: 'Avtopilot admin panelida oʻchirilgan — ijrochini qoʻlda tayinlang.',
    autopilotHint:
      'Ijrochini avtopilot kun rejasini tuzishda tanlaydi — marshrut va yukni hisobga olib.',
    measurersTitle: 'Oʻlchovchilar',
    installersTitle: 'Montajchilar',
    futureDistanceNote:
      'Oʻlchov bugunga emas — kim yaqinroq boʻlishi oldindan nomaʼlum (oʻsha kun marshrutiga bogʻliq). Tajriba/yuk boʻyicha tanlang yoki avtopilotga ishoning.',
    handToAutopilot: 'Avtopilotga berish — oʻlchovchini tanlaydi',
    handToAutopilotInstall: 'Avtopilotga berish — montajchini tanlaydi',
    orManual: 'yoki qoʻlda tayinlash',
    autopilotTakeoverTitle: 'Avtopilotga berildi',
    autopilotTakeoverDesc:
      'Sana yangilandi — avtopilot ijrochini tanlaydi. «Avtopilotni ishga tushirish»ni bosishingiz mumkin.',
    suggestTitle: 'Mos ijrochilar',
    suggestManualTitle: 'Yoki oʻlchovchini qoʻlda tanlang',
    suggesting: 'Yaqinlik va yuk boʻyicha tanlanmoqda…',
    noExecutors: 'Mavjud ijrochilar yoʻq',
    assigned: 'Biriktirildi: {{name}}',
    assignFailed: 'Biriktirib boʻlmadi',
    recommended: 'Eng yaxshi',
    blockedNoTravel: 'Yetib bormaydi',
    blockedBusy: 'Band',
    kmEta: '{{km}} km · {{eta}} daq',
    noGps: 'GPSsiz',
  },
  installationWorkspace: {
    scheduleTitle: 'Montajlarni boshqarish',
    scheduleSubtitle: 'Navbat, avtopilot va montajchilarni qoʻlda tayinlash',
    defectsTitle: 'Ombor va brak',
    defectsSubtitle: 'OTK izohi, brak sababi va keyingi yoʻnalishni tanlash',
    fastDecisionSubtitle:
      'Avval qayta ishlash yoki almashtirishni tez boshlang. Pul masalasi alohida ko‘riladi.',
    active: 'ishda',
    closed: 'yopilgan',
    loadFailed: 'Ishlarni yuklab boʻlmadi',
    noDefects: 'Faol brak ishlari yoʻq',
    noClosedDefects: 'Yopilgan ishlar hozircha yoʻq',
    priceBasis: 'Mahsulot narxi',
    client: 'Mijoz',
    reportedReason: 'Nima sodir boʻldi',
    warehouse: 'Ombor va harakat',
    resolutionLabel: 'Oʻrnatish rahbari qarori',
    causeLabel: 'Brak sababi',
    qcCauseLocked: 'OTK ko‘rsatgan · o‘zgartirib bo‘lmaydi',
    qcResponsibleMissing:
      'Javobgar manbasi topilmadi. Yo‘nalishni saqlash mumkin, ammo ma’lumot tuzatilmaguncha moliyaviy taqsimot bloklanadi.',
    driverRole: 'Haydovchi',
    contractorRole: 'Pudratchi',
    installerComment: 'Oʻrnatuvchi izohi',
    initialQcComment: 'OTKning dastlabki izohi',
    reporterComment: 'Dastlabki izoh',
    qcComment: 'OTK izohi',
    approved: 'Tasdiqlangan',
    correct: 'Qarorni tuzatish',
    repeatVisit: 'Takroriy chiqish uchun qoʻshimcha haq',
    correctTitle: 'Aybdorlarni tuzatish',
    classifyTitle: 'Aybdorlarni belgilash',
    saving: 'Saqlanmoqda…',
    saveDecision: 'Qarorni saqlash',
    resolutionTitle: 'Mahsulot bilan nima qilinadi',
    resolutionHint:
      'OTK izohini oʻqing, brak sababini belgilang va mahsulotning keyingi yoʻlini tanlang.',
    confirmResolution: 'Qarorni tasdiqlash',
    resolutionSaved: 'Qaror ishga tushdi',
    supplier: 'Yetkazib beruvchi',
    dueAt: 'Rejalashtirilgan tayyorlik sanasi',
    driver: 'Qaytarish haydovchisi',
    selectDriver: 'Haydovchini tanlang',
    driverAssignedAutomatically:
      'Haydovchi sexga biriktirilgan sozlama bo‘yicha avtomatik tayinlanadi.',
    retryAssignedDriver: 'Tayinlashni takrorlash',
    preliminaryResponsible: 'Dastlabki javobgar',
    noResponsibleYet: 'Aybdor hozircha ko‘rsatilmagan',
    noResponsibleAllowed:
      'Aybdorni ko‘rsatmasdan davom etish va uni keyingi tahlilda aniqlash mumkin.',
    selectResponsible: 'Kamida bitta taxminiy javobgarni tanlang.',
    addResponsible: 'Javobgar qo‘shish',
    removeResponsible: 'Javobgarni olib tashlash',
    continueWithoutResponsible: 'Aybdorsiz davom etish',
    otherCauseNote: 'Boshqa sababni yozing',
    qcPassedBefore: 'Mahsulot avval OTK tomonidan qabul qilingan',
    qcPassedPossibleEscape:
      'Mahsulot avval OTK tomonidan qabul qilingan — tahlilda nazoratdan o‘tib ketganini hisobga oling',
    moneyMovedToReview:
      'Jarimalar, mahsulot qiymatini taqsimlash va takroriy chiqish haqi «Tahlil va hisob-kitoblar» bo‘limida alohida rasmiylashtiriladi.',
    manufacturingNoCharge:
      '“{{contractor}}” o‘zining brak mahsulotini qaytarib oladi va uni bepul qayta ishlaydi yoki almashtiradi. Aybdor, ushlab qolish, jarima va moliyaviy taqsimot yaratilmaydi.',
    whatToDo: 'Mahsulot bilan nima qilish kerak',
    newSupplier: 'Yangi pudratchi',
    selectSupplier: 'Pudratchini tanlang',
    ownWorkshop: 'O‘z seximiz',
    routeChoice: {
      REWORK: {
        title: 'Joriy mahsulotni avvalgi ishlab chiqaruvchida tuzatish',
        hint: 'Brak mahsulot hisobdan chiqarilmasdan avvalgi ishlab chiqaruvchiga tuzatish uchun qaytariladi.',
      },
      NEW_SAME: {
        title: 'Avvalgi ishlab chiqaruvchida yangi mahsulot tayyorlash',
        hint: 'Brak avvalgi ishlab chiqaruvchiga qaytariladi va uning o‘rniga yangi mahsulot tayyorlanadi.',
      },
      NEW_SAME_OWN: {
        title: 'Brakni hisobdan chiqarib, avvalgi sexda yangi mahsulot tayyorlash',
        hint: 'O‘z seximizdagi brak hisobdan chiqariladi va shu sex yangi mahsulot tayyorlaydi.',
      },
      NEW_SAME_EXTERNAL: {
        title: 'Brakni qaytarib, avvalgi ishlab chiqaruvchida yangi mahsulot tayyorlash',
        hint: 'Haydovchi brakni avvalgi pudratchiga qaytaradi va u yangi mahsulot tayyorlaydi.',
      },
      NEW_OTHER: {
        title: 'Boshqa ishlab chiqaruvchida qayta tayyorlash',
        hint: 'Boshqa pudratchi yoki o‘z sexingizni tanlang; yangi topshirish shu partiyada qoladi.',
      },
      NEW_OTHER_OWN: {
        title: 'Brakni hisobdan chiqarib, boshqa ishlab chiqaruvchida yangidan tayyorlash',
        hint: 'O‘z seximizdagi brak hisobdan chiqariladi, tanlangan mahsulotni boshqa sex yangidan tayyorlaydi.',
      },
      NEW_OTHER_EXTERNAL: {
        title: 'Brakni qaytarib, boshqa ishlab chiqaruvchida yangidan tayyorlash',
        hint: 'Brak avvalgi pudratchiga qaytariladi, tanlangan mahsulotni boshqa sex yangidan tayyorlaydi.',
      },
    },
    managerResolution: {
      REWORK_SAME_CONTRACTOR: {
        title: 'Hozirgisini qayta ishlash',
        hint: 'Eski mahsulot oʻsha ishlab chiqarishga qaytariladi. Hisobdan chiqarilmaydi.',
      },
      NEW_ORDER: {
        title: 'Yangi ishlab chiqarish',
        hint: 'Alohida buyurtma yaratilmaydi: shu partiya tanlangan ishlab chiqaruvchida qaytadan boshlanadi.',
      },
    },
    installer: 'Montajchi',
    selectEmployee: 'Xodimni tanlang',
    amountSum: 'Qoʻshimcha haq, soʻm',
    note: 'Izoh',
    clientAssessment: 'Mijoz rad javobini baholash',
    regulationFollowed: 'Reglament bajarilgan — kompaniya javobgar',
    clientMisinformed: 'Mijozga notoʻgʻri tushuntirilgan — oʻlchovchi javobgar',
    priceBasisSum: 'Mahsulot narxi, soʻm',
    noResponsibleReason: 'Nega aybdor yoʻq',
    responsible: 'Asosiy aybdor',
    company: 'Kompaniya',
    qcEscape: 'OTK nuqsonni oʻtkazib yubordi (ixtiyoriy)',
    noQcEscape: 'OTK uchun qoʻshimcha javobgarlik yoʻq',
    qcFineSum: 'OTK jarimasi, soʻm',
    classified: 'Aybdorlar saqlandi',
    corrected: 'Qaror tuzatildi',
    repeatDone: 'Takroriy chiqish haqi hisoblandi',
    rollback: 'Oxirgi bosqichni bekor qilish',
    rollbackTitle: 'Oxirgi bosqichni bekor qilish',
    rollbackHint:
      'Tizim faqat oxirgi oʻtkazilgan bosqichni bekor qiladi va buni ish tarixida saqlaydi.',
    rollbackReason: 'Bekor qilish sababi',
    rollbackDone: 'Oxirgi bosqich bekor qilindi',
    actionFailed: 'Amalni bajarib boʻlmadi',
    qualityTitle: 'OTK nazorati',
    qualitySubtitle: 'Tekshiruv, brakni qaytarish va montajga tayyor mahsulotlar',
    checking: 'Tekshiruvda',
    qcDefect: 'Brak',
    passed: 'Qabul qilindi',
    noQualityItems: 'Bu navbatda hech narsa yoʻq',
    waitingDriver: 'Qaytarish uchun haydovchi kerak',
    returnInProgress: 'Qaytarish: {{status}}',
    pickDriver: 'Haydovchini tanlang',
    returnStatus: {
      WAITING_DRIVER: 'haydovchini kutmoqda',
      RETURNING: 'yoʻlda',
      RETURNED: 'qaytarildi',
    },
    assignDriver: 'Haydovchini tayinlash',
    driverAssigned: 'Haydovchi tayinlandi',
    step: {
      WAIT_WAREHOUSE: 'Omborni kutmoqda',
      WAIT_QC: 'OTK izohini kutmoqda',
      DECIDE: 'Qarorni tanlash kerak',
      TRACKING: 'Qaror bajarilmoqda',
      DONE: 'Tayyor',
    },
    action: { DECIDE: 'Qayta ishlash yoki yangisini yaratish' },
    confirm: {
      APPROVE: 'Moliyaviy qarorni tasdiqlaysizmi?',
      EXECUTE: 'Qayta ishlash yoki almashtirishni boshlaysizmi?',
      CLOSE: 'Brak ishini yopasizmi?',
    },
    done: { approve: 'Qaror tasdiqlandi', execute: 'Qaror ishga tushdi', close: 'Ish yopildi' },
  },
  warehouseWorkspace: {
    finishedTitle: 'Tayyor mahsulot',
    finishedSubtitle: 'Masʼul omborlar bo‘yicha tayyor mahsulot reyestri',
    autoReceiptHint:
      'O‘z sexi tayyorligini tasdiqlagach yoki OTK maʼqullagach mahsulot avtomatik kirim qilinadi.',
    autoPosted: 'Avtomatik kirim qilindi',
    pending: 'Qabul qilish',
    received: 'Qabul qilindi',
    order: 'BUYURTMA',
    ownWorkshopReady: 'Oʻz seximiz · haydovchi va OTKsiz',
    qcPassed: 'OTKdan oʻtdi',
    accept: 'Qabul qilish',
    cancelAct: 'Dalolatnomani bekor qilish',
    targetWarehouse: 'Ombor: {{name}}',
    warehouseNotConfigured:
      'Bu ishlab chiqaruvchi va filial uchun ombor sozlanmagan. Administratorga murojaat qiling.',
    warehouseConflict:
      'Ishlab chiqaruvchilar uchun turli omborlar ko‘rsatilgan. Administrator bitta omborni tanlashi kerak.',
    noPending: 'Barcha tayyor mahsulot qabul qilindi',
    noReceived: 'Hali oʻtkazilgan dalolatnoma yoʻq',
    loadFailed: 'Navbatni yuklab boʻlmadi',
    receiptDone: 'Tayyor mahsulot qabul qilindi',
    receiptCancelled: 'Qabul dalolatnomasi bekor qilindi',
    cancelTitle: 'Qabul dalolatnomasi bekor qilinsinmi?',
    cancelHint:
      '{{code}} qabul navbatiga qaytadi. Montaj tayinlangandan keyin bekor qilib boʻlmaydi.',
    saving: 'Bajarilmoqda…',
    actionFailed: 'Amalni bajarib boʻlmadi',
    defectsTitle: 'Brak ombori',
    defectsSubtitle: 'Haqiqiy qabul, mahsulot marshruti va hisobdan chiqarish',
    noDefects: 'Brak omborida faol ish yoʻq',
    handedOverBy: 'Mahsulotni kim topshirdi',
    handedOverAt: 'Haqiqiy topshirish vaqti',
    selectEmployee: 'Xodimni tanlang',
    storageType: 'Qabul joyi',
    defectPoint: 'Brak punkti',
    defectWarehouse: 'Brak ombori',
    rack: 'Stellaj',
    cell: 'Katak',
    resolution: 'Mahsulotni qayerga yuborish',
    targetContractor: 'Yangi pudratchi',
    selectContractor: 'Pudratchini tanlang',
    returnDriver: 'Qaytarish haydovchisi',
    selectDriver: 'Haydovchini tanlang',
    dueAt: 'Qayta ishlash muddati',
    writeOffReason: 'Hisobdan chiqarish sababi',
    note: 'Izoh',
    receivedAt: 'Omborga qabul qilindi',
    rollbackShort: 'Tuzatish',
    rollback: 'Bosqichni bekor qilish',
    rollbackTitle: 'Ombor bosqichini bekor qilish',
    rollbackHint:
      'Faqat ushbu ishning sizning ombor konturingizda bajarilgan oxirgi bosqichi bekor qilinadi.',
    rollbackReason: 'Bekor qilish sababi',
    rollbackDone: 'Oxirgi ombor bosqichi bekor qilindi',
    step: {
      RECEIVE: 'Haqiqiy qabulni kutmoqda',
      DISPOSITION: 'Marshrutni belgilash kerak',
      WRITE_OFF: 'Hisobdan chiqarishni kutmoqda',
      DONE: 'Ombor bosqichi tugadi',
    },
    action: {
      RECEIVE: 'Qabul qilish',
      DISPOSITION: 'Marshrutni belgilash',
      WRITE_OFF: 'Hisobdan chiqarish',
      DONE: 'Tayyor',
    },
    done: {
      RECEIVE: 'Mahsulot brak omboriga qabul qilindi',
      DISPOSITION: 'Mahsulot marshruti saqlandi',
      WRITE_OFF: 'Hisobdan chiqarildi',
    },
  },
  defectWorkspace: {
    cause: {
      MEASUREMENT_ERROR: 'Oʻlchov xatosi',
      MANUFACTURING_ERROR: 'Ishlab chiqarish xatosi',
      INSTALLER_DAMAGE: 'Montajchi shikastladi',
      DRIVER_DAMAGE: 'Haydovchi shikastladi',
      CLIENT_REJECTION: 'Mijoz rad etdi',
      OTHER: 'Boshqa sabab',
    },
    resolution: {
      REWORK_SAME_CONTRACTOR: 'Oʻsha pudratchida qayta ishlash',
      REWORK_OTHER_CONTRACTOR: 'Boshqa pudratchida qayta ishlash',
      NEW_ORDER: 'Yangi ishlab chiqarish',
      WRITE_OFF: 'Hisobdan chiqarish',
      HOLD: 'Saqlashda qoldirish',
      NO_ACTION: 'Keyingi harakatsiz',
    },
  },
  driverCommon: {
    pickupConfirmed: 'Olib ketish tasdiqlandi',
    pickupRejected: 'Rad javobi menejerga yuborildi',
    returnCompleted: 'Qaytarish tasdiqlandi',
    ttn: 'TTN',
    ttnFailed: 'TTN ochilmadi',
    itemsN: '{{count}} dona',
    searchPlaceholder: 'Qidirish: buyurtma raqami yoki mijoz',
    notFoundTitle: 'Hech narsa topilmadi',
    notFoundHint: 'Buyurtma raqami yoki mijoz ismini tekshiring.',
  },
  driverPickup: {
    title: 'Sexdan olib ketish',
    subtitle: 'Tayinlangan olib ketishni tasdiqlang yoki rad eting',
    emptyTitle: 'Olib ketadigan narsa yoʻq',
    emptyHint:
      'Menejer tayinlagach, soʻrov bu yerda «Oldim» va «Olmayman» tugmalari bilan chiqadi.',
    statusFilterLabel: 'Olib ketish holati bo‘yicha filtr',
    statusFilters: {
      PENDING: 'Kutilmoqda',
      TAKEN: 'Olingan',
      REJECTED: 'Rad etilgan',
      ALL: 'Barchasi',
    },
    statusEmptyHints: {
      PENDING: 'Qaroringizni kutayotgan so‘rovlar yo‘q.',
      TAKEN: 'Tarixda hali qabul qilingan olib ketishlar yo‘q.',
      REJECTED: 'Tarixda hali rad javoblari yo‘q.',
      ALL: 'Menejer tayinlagach, so‘rov bu yerda «Oldim» va «Olmayman» tugmalari bilan chiqadi.',
    },
    sectionConfirm: 'Olib ketishga',
    sectionReturnConfirm: 'Brakni qaytarishni tasdiqlash',
    sectionHistory: 'Qarorlar tarixi',
    historyAttempts: 'Urinishlar tarixi · {{count}}',
    historyAttempt: '{{count}}-urinish',
    historyTaken: 'Oldim',
    historyRejected: 'Rad etildi',
    historyQcHandoffPending: 'OTKga topshirish kutilmoqda',
    historyQcHandoffDone: 'OTKga topshirdi',
    historyDefectReturn: 'Brakni qaytarish',
    historyProductionPickup: 'Sexdan olib ketish',
    historyDetailTitle: '{{code}} tarkibi',
    historyDetailFailed: 'Tarkibni yuklab bo‘lmadi',
    historyProducts: 'Mahsulotlar va qismlar',
    historyProductQty: '{{count}} dona',
    historyDimensions: '{{width}} × {{height}}',
    historyMaterials: 'Materiallar',
    historyNoMaterials: 'Materiallar ko‘rsatilmagan',
    historyTtnDownload: 'TTN yuklab olish',
    historyDocumentStatus: 'Hujjat holati',
    historyAccepted: 'Qabul qilindi',
    productTypes: {
      ROLL: 'Rulonli jalyuzi',
      COMBO: 'Kombo',
      PLISSE: 'Plisse',
      ROMAN: 'Rim pardasi',
      TEXTILE: 'Tekstil',
      HORIZONTAL: 'Gorizontal jalyuzi',
      MOSQUITO: 'Chivin to‘ri',
      BAMBOO: 'Bambuk jalyuzi',
      WOOD: 'Yog‘och jalyuzi',
      CUSTOM: 'Mahsulot',
      ACCESSORY: 'Butlovchi qism',
    },
    materialKinds: {
      FABRIC: 'Mato',
      RIBBON: 'Lenta',
      CORNICE: 'Karniz',
      MECHANISM: 'Mexanizm',
      SLAT: 'Planka',
      BORT: 'Bort',
      BAHROMA: 'Baxroma',
      ACCESSORY: 'Nomi',
    },
    materialValues: { without: 'Yo‘q', with: 'Bor', corniceType: 'Karniz turi', mm: 'mm' },
    confirmPickupTitle: '{{code}} olib ketishni tasdiqlaysizmi?',
    confirmPickupDesc: 'Menejer siz olib ketganingizni belgiladi. Tasdiqlang.',
    confirmTaken: 'Oldim',
    rejectTaken: 'Olmayman',
    rejectPickupTitle: '{{code}} ni olmaysizmi?',
    rejectPickupDesc:
      'Buyurtma «Tayyor» holatiga qaytadi va menejer boshqa haydovchini tayinlaydi.',
    confirmAssignmentTakenTitle: '{{code}} olib ketilganini tasdiqlaysizmi?',
    confirmAssignmentTakenDesc: 'Faqat partiya haqiqatan ham sizda bo‘lsa tasdiqlang.',
    rejectAssignmentTakenTitle: '{{code}} ni olmaysizmi?',
    rejectAssignmentTakenDesc:
      'Partiya «Tayyor» holatiga qaytadi va menejer boshqa haydovchini tayinlaydi.',
    confirmReturnTaken: 'Ha, brakni oldim',
    rejectReturn: 'Olmadim',
    confirmReturnTitle: '{{code}} braki olindimi?',
    confirmAssignmentReturnTitle: '{{code}} braki olindimi?',
    confirmReturnDesc: 'Mahsulot haqiqatan ham sizda bo‘lgandagina tasdiqlang.',
    rejectReturnTitle: '{{code}} brakini olmadingizmi?',
    rejectAssignmentReturnTitle: '{{code}} brakini olmadingizmi?',
    rejectReturnDesc: 'Menejer rad javobini oladi va boshqa haydovchini tayinlashi mumkin.',
    defectPoint: 'Brak punkti',
    returnPickupPlace: 'Bu manzildan oling: {{address}}',
  },
  driverDeliver: {
    title: 'Topshirish',
    subtitle: 'Olib kelganingizni OTKga bering',
    emptyTitle: 'Topshiradigan narsa yoʻq',
    emptyHint: 'Olib ketilgan buyurtmalar OTKga berish uchun shu yerda chiqadi.',
    sectionToQc: 'OTKga topshirish',
    toQc: 'OTKga →',
    confirmToQcTitle: '{{code}} OTKga berilsinmi?',
    confirmToQcDesc: 'OTK tekshiradi va oʻtkazadi yoki sexga qaytaradi.',
    confirmPartToQcTitle: '{{code}} · {{contractor}} OTKga berilsinmi?',
    confirmPartToQcDesc:
      'Faqat shu pudratchining qismi topshiriladi. Qolgan qismlar alohida topshirilguncha o‘z haydovchilarida qoladi.',
    sectionDefect: 'Brakni qaytarish',
    defectBadge: '⚠ Brak',
    returnDestination: 'Olib borish: {{destination}}',
    completeReturn: 'Qaytardim',
    completeReturnTitle: '{{code}} qaytarilganini tasdiqlaysizmi?',
    completeReturnDesc: 'Mahsulot ombor yoki yetkazib beruvchiga topshirilgandan keyingina bosing.',
  },
  driverSummary: {
    title: 'Sarhisob',
    subtitle: 'Davr uchun qancha qilindi va hozir nima ishda',
    picked: 'Olindi',
    handed: 'OTKga berildi',
    defect: 'Brak',
    inWork: 'Hozir ishda',
    emptyInWorkTitle: 'Hammasi topshirildi',
    emptyInWorkHint: 'Qoʻlda faol buyurtma yoʻq.',
  },
  qcCheck: {
    title: 'Tekshirish',
    subtitle: 'Oʻrnatishdan oldin tayyor buyurtmalarni tekshiring',
    statusFilterLabel: 'Tekshiruv filtri',
    statusFilters: {
      PENDING: 'Kutilmoqda',
      PASS: 'Yaroqli',
      DEFECT: 'Brak',
      ALL: 'Barchasi',
    },
    statusEmptyHints: {
      PENDING: 'Haydovchi buyurtmalarni topshirgach, ular shu yerda chiqadi.',
      PASS: 'Yaroqli deb topilgan mahsulotlar hali yoʻq.',
      DEFECT: 'Brak qarorlari hali yoʻq.',
      ALL: 'OTK qarorlari tarixi hali bo‘sh.',
    },
    sectionChecking: 'Tekshiruvda',
    sectionHistory: 'Qarorlar tarixi',
    check: 'Tekshirish',
    supplierWave: '{{count}}-topshirish',
    historyPassed: 'Yaroqli',
    historyCancelled: 'Qaror bekor qilingan',
    historyDefect: 'Brak',
    historyAttempts: 'Tekshiruvlar tarixi · {{count}}',
    historyAttempt: 'Tekshiruv {{count}}',
    historyInspector: 'Tekshirgan: {{name}}',
    historyDetailTitle: 'OTK · {{code}}',
    historyTitle: 'OTK',
    historyDestinationPassed: '→ {{name}}',
    historyDestinationPassedFallback: '→ Tayyor mahsulot ombori',
    historyDestinationDefect: '→ Brak ombori',
    historyDetailFailed: 'Tekshiruvni yuklab bo‘lmadi',
    historyDocumentStatus: 'OTK qarori',
    emptyTitle: 'Tekshiradigan narsa yoʻq',
    emptyHint: 'Haydovchi buyurtmalarni topshirgach, ular shu yerda chiqadi.',
  },
  qcDefects: {
    title: 'Brak',
    subtitle:
      'OTK aniqlagan brak darhol uzatiladi. Bu yerda montajdan qaytgan mahsulot qabul qilinadi',
    sectionDefect: 'Brak — sexga',
    sectionDecision: 'OTK izohi kerak',
    sectionForwarded: 'Keyingi bosqichga berildi',
    sectionCompleted: 'Yakunlangan',
    emptyTitle: 'Brak yoʻq',
    emptyHint: 'Brak qilingan buyurtmalar sababi bilan shu yerda chiqadi.',
    resolve: 'Qabul qilish va izohlash',
    cause: 'Brak sababi',
    selectCause: 'Sababni tanlang',
    comment: 'OTK izohi',
    installerComment: 'Oʻrnatuvchi izohi',
    initialQcComment: 'OTKning dastlabki izohi',
    reporterComment: 'Dastlabki izoh',
    outcome: 'Mahsulot bilan nima qilinadi',
    formHint:
      'Faqat amalda ko‘rgan nuqsoningizni yozing. Brak sababi va keyingi yoʻnalishni “Ombor va brak” bo‘limida o‘rnatish rahbari belgilaydi.',
    supplier: 'Yetkazib beruvchi',
    noSupplier: 'Tayinlanmagan',
    dueAt: 'Qayta ishlash muddati',
    driver: 'Qaytarish haydovchisi',
    selectDriver: 'Haydovchini tanlang',
    confirm: 'Qabul qilib, rahbarga yuborish',
    saving: 'Saqlanmoqda…',
    saved: 'OTK izohi o‘rnatish rahbariga yuborildi',
    saveFailed: 'Izohni saqlab bo‘lmadi',
    actFailed: 'Dalolatnomani ochib boʻlmadi',
    openAct: '{{number}} dalolatnomasi',
    causes: {
      MEASUREMENT_ERROR: 'Oʻlchov xatosi',
      MANUFACTURING_ERROR: 'Ishlab chiqarish xatosi',
      INSTALLER_DAMAGE: 'Oʻrnatuvchi shikastlagan',
      DRIVER_DAMAGE: 'Haydovchi shikastlagan',
      CLIENT_REJECTION: 'Mijoz rad etdi',
      OTHER: 'Boshqa sabab',
    },
    resolutions: {
      REWORK_SAME_CONTRACTOR: {
        title: 'Hozirgi mahsulotni qayta ishlash',
        hint: 'Hisobdan chiqarilmaydi. Haydovchi mahsulotni oʻsha yetkazib beruvchiga qaytaradi.',
      },
      NEW_ORDER: {
        title: 'Yangi mahsulot tayyorlash',
        hint: 'Eski mahsulot qaytariladi yoki hisobdan chiqariladi, shu partiya tanlangan ishlab chiqaruvchida yangidan boshlanadi.',
      },
      WRITE_OFF: {
        title: 'Almashtirmasdan hisobdan chiqarish',
        hint: 'Mahsulot hisobdan chiqariladi va alohida dalolatnoma oladi.',
      },
    },
    status: {
      needsDecision: 'OTK qarori kerak',
      needsVerdict: 'OTK izohi kerak',
      waitingManager: 'O‘rnatish rahbari qarorini kutmoqda',
      waitingDriver: 'Haydovchini kutmoqda',
      inTransit: 'Haydovchi yetkazib beruvchiga olib bormoqda',
      rework: 'Qayta ishlab chiqarishga berildi',
      replacementCreated: '{{code}} almashtirish yaratildi · eskisi hisobdan chiqdi',
      writtenOff: 'Hisobdan chiqarildi',
    },
    destination: {
      qc: 'Hozir: OTK brak punkti',
      manager: 'Keyin: “Ombor va brak” bo‘limi',
      supplier: 'Keyin: “{{name}}” yetkazib beruvchisi',
      replacement: 'Keyin: yangi buyurtma ishda',
      writeOff: 'Keyin: hisobdan chiqarish dalolatnomasi',
    },
  },
  qcSummary: {
    title: 'Sarhisob',
    subtitle: 'Davr uchun tekshirilgan va hozir tekshiruvda nima bor',
    checking: 'Tekshiruvda',
    passed: 'Yaroqli',
    defect: 'Brak',
    onCheck: 'Hozir tekshiruvda',
    emptyTitle: 'Navbat boʻsh',
    emptyHint: 'Hozir tekshiruvda buyurtma yoʻq.',
  },
  qcSheet: {
    compositionFailed: 'Tarkibni yuklab bo‘lmadi',
    compositionEmpty: 'Tarkibda mahsulot yo‘q',
    manufacturer: 'Ishlab chiqaruvchi',
    checkAll: 'Barchasini belgilash',
    clearAll: 'Barchasini olib tashlash',
    defectPhotoTitle: 'Brak surati',
    defectPhotoHint: 'Brakni suratga oling — sexga nima tuzatishni tushunarli boʻladi.',
    checklistTitle: 'Majburiy SNT tekshiruv roʻyxati',
    checklist: {
      dimensions: 'Oʻlchamlar oʻlchov va buyurtmaga mos',
      specification: 'Rang, mato va toʻplam spetsifikatsiyaga mos',
      mechanism: 'Mexanizm soz va tiqilmasdan ishlaydi',
      appearanceAndKit: 'Shikast yoʻq, toʻplam toʻliq',
    },
    passedCheck: 'Oʻtdi',
    failedCheck: 'Oʻtmadi',
    evidenceTitle: 'Tekshiruv suratlari',
    evidenceHint: 'Ixtiyoriy — fotosiz qaror qabul qilish mumkin',
    camera: 'Kamera',
    gallery: 'Galereya',
    checklistRequired: 'Tekshiruv roʻyxati tugallanmagan',
    checklistPassHint: '“Yaroqli” qarori uchun toʻrtta tekshiruvning barchasi oʻtishi kerak.',
    failedCheckRequired: 'Nuqson belgilanmagan',
    failedCheckHint: 'Brak uchun kamida bitta bandni “Oʻtmadi” holatida qoldiring.',
    defectItemsTitle: 'Qaysi mahsulotlarda brak bor',
    defectItemsHint: 'Faqat SNT haqiqatan muammo topgan mahsulotlarni tanlang.',
    defectItemsSelected: 'Tanlandi: {{selected}} / {{total}}',
    defectItemsRequired: 'Mahsulotni tanlang',
    defectItemsRequiredHint: 'Kamida bitta aniq brak mahsulotni belgilang.',
    defectItemsEmpty: 'Bu topshiruvda brak sifatida belgilash mumkin bo‘lgan mahsulot yo‘q.',
    withoutRoom: 'Xonasiz',
    causeTitle: 'Nima sodir bo‘ldi',
    causeRequired: 'Brak sababini tanlang',
    causeRequiredHint: 'Ishlab chiqarish xatosi yoki tashishda shikastlanishni tanlang.',
    cause: {
      MANUFACTURING_ERROR: 'Ishlab chiqarish xatosi',
      DRIVER_DAMAGE: 'Tashishda shikastlangan',
    },
    responsiblePreview: 'Javobgar: {{name}} · {{role}}',
    manufacturingNoChargePreview:
      'Qaytarish: {{name}} · jarima va ushlab qolishsiz bepul qayta ishlash',
    roleContractor: 'Pudratchi',
    roleCompany: 'Kompaniya',
    roleDriver: 'Haydovchi',
    manufacturerNotRecorded:
      'Ishlab chiqaruvchi qayd etilmagan — ish aniqlashtirish uchun saqlanadi',
    driverNotRecorded: 'Haydovchi qayd etilmagan — ish aniqlashtirish uchun saqlanadi',
    notRecorded: 'aniqlanmagan',
    confirmDefectCause:
      '{{cause}}. Brak mahsulotlar: {{count}}. Javobgar: {{responsible}}. Tasdiqlangach faqat tanlangan mahsulotlar brak ishiga o‘tadi.',
    confirmManufacturingDefect:
      '{{cause}}. Brak mahsulotlar: {{count}}. Tasdiqlangach tizim biriktirilgan haydovchiga ularni OTKdan olib, «{{manufacturer}}» ishlab chiqaruvchiga topshirish vazifasini darhol beradi. Rahbar qarori qaytarishni kechiktirmaydi.',
    onlineRequired: 'Internet kerak',
    onlineRequiredHint: 'SNT qarori dalillar bilan serverda darhol qayd etiladi.',
    uploadFailed: 'Suratni yuklab boʻlmadi',
    deleteFailed: 'Suratni oʻchirib boʻlmadi',
  },
  handoffPage: {
    title: 'Sexdan qabul qilish',
    titleQc: 'OTK · qabul',
    subtitle: 'Sexdan oling, OTKga topshiring',
    subtitleQc: 'Oʻrnatishdan oldin tayyor buyurtmalarni tekshiring',
    statToPickup: 'Olishga',
    statInTransit: 'Yoʻlda',
    statHandedPeriod: 'Davrda OTKga',
    statChecking: 'Tekshiruvda',
    statPassedPeriod: 'Davrda yaroqli',
    statDefectPeriod: 'Davrda brak',
    loadFailed: 'Yuklab boʻlmadi',
    emptyTitle: 'Qabul qilishga buyurtma yoʻq',
    emptyTitleQc: 'Tekshiradigan narsa yoʻq',
    emptyHint: 'Sex tugatgach — buyurtma «Sexdan oldim» tugmasi bilan paydo boʻladi.',
    emptyHintQc: 'Haydovchi buyurtmalarni topshirgach — ular shu yerda paydo boʻladi.',
    statusUpdated: 'Holat yangilandi',
    updateFailed: 'Yangilab boʻlmadi',
    pickedUp: 'Oldim',
    toQc: 'OTKga →',
    defectBadge: '⚠ Brak — sexga qaytaring',
    check: 'Tekshirish',
    confirmPickupTitle: '{{code}} sexdan olindimi?',
    confirmQcGiveTitle: '{{code}} OTKga topshirilsinmi?',
    confirmPickupDesc: 'Mahsulot ofisda. Keyin — OTKga.',
    confirmQcGiveDesc: 'OTK tekshiradi va oʻtkazadi yoki sexga qaytaradi.',
    describeDefect: 'Brakni tavsiflang',
    minThreeChars: 'Kamida 3 ta belgi.',
    passed: '✓ Yaroqli',
    defectRecorded: '⚠ Brak qayd etildi',
    failed: 'Boʻlmadi',
    qcSheetTitle: 'OTK · {{code}}',
    checked: '✓ Tekshirildi',
    sum: 'Summa',
    defectIntro: 'Brak — sababini yozing, buyurtma sexga qaytadi',
    reasonPlaceholder: 'Nimasi notoʻgʻri (qisqacha)',
    descriptionPlaceholder: 'Tavsif — qaysi mahsulotlar, qanday nuqson (ixtiyoriy)',
    recordDefect: '⚠ Brakni qayd etish',
    passQuestion: 'Mahsulot yaroqli deb topilsinmi?',
    recordDefectQuestion: 'Brak qayd etilsinmi?',
    passDesc: 'Buyurtmaning barcha qismlari yaroqli boʻlsa, u avtomatik omborga qabul qilinadi va «Montajga tayyor» boʻladi.',
    recordDefectDesc: 'Buyurtma qayta ishlash uchun sexga qaytadi.',
    passAction: 'Yaroqli deb topish',
    recordAction: 'Qayd etish',
    metaProducedLabel: 'Sexda tayyor',
    metaProducedHint: 'Oling va ofisga olib keling',
    metaDeliveredLabel: 'Ofisda',
    metaDeliveredHint: 'OTK tekshiruviga topshiring',
    metaQcCheckingLabel: 'Tekshiruvda',
    metaQcCheckingHint: 'Sifatni tekshiring va hal qiling: yaroqli yoki brak',
    metaQcPassedLabel: 'Montajga tayyor',
    metaQcPassedHint: 'Tayyor mahsulot omboriga qabul kutilmoqda',
    metaQcDefectLabel: 'Brak — sexga',
    metaQcDefectHint: 'Qayta ishlash uchun sexga qaytaring',
  },
  homePage: {
    quoteRequests: 'Hisob-kitob soʻrovlari',
    orders: 'Buyurtmalar',
  },
  avatarCropper: {
    title: 'Suratni kesing',
    zoom: 'Masshtab',
    uploading: 'Yuklanmoqda…',
  },
  changePinModal: {
    titleSetup: 'PIN oʻrnatish',
    titleKnow: 'PIN ni oʻzgartirish',
    titleForgotRequest: 'PIN ni tiklash',
    titleForgotVerify: 'Kodni kiriting',
    titleDone: 'PIN faol',
    subSetup: 'Sessiya bloklanganda kerak boʻladi.',
    subKnow: 'Joriy va yangi PIN ni kiriting.',
    subForgotRequest: 'Telegram-bot orqali tiklash.',
    subForgotVerify: 'Va darhol yangi PIN oʻrnating.',
    subDone: 'Tayyor.',
    newPin: 'Yangi PIN',
    repeatPin: 'PIN ni takrorlang',
    currentPin: 'Joriy PIN',
    repeatNewPin: 'Yangi PIN ni takrorlang',
    forgotPinLink: 'Joriy PIN ni eslay olmayman — Telegram orqali tiklash',
    disablePin: 'PIN orqali kirishni oʻchirish',
    disableTitle: 'PIN oʻchirilsinmi?',
    disableDescription:
      'PIN blokirovkasi oʻchiriladi. Toʻliq chiqqandan keyin qayta kirish uchun Telegram-kod kerak boʻladi.',
    disableCancel: 'PIN qolsin',
    disableConfirm: 'Oʻchirish',
    forgotRequestHint:
      'Bot bilan Telegram-chatingizga kod yuboramiz. Tasdiqlangandan soʻng eski PIN bekor qilinadi va siz yangisini oʻrnata olasiz.',
    backArrow: '← Orqaga',
    sending: 'Yuborilmoqda…',
    sendCode: 'Kod yuborish',
    codeSentTelegram: 'Kod Telegramga yuborildi',
    codeFromTelegram: 'Telegramdan kelgan kod',
    resetAndSetNew: 'Tiklash va yangisini oʻrnatish',
    doneHint:
      'Yangi PIN faol. Uni eslab qoling — keyingi safar sessiya bloklanganda kerak boʻladi.',
    done: 'Tayyor',
    pinMismatch: 'PIN mos kelmadi',
    pinMustDiffer: 'Yangi PIN joriysidan farq qilishi kerak',
    saving: 'Saqlanmoqda…',
    errorGeneric: 'Xatolik',
    errorOldPinInvalid: 'Joriy PIN notoʻgʻri kiritildi',
    errorPinNotSet: 'PIN hali oʻrnatilmagan',
    errorSendCodeFailed: 'Kodni yuborib boʻlmadi',
    errorCodeInvalid: 'Notoʻgʻri kod',
    errorCodeExpired: 'Kod muddati tugadi, yangisini soʻrang',
  },
  clientOrderCard: {
    order: 'Buyurtma',
    sum: 'Summa',
    measureLabel: 'Oʻlchov:',
    installLabel: 'Oʻrnatish:',
    newTitle: 'Menejer tez orada bogʻlanadi',
    newDetail: 'Tafsilotlarni aniqlaydi va oʻlchov sanasini taklif qiladi',
    measureAssignedTitle: 'Oʻlchov tayinlangan',
    measureAssignedDetail: 'Oʻlchovchi vaqt boʻyicha bogʻlanadi',
    measureDoneTitle: 'Oʻlchov olindi',
    measureDoneDetail: 'Menejer buyurtmani tasdiqlash uchun bogʻlanadi',
    orderedTitle: 'Buyurtma rasmiylashtirildi',
    orderedDetail: 'Mahsulotlarni ishlab chiqarishga tayyorlayapmiz',
    productionTitle: 'Ishlab chiqarishda',
    productionDetail: 'Sex sizning mahsulotingizni tayyorlayapti',
    producedTitle: 'Montajga tayyor',
    producedDetail: 'Tez orada oʻrnatishni tayinlaymiz',
    installAssignedTitle: 'Oʻrnatish tayinlangan',
    installedTitle: 'Oʻrnatildi',
    installedDetail: 'Ishning yakuniy qabuli',
    completedTitle: '✓ Buyurtma yakunlandi',
    completedDetail: 'Bizni tanlaganingiz uchun rahmat!',
    rescheduledTitle: 'Koʻchirildi',
    rescheduledDetail: 'Yangi sanani kelishamiz',
  },
  clientQuoteCard: {
    pendingReviewLabel: 'Javob kutilmoqda',
    quotedLabel: 'Hisob tayyor',
    acceptedLabel: 'Qabul qilindi',
    rejectedLabel: 'Rad etildi',
    expiredLabel: 'Muddati oʻtdi',
    pendingReviewSubtitle: 'Menejer tez orada hisoblaydi',
    quotedSubtitle: 'Qaror qabul qilish uchun oching',
    acceptedSubtitle: 'Buyurtma yaratildi',
    expiredSubtitle: 'Hali ham dolzarb boʻlsa, yangisini yarating',
    requestLabel: 'SOʻROV',
    dayOne: 'kun',
    dayFew: 'kun',
    dayMany: 'kun',
    itemOne: 'pozitsiya',
    itemFew: 'pozitsiya',
    itemMany: 'pozitsiya',
  },
  clientShareSheet: {
    title: '📱 Mijozga koʻrsating',
    copied: '✓ Nusxalandi',
    copyLink: '📋 Havoladan nusxa olish',
    hintBefore: 'Mijoz telefon kamerasi bilan skanerlaydi — bizning',
    hintBot: 'Telegram-bot',
    hintAfter:
      'status kartochkasi bilan ochiladi. Istalgan vaqtda botda «🔄 Yangilash» tugmasini bosib, joriy holatni koʻrish mumkin.',
    modeTelegram: 'Telegram',
    modeWeb: 'Veb-havola',
    webHint:
      'Mijoz telefon kamerasi bilan skanerlaydi — buyurtma veb-sahifasi ochiladi: holat, buyurtma PDF va ish bahosi.',
    creatingLink: 'Havola yaratilmoqda…',
    qrAlt: 'Buyurtma QR kodi',
    expiresUntil: 'Havola {{date}} gacha amal qiladi',
    qrFailed: 'QR yaratib boʻlmadi',
    copyFailed: 'Nusxalab boʻlmadi',
    copyManual: 'Quyidagi matndan qoʻlda nusxa oling',
  },
  deviceGate: {
    copied: 'Havola nusxalandi. Telefonda oching.',
    copyFailed: 'Nusxalab boʻlmadi. Havolani manzil satridan qoʻlda nusxalang.',
    title: 'Telefonda oching',
    descMobile:
      'Mobil ilova bir qoʻlda ishlash rejimiga moslangan — yirik tugmalar, buyurtma kartochkalari, suratlar — barchasini barmoq bilan qulay varaqlash mumkin.',
    descDesktop:
      'Kompyuterda ishlash uchun boshqaruv panelidan foydalaning — u maxsus katta ekran uchun ishlangan.',
    copyLink: 'Telefon uchun havolani nusxalash',
    continueAnyway: 'Baribir kompyuterda davom etish',
    footer: 'Jalousie Brand · Mobil ilova',
  },
  inboxButton: {
    ariaLabelUnread: 'Bildirishnomalar, {{count}} ta oʻqilmagan',
    ariaLabel: 'Bildirishnomalar',
    title: 'Bildirishnomalar',
  },
  inboxSheet: {
    loadError: 'Bildirishnomalarni yuklab boʻlmadi.',
    justNow: 'hozirgina',
    minutesAgo: '{{count}} daqiqa oldin',
    hoursAgo: '{{count}} soat oldin',
    daysAgo: '{{count}} kun oldin',
  },
  installerDashboardCard: {
    installs: 'Oʻrnatishlar',
    sum: 'Summa',
    debtors: 'Qarzdorlar',
    perMonth: 'oy uchun',
    totalDue: 'Jami toʻlanadigan',
    ordersCount: '{{count}} ta buyurtma',
    installedOn: 'oʻrnatildi {{date}}',
    paidOutOf: '{{total}} dan {{paid}} toʻlangan',
  },
  managerDashboardCard: {
    measures: 'Oʻlchovlar',
    successful: 'Muvaffaqiyatli',
    conversion: 'Konversiya',
    amount: 'Summa',
  },
  networkBadge: {
    online: 'Onlayn',
    offlineHint: 'Oflayn — oʻzgarishlar keyinroq sinxronlanadi',
  },
  common: {
    loading: 'Yuklanmoqda…',
    retry: 'Qayta urinish',
    cancel: 'Bekor qilish',
    save: 'Saqlash',
    next: 'Keyingi',
    back: 'Orqaga',
    close: 'Yopish',
    done: 'Tayyor',
    confirm: 'Tasdiqlash',
    yes: 'Ha',
    no: 'Yoʻq',
    ok: 'OK',
    delete: 'Oʻchirish',
    clear: 'Tozalash',
  },
  filter: {
    title: 'Buyurtmalar filtri',
    search_placeholder: 'Qidirish: F.I.O., telefon, kod',
    open_filters: 'Filtrlarni ochish',
    status: 'Holat',
    district: 'Tuman',
    city: 'Shahar',
    any: 'Har qanday',
    all_districts: 'Barcha tumanlar',
    no_districts: 'Bu shaharda tumanlar yoʻq',
    apply: 'Qoʻllash',
    reset: 'Tozalash',
    nothing_found_title: 'Hech narsa topilmadi',
    nothing_found_hint: 'Qidiruvni oʻzgartirib koʻring yoki filtrni tozalang.',
  },
  a11y: {
    main_nav: 'Asosiy navigatsiya',
    profile_menu: 'Profil menyusi, {{name}}',
    change_avatar: 'Profil suratini almashtirish',
    view_avatar: 'Profil suratini koʻrish',
    add_avatar: 'Profil suratini qoʻshish',
    unread_dot: 'oʻqilmagan',
    order_meta: 'Buyurtma metamaʼlumotlari',
    call: 'Qoʻngʻiroq qilish',
    to_point: 'Nuqtaga',
    take_photo: 'Suratga olish',
    from_gallery: 'Galereyadan',
  },
  push: {
    enabled: 'Push yoqildi',
    failed_subscribe: 'Obuna boʻlib boʻlmadi',
    failed_subscribe_hint: 'Nimadir notoʻgʻri ketdi. Keyinroq urinib koʻring.',
    no_vapid:
      'Server tomonida Push sozlanmagan (VAPID kalitlari yoʻq). Administratorga murojaat qiling.',
    no_standalone:
      'iPhone uchun bildirishnomalarni olish uchun ilovani bosh ekranga qoʻshing («Ulashish» → «Bosh ekranga»).',
    denied_in_browser: 'Brauzerda ruxsat bekor qilingan',
    unsupported: 'Qoʻllab-quvvatlanmaydi',
  },
  avatar: {
    updated: 'Surat yangilandi',
    deleted: 'Surat oʻchirildi',
    failed: 'Bajarib boʻlmadi',
  },
  tab: {
    today: 'Bugun',
    my_orders: 'Buyurtmalarim',
    week: 'Hafta',
    dashboard: 'Sarhisob',
    cashbox: 'Kassa',
    handoff: 'Qabul',
    driverPickup: 'Olib ketish',
    driverDeliver: 'Topshirish',
    driverSummary: 'Sarhisob',
    qcCheck: 'Tekshirish',
    qcDefects: 'Brak',
    qcSummary: 'Sarhisob',
    dealer: 'Kabinet',
    dispatch: 'Dispetcher',
    installationDefects: 'Brak',
    installationQuality: 'OTK',
    warehouseFinished: 'Tayyor',
    productionOwn: 'Oʻz sexi',
    productionContractors: 'Pudratchilar',
    catalog: 'Katalog',
    quotes: 'Soʻrovlar',
    referrals: 'Doʻstlar',
    measures: 'Oʻlchovlar',
    installs: 'Montajlar',
    visits: 'Chiqishlar',
    roles: 'Rollar',
    driver: 'Haydovchi',
    qc: 'OTK',
    shop: 'Sex',
    cashdesk: 'Kassa',
  },
  assignments: {
    active: 'Faol',
    done: 'Yakunlangan',
    empty: 'Hozircha tayinlangan buyurtmalar yoʻq.',
    status_waiting: 'Kutilmoqda',
    status_done: 'Rasmiylashtirilgan',
    status_overdue: 'Muddati oʻtgan',
    status_refused: 'Rad etilgan',
    all: 'Hammasi',
    filtered_empty: 'Bu holatdagi buyurtmalar yoʻq.',
    no_date: 'Sanasiz',
    collapse_all: 'Hammasini yigʻish',
    expand_all: 'Hammasini ochish',
    pick_measurer: 'Oʻlchovchi',
    pick_installer: 'Montajchi',
    pick_placeholder: 'Xodimni tanlang',
    pick_hint_measures: 'Yuqorida oʻlchovchini tanlang — uning oʻlchovlarini koʻrsatamiz.',
    pick_hint_installs: 'Yuqorida montajchini tanlang — uning montajlarini koʻrsatamiz.',
    pick_driver: 'Haydovchi',
    pick_hint_driver: 'Yuqorida haydovchini tanlang — uning olib ketish va topshirishlarini koʻrsatamiz. Tugmalar uning nomidan ishlaydi.',
    roles_label: 'Kimning oynasini ochish',
  },
  clientReferralsPage: {
    title: 'Doʻstingni olib kel',
    subtitle: 'Doʻstingiz jalyuzi buyurtma qilsa — tavsiya uchun sizga bonus.',
    subtitleBoth:
      'Doʻstga birinchi buyurtmaga chegirma, sizga — bonus. Ikkalangizga ham foydali 🤝',
    friendBonusNote:
      'Doʻstga — birinchi buyurtmaga {{bonus}} chegirma. Sizga — tavsiya uchun bonus.',
    inviteTitle: 'Sizning taklifingiz',
    inviteHint:
      'Doʻstingizga yuboring. Buyurtma vaqtida menejerga ismingiz yoki raqamingizni aytsin — soʻrovni siz bilan bogʻlaymiz.',
    shareBtn: 'Ulashish',
    copyBtn: 'Nusxalash',
    copied: 'Nusxalandi',
    shareText:
      'Jalousie tavsiya qilaman — jalyuzi va pardalar! Oʻlchovga yoziling va menejerga sizni {{name}} ({{phone}}) olib kelganini ayting — unga tavsiya uchun bonus beriladi. 🎁',
    shareTextBoth:
      'Jalousie tavsiya qilaman — jalyuzi va pardalar! Mening tavsiyam bilan buyurtma bering va birinchi buyurtmaga {{bonus}} chegirma oling. Menejerga sizni {{name}} ({{phone}}) olib kelganini ayting. 🎁',
    statInvited: 'Taklif qilingan',
    statRewarded: 'Mukofotlangan',
    statEarned: 'Ishlangan',
    listTitle: 'Men taklif qilganlar',
    empty:
      'Hozircha hech kim olib kelinmagan. Yuqoridagi taklifni ulashing — bonuslarni shu yerda kuzating.',
    friend: 'Doʻst',
    status_PENDING: 'kutilmoqda',
    status_REWARDED: 'bonus hisoblandi',
    status_CANCELLED: 'bekor qilingan',
    loadFailed: 'Yuklab boʻlmadi',
  },
  greet: {
    night: 'Xayrli tun',
    morning: 'Xayrli tong',
    day: 'Xayrli kun',
    evening: 'Xayrli kech',
    none: 'Hozircha buyurtmalar yoʻq.',
    today_count_one: 'Bugun {{count}} ta buyurtma',
    today_count_other: 'Bugun {{count}} ta buyurtma',
    upcoming_count_one: 'Yaqin kunlarda {{count}} ta buyurtma.',
    upcoming_count_other: 'Yaqin kunlarda {{count}} ta buyurtma.',
    factory_idle: 'Sexda hozir hech narsa yoʻq.',
    factory_in_progress_one: 'Ishlanmoqda: {{count}} ta buyurtma',
    factory_in_progress_other: 'Ishlanmoqda: {{count}} ta buyurtma',
  },
  order: {
    empty_today: 'Bugun buyurtmalar yoʻq',
    empty_today_hint: 'Menejer oʻlchov yoki oʻrnatish biriktirsa — shu yerda koʻrasiz.',
    empty_active_hint:
      'Bugunga oʻlchov yoʻq. Kelajakdagilar — «Hafta» tabida, muddati oʻtganlar va rad etilganlar — «Oʻlchovlar» boʻlimida.',
    empty_active_hint_installer:
      'Bugunga oʻrnatish yoʻq. Kelajakdagilar — «Hafta» tabida, barcha oʻrnatishlar — «Montajlar» boʻlimida.',
    empty_factory: 'Hozir ishda hech nima yoʻq',
    empty_factory_hint: 'Oʻlchovchi hisob-kitobni yuborgach, buyurtma shu yerda paydo boʻladi.',
    section: {
      overdue: 'Muddati oʻtgan',
      today: 'Bugun',
      tomorrow: 'Ertaga',
      week: 'Bu hafta',
      later: 'Keyin',
      in_progress: 'Ishlanmoqda',
      done: 'Tayyor',
      undated: 'Sanasiz',
    },
    error_title: 'Buyurtmalarni yuklab boʻlmadi',
  },
  auth: {
    title: 'Kirish',
    subtitle: 'Raqamingizni kiriting, kod yuboramiz',
    phone_label: 'Telefon raqami',
    phone_hint: 'Kod ushbu raqamga bogʻlangan Telegramʼga yuboriladi.',
    request_code: 'Kod olish',
    dev_title: 'DEV · Tez kirish',
    dev_hint: 'Telegram kodisiz. Productionʼda koʻrsatilmaydi.',
    welcome_back: 'Xush kelibsiz, {{name}}',
    offline_pin_wrong: 'PIN noto‘g‘ri',
    offline_unlock_unavailable: 'Server bilan aloqa yo‘q. Internetsiz kirish shu telefonda kamida bir marta PIN bilan onlayn kirgandan keyin ishlaydi.',
    pin_prompt: 'Davom etish uchun PIN-kodni kiriting',
    pin_forgot: 'Unutdim',
    other_phone: 'Boshqa raqam bilan kirish',
    method_password: 'Parol',
    method_code: 'Telegram kodi',
    subtitle_password: 'Raqam va parolni kiriting',
    password_label: 'Parol',
    password_hint: 'Parolni administrator beradi.',
    show_password: 'Parolni koʻrsatish',
    hide_password: 'Parolni yashirish',
  },
  profile: {
    menu: 'Menyu',
    theme: 'Mavzu',
    theme_hint: 'Ilovaning koʻrinishi',
    notifications: 'Bildirishnomalar',
    notifications_hint: 'Push va Telegram',
    language: 'Til',
    personal_data: 'Shaxsiy maʼlumotlar',
    my_cards: 'Mening kartalarim',
    my_cards_hint: 'Toʻlov qabul qilish uchun shaxsiy kartalar · {{count}}',
    security_pin: 'Xavfsizlik · PIN',
    archive: 'Buyurtmalar arxivi',
    notifications_history: 'Bildirishnomalar tarixi',
    help: 'Yordam va qoʻllanma',
    soon: 'tez orada',
    delete_photo: 'Suratni oʻchirish',
    lock: 'Bloklash (PIN)',
    logout: 'Toʻliq chiqish',
    logout_confirm_title: 'Toʻliq chiqilsinmi?',
    calc_images: 'Kalkulyatorda rasmlar',
    calc_images_hint: 'Jalyuzi, mato, mexanizm va karniz rasmlari',
    calc_layout: 'Oʻlchov shakli koʻrinishi',
    calc_layout_hint: 'Sizga qulay shaklda — bosqichma-bosqich yoki bir ekranda',
    calc_layout_wizard: 'Rasmlar bilan (bosqichma-bosqich)',
    calc_layout_wizard_desc: 'Klassik: 7 bosqich, katta rasm-kartochkalar',
    calc_layout_linear: 'Chiziqli forma',
    calc_layout_linear_desc: 'Hammasi bir ekranda, roʻyxatdan tanlash',
    calc_layout_accordion: 'Akkordeon',
    calc_layout_accordion_desc: 'Yigʻilgan guruhlar, prokress koʻrinadi',
    calc_layout_cards: 'Kartochkalar + chiplar',
    calc_layout_cards_desc: 'Kompakt tanlov chiplari, bir nechta deraza uchun qulay',
    order_layout: 'Buyurtma kartasi koʻrinishi',
    order_layout_managed: '{{layout}} · Administrator belgilagan',
    overdue_highlight: 'Muddati oʻtganlarni belgilash',
    overdue_highlight_hint:
      'Roʻyxatdagi muddati oʻtgan oʻlchovlarda qizil ramka va statik qizil belgi.',
    order_layout_hint: 'Bitta oynada akkordeon boʻlimlar yoki tablarga boʻlish',
    order_layout_single: 'Yagona oyna',
    order_layout_single_desc:
      'Boʻlimlar-akkordeon: Buyurtma · Hisob · Toʻlov · Surat, bosilganda ochiladi',
    order_layout_tabs2: '2 tab',
    order_layout_tabs2_desc: 'Asosiy · Hisob-kitob',
    order_layout_tabs3: '3 tab',
    order_layout_tabs3_desc: 'Buyurtma · Hisob-kitob · Surat va toʻlov',
    order_layout_tabs4: '4 tab',
    order_layout_tabs4_desc: 'Buyurtma · Hisob-kitob · Surat · Toʻlov',
  },
  inbox: {
    title: 'Bildirishnomalar',
    empty: 'Hozircha boʻsh',
    empty_hint:
      'Menejer buyurtma yuborganda yoki sex tayyorligini belgilaganda — bu yerda koʻrasiz.',
    mark_all_read: 'Hammasini oʻqilgan deb belgilash',
    marking: 'Belgilanmoqda…',
    unread_one: '{{count}} ta oʻqilmagan',
    unread_other: '{{count}} ta oʻqilmagan',
    ownGroup: 'Mening bildirishnomalarim',
    needsDecision: 'Qaror',
    filterTitle: 'Bildirishnoma turlari',
    filterAllOn: 'Hammasini koʻrsatish',
    filterNothing: 'Barcha turlar yashirilgan — kamida bittasini yoqing.',
    type_NEW_MEASURE_ASSIGNED: 'Oʻlchov tayinlandi',
    type_ORDER_READY_FOR_INSTALL: 'Montajga tayyor',
    type_PRODUCTION_COMPLETED: 'Sex tugatdi',
    type_PAYMENT_RECEIVED: 'Toʻlov',
    type_CLIENT_RESCHEDULED: 'Koʻchirish',
    type_ORDER_REFUSED: 'Muammo / rad etish',
    type_GENERIC: 'Marshrut va boshqa',
  },
  dashboard: {
    title: 'Sarhisob',
    today: 'Bugun',
    today_hint: 'bugun buyurtmalar',
    week: 'Hafta',
    week_hint: 'yaqin 7 kun ichida',
    in_work: 'Ishda',
    in_work_hint: 'davr ichida faol',
    done: 'Yakunlangan',
    done_hint: 'davr ichida',
    sum_week: 'Haftalik summa',
    sum_week_hint: 'yaqin 7 kun buyurtmalari boʻyicha',
    conversion: 'Konversiya',
    conversion_hint: 'qayta ishlanganlardan rasmiylashtirilganlar',
    inPeriod: 'Davr uchun',
    inPeriod_hint: 'davrdagi buyurtmalar',
    refused: 'Rad etilgan',
    refused_hint: 'rad etish va qaytarishlar',
    sumPeriod: 'Davr summasi',
    sumPeriod_hint: 'davr buyurtmalari boʻyicha',
  },
  logistDash: {
    title: 'Sarhisob',
    subtitle: 'bugungi taqsimot',
    apOn: 'Avtopilot',
    apOff: 'Qoʻlda',
    cardAssigned: 'Taqsimlangan',
    cardAssignedHint: 'ijrochi tayinlangan',
    cardQueue: 'Taqsimlanmagan',
    cardQueueHint: 'ijrochi kutmoqda',
    cardOverdue: 'Muddati oʻtgan',
    cardRefused: 'Rad etishlar',
    cardDone: 'Bajarilgan',
    distributedTitle: 'Taqsimot tayyorligi',
    distributedHint: 'tayinlandi {{assigned}}/{{planned}} · bajarildi {{done}}',
    openDispatch: 'Dispetcherda taqsimlash',
    loadTitleMeasure: 'Oʻlchovchilar yuki',
    loadTitleInstall: 'Montajchilar yuki',
    loadUnit: 'buy.',
    free: 'Boʻsh',
    emptyRoster: 'Faol ijrochilar yoʻq',
    emptyTitle: 'Bugun buyurtmalar yoʻq',
    emptyHint: 'Yangi buyurtmalar ishga tushishi bilan shu yerda paydo boʻladi.',
  },
  archive: {
    title: 'Arxiv',
    no_orders: 'buyurtmalarsiz',
    orders_total: '{{count}} ta buyurtma · {{sum}}',
    search_placeholder: 'Kod, ism, manzil boʻyicha qidirish…',
    filter_completed: 'Yakunlangan',
    filter_refused: 'Rad / qaytarilgan',
    filter_all: 'Hammasi',
    empty_completed: 'Yakunlangan buyurtmalar yoʻq',
    empty_records: 'Yozuvlar yoʻq',
    empty_search: 'Hech nima topilmadi',
    empty_search_hint: 'Boshqa soʻrov bilan urinib koʻring yoki filtrni oʻzgartiring.',
    empty_hint: 'Yopilgan buyurtmalar ish jarayonida shu yerda paydo boʻladi.',
  },
  role: {
    measurer: 'Oʻlchovchi',
    installer: 'Oʻrnatuvchi',
    manager: 'Menejer',
    logist: 'Logist',
    accountant: 'Buxgalter',
    production: 'Sex',
    admin: 'Administrator',
  },
  status: {
    draft: 'Qoralama (amoCRM)',
    new: 'Yangi',
    measure_assigned: 'Oʻlchov biriktirildi',
    measuring: 'Oʻlchov ketmoqda',
    ordered: 'Rasmiylashtirildi',
    on_hold: 'Kutilmoqda',
    production: 'Ishlab chiqarishda',
    delivered: 'Haydovchi olib ketdi',
    qc_checking: 'OTK tekshiruvida',
    qc_passed: 'Montajga tayyor',
    qc_defect: 'Brak / qayta ishlash',
    produced: 'Tayyor — haydovchi kerak',
    install_assigned: 'Montaj biriktirildi',
    installed: 'Oʻrnatildi',
    completed: 'Yakunlangan',
    rescheduled: 'Koʻchirildi',
    refused: 'Rad etildi',
    returned: 'Qaytarildi',
    measure_lost: 'Muddati oʻtgani uchun olindi',
  },
  detail: {
    title: 'Buyurtma',
    cancellationCredit: 'Mahsulotlarning kelishilgan bekor qilinishi',
    cancelledQuantity: 'Mijoz bekor qilgan: {{count}} dona',
    refund: 'Mijozga qaytarish',
    refunded_by: 'Qaytardi: {{name}}',
    received_by: 'Qabul qildi: {{name}}',
    payment_pending_confirmation: 'Toʻlov tasdiqlanishini kutmoqda',
    block_client: 'Mijoz',
    block_measure: 'Oʻlchov',
    block_install: 'Montaj',
    installer: 'Oʻrnatuvchi',
    block_address: 'Manzil',
    block_notes: 'Eslatmalar',
    block_flags: 'Oʻlchovchiga belgilar',
    block_note: 'Izoh',
    block_calc: 'Hisob-kitob',
    block_payment: 'Toʻlov',
    block_photo: 'Suratlar',
    block_history: 'Tarix',
    tab_main: 'Asosiy',
    tab_order: 'Buyurtma',
    tab_calc: 'Hisob-kitob',
    tab_media: 'Surat va toʻlov',
    tab_photos: 'Suratlar',
    tab_pay: 'Toʻlov',
    section_calc_pay: 'Hisob-kitob va toʻlov',
    tab_note: 'Izoh',
    block_measure_note: 'Sex uchun izoh',
    measure_note_hint:
      'Ishlab chiqarish uchun muhim narsalar: obyekt xususiyatlari, oʻlchov boʻyicha eslatmalar, mijoz istaklari. Buyurtma bilan birga ketadi.',
    measure_note_help: 'Bu maydon nima uchun',
    measure_note_ph: 'Masalan: deraza radiator orqasida, tavaqa ichkariga ochiladi',
    measure_note_save: 'Izohni saqlash',
    measure_note_saving: 'Saqlanmoqda…',
    measure_note_saved: 'Izoh saqlandi',
    measure_note_queued: 'Oflayn saqlandi — tarmoq paydo boʻlganda yuboramiz',
    measure_note_failed: 'Izohni saqlab boʻlmadi',
    expand_all: 'Hammasini ochish',
    collapse_all: 'Hammasini yigʻish',
    history_title: 'Buyurtma tarixi',
    history_current: 'Hozir',
    history_btn: 'Tarix',
    btn_call: 'Qoʻngʻiroq',
    btn_route: 'Yoʻnalish',
    measurer: 'Oʻlchovchi',
    date_slot: 'Sana / vaqt',
    measurement_count: 'Mahsulotlar soni',
    measurement_plan: 'Reja {{count}}',
    measurement_actual: 'Haqiqiy {{count}}',
    measurement_difference: 'Farq {{difference}}',
    items_count_one: '{{count}} ta mahsulot',
    items_count_other: '{{count}} ta mahsulot',
    total: 'Jami',
    due: 'Toʻlanishi kerak',
    paid_full: '✓ Toʻliq toʻlangan',
    overpaid: '⚠ Ortiqcha toʻlov',
    payment_status: 'Holat',
    status_updated: 'Holat yangilandi',
    update_failed: 'Bajarib boʻlmadi',
    unknown_action: 'Notanish amal',
  },
  problem: {
    title: 'Nimadir notoʻgʻri ketdimi?',
    intro: 'Sababni tanlang — menejer koʻradi va mijoz bilan bogʻlanadi.',
    saved: 'Sabab qayd etildi',
    save_failed: 'Bajarib boʻlmadi',
    submit: 'Saqlash',
    saving: 'Saqlanmoqda…',
    comment_label: 'Izoh (ixtiyoriy)',
    comment_placeholder: 'Masalan: mijoz juma kuni 15:00 ga koʻchirishni soʻradi',
    reason: {
      client_refused: 'Mijoz rad etdi',
      no_answer: 'Javob yoʻq',
      on_hold: 'Kutilmoqda (menejer hal qiladi)',
      reschedule: 'Boshqa kunga koʻchirish',
      wrong_address: 'Notoʻgʻri manzil',
      wrong_measurement: 'Oʻlchovda xato',
      other: 'Boshqa',
    },
    help_btn: 'Bu nimani anglatadi?',
    help: {
      client_refused: 'Mijoz buyurtmadan voz kechdi — buyurtma yopiladi.',
      no_answer:
        'Mijoz telefonni koʻtarmayapti. Qoʻngʻiroq urinishi qayd etiladi, buyurtma sizda qoladi — keyinroq urinib koʻring.',
      on_hold:
        'Mijoz oʻylanyapti yoki hozir tayyor emas. Buyurtma menejerga pauzaga oʻtadi — u shugʻullanadi.',
      reschedule: 'Oʻlchov boshqa kunga koʻchiriladi (yangi sanani kelishing).',
      wrong_address:
        'Manzil notoʻgʻri. Buyurtma menejerga qaytadi — u manzilni aniqlab, oʻlchovni qayta tayinlaydi.',
      wrong_measurement:
        'Olingan oʻlchovda xato. Buyurtma qayta oʻlchovga qaytadi — xonalar saqlanadi, siz tuzatasiz.',
      other: 'Boshqa sabab — izohda yozing. Buyurtma menejerga pauzaga oʻtadi.',
    },
  },
  calc: {
    price_label: 'Narxi', price_offline: 'aloqasiz hisob — server qayta hisoblaydi',
    quantity: 'Dona',
    step_type: 'Jaluzi turi',
    step_size: 'Oʻlchamlar',
    step_fabric: 'Mato',
    fabric_search: 'Mato qidirish…',
    step_ribbon: 'Lenta',
    step_mechanism: 'Mexanizm',
    mechanism_details: 'Mexanizm parametrlari',
    mech_manual: 'Mexanizmsiz',
    mech_electric: 'Elektron',
    mech_pick_motor: 'Motorni tanlang',
    mech_enter_value: 'Qiymatni kiriting',
    step_cornice: 'Karniz',
    step_mount_type: 'Oʻrnatish turi',
    price_option: 'Hisob varianti',
    plisse_mount_type: 'Oʻrnatish turi',
    cornice_type: 'Karniz turi',
    cornice_arch: 'Arka',
    price_by_size: 'Oʻlcham boʻyicha',
    price_by_size_hint: 'Narx buyum oʻlchamiga bogʻliq',
    fixed_price: 'Qatʼiy narx',
    base_cornice_color_hint: 'Rang alohida tanlanadi',
    mount_variant_with_cornice: 'Karniz bilan',
    no_cornice_color_hint: 'Rang tanlanmaydi',
    price_range: '{{from}} dan {{to}} gacha',
    base_cornice: 'Bazaviy',
    plisse_cornice_faltex: 'Faltex, teshmasdan',
    plisse_cornice_drill: 'Teshish bilan',
    plisse_cornice_frame: 'Rom profilida teshmasdan',
    plisse_cornice_arch: 'Arka montaji',
    plisse_variant_base: 'Oddiy montaj',
    plisse_opening_direction: 'Ochilish tomoni',
    plisse_opening_side: 'Yon tomonga',
    plisse_opening_up: 'Yuqoriga',
    plisse_measurement: 'O‘lchash',
    plisse_measurement_inner: 'Ichki',
    plisse_measurement_outer: 'Tashqi',
    auto_value: 'avtomatik',
    pb_cornices_count: '{{count}} ta karniz',
    step_total: 'Yakun',
    nav_back: '← Orqaga',
    nav_next: 'Keyingi →',
    save_item: '✓ Mahsulotni saqlash',
    save_add_another: 'Yangi oʻlchov',
    add_size_row: 'Oʻlcham qatorini qoʻshish',
    size_row_short: 'qator',
    size_row_number: 'Oʻlcham {{number}}',
    row_number_short: '№',
    remove_size_row: '{{number}}-oʻlchamni oʻchirish',
    saving: 'Saqlanmoqda…',
    saved: 'Mahsulot saqlandi',
    save_failed: 'Saqlab boʻlmadi',
    live_total: 'Joriy hisob',
    exit_title: 'Kalkulyator yopilsinmi?',
    exit_desc: 'Kiritilgan maʼlumotlar saqlanmaydi.',
    exit_confirm: 'Yopish',
    room_title: 'Qaysi xonada?',
    preview_btn: 'Oldindan koʻrish',
    order_preview_title: 'Buyurtma oldindan koʻrish',
    order_preview_hint:
      'Buyurtma ishlab chiqarishga shunday ketadi. Hammasi toʻgʻriligini tekshiring.',
    order_preview_empty: 'Buyurtmada hozircha mahsulot yoʻq.',
    order_preview_note: 'Sex uchun izoh',
    order_preview_note_empty: 'Toʻldirilmagan',
    room_items_in: 'Bu xonada',
    measurements_count: 'Oʻlchovlar: {{count}}',
    copy_help_title: 'Bu qanday ishlaydi',
    copy_help_1: 'Mahsulot doim joriy xonaga saqlanadi — nomi forma sarlavhasida.',
    copy_help_2:
      '«+ Xona…» belgilang — xuddi shu mahsulot u yerda ham avtomatik yaratiladi. Bir xil derazalar bir nechta xonada boʻlsa qulay: bir marta toʻldirasiz.',
    copy_help_3: 'Nusxalar mustaqil: keyin har birini alohida oʻzgartirish yoki oʻchirish mumkin.',
    pcs: 'dona',
    item_deleted: 'Mahsulot oʻchirildi',
    item_delete_title: 'Mahsulot oʻchirilsinmi?',
    item_delete_confirm: 'Oʻchirish',
    item_deleting: 'Oʻchiryapman…',
    item_view: 'Koʻrish',
    item_view_empty: 'Faqat oʻlchamlar',
    item_amount: 'Summa',
    item_copy: 'Nusxalash',
    item_copied: 'Nusxalandi',
    item_copied_hint: 'Yangi deraza oʻlchamlarini kiriting va saqlang',
    item_edit: 'Tahrirlash',
    item_edit_started: 'Mahsulot tahriri',
    item_edit_hint: 'Oʻzgartiring va saqlang — mahsulot yangilanadi',
    item_editing: 'Mahsulot tahriri',
    item_edit_cancel: 'Bekor qilish',
    save_edit: '✓ Oʻzgarishlarni saqlash',
    notes: 'Izoh',
    notes_label: 'Izoh',
    notes_placeholder: 'Oʻlchov xususiyatlari, mijoz istaklari…',
    item_delete_failed: 'Oʻchirib boʻlmadi',
    preview_hide: 'Yashirish',
    preview_title: 'Nima toʻldirildi',
    mm: 'mm',
    cm: 'sm',
    m: 'm',
    room_existing: 'Mavjud',
    room_new: 'Yangi xona',
    room_placeholder: 'Masalan: Yotoqxona',
    room_create: 'Xona yaratish',
    change_room: '+ Xona',
    room_creating: 'Yaratilmoqda…',
    room_created: '«{{name}}» xonasi yaratildi',
    room_name_exists:
      'Bunday nomli xona allaqachon bor. Uni roʻyxatdan tanlang yoki boshqa nom kiriting.',
    room_close_title: 'Saqlamasdan yopilsinmi?',
    room_close_desc: 'Kiritilgan xona nomi saqlanmaydi.',
    room_close_stay: 'Qolish',
    room_close_confirm: 'Yopish',
    new_room_warn: 'Saqlanmagan oʻlchov maʼlumotlari oʻchib ketishi mumkin',
    items_count_one: '{{count}} ta mahsulot',
    items_count_other: '{{count}} ta mahsulot',
    // Step 2 — oʻlchamlar (oʻlchovchi METRDA kiritadi; bazada mm saqlanadi)
    width_mm: 'Eni, m',
    height_mm: 'Boʻyi, m',
    quantity_pcs: 'Soni, dona',
    // requiresSlatSize belgili karniz: avval «Plankali / Plankasiz» tanlovi,
    // faqat «Plankali» holatida oʻlcham maydoni koʻrsatiladi.
    slat_choice: 'Planka',
    slat_on: 'Plankali',
    slat_off: 'Plankasiz',
    slat_size: 'Planka oʻlchami, m',
    slat_size_optional: 'Planka oʻlchami, m (ixtiyoriy)',
    slat_size_ph: 'mas. 0,05',
    bort_size: 'Bort oʻlchami, mm',
    bort_size_ph: 'mas. 50',
    bahroma_choice: 'Bahroma',
    bahroma_on: 'Bahromali',
    bahroma_off: 'Bahromasiz',
    slat_title: 'Planka',
    slat_no: 'Plankasiz',
    slat_yes: 'Planka bilan',
    area: 'Maydon',
    perimeter: 'Perimetr',
    decrease: 'Kamaytirish',
    increase: 'Oshirish',
    // Steps 3-6 — without
    no_fabric: 'Matosiz',
    no_fabric_hint: 'Mahsulot matosiz — masalan, faqat mexanizm',
    no_ribbon: 'Lentasiz',
    no_ribbon_hint: 'Mahsulot dekorativ pardozsiz boradi',
    no_mechanism: 'Mexanizmsiz',
    no_cornice: 'Karnizsiz',
    no_photo: 'Surat yoʻq',
    categories_empty:
      'Toifalar yuklanmagan. Administratordan «Narxlar» roʻyxatini toʻldirishni soʻrang.',
    // Step 7 — yakun
    summary_size: 'Oʻlchami',
    summary_fabric: 'Mato',
    summary_ribbon: 'Lenta',
    summary_mechanism: 'Mexanizm',
    summary_cornice: 'Karniz',
    summary_base_price: 'Asosiy narx',
    summary_total: 'Jami',
    zero_hint_components:
      'Jami 0 soʻm — bu jalyuzi turi uchun mos mato/mexanizm sozlanmagan. Administratordan «Moslik» yorligʻida ularni bogʻlashni soʻrang.',
    zero_hint_fixed:
      'Jami 0 soʻm — bu jalyuzi turi uchun asosiy narx koʻrsatilmagan. Administrator buni admin panelida sozlashi kerak (Prays → Jalyuzi turlari).',
    markup: 'Qoʻshimcha narx',
    discount: 'Chegirma',
    help_toggle: 'Bu nima va nima uchun?',
    help_hide: 'Yordamni yashirish',
    help_intro:
      'Asosiy narx prays boʻyicha hisoblangan (mato × maydon, lenta × perimetr, mexanizm har bir mahsulot, karniz × kenglik). Qoʻshimcha narx va chegirma — bu yakuniy qoʻlda sozlash.',
    help_markup_title: 'Qoʻshimcha narx — qachon koʻtarish kerak',
    help_markup_reasons:
      'Qimmat tuman · qiyin obyekt (yuqori qavat, liftsiz, nostandart) · shoshilinch buyurtma · mijoz koʻproq toʻlashga tayyor.',
    help_discount_title: 'Chegirma — qachon kamaytirish kerak',
    help_discount_reasons:
      'Doimiy mijoz · katta buyurtma (3+ deraza) · mavsumiy aksiya · joyda savdo.',
    help_formula:
      'Formula: Jami = Asosiy × (1 + Qoʻshimcha%) × (1 − Chegirma%). Avval qoʻshimcha narx, keyin chegirma.',
    help_note:
      'Ikkala maydon buyurtmaga alohida yoziladi — rahbar qancha qoʻshilgani yoki kamaytirilganini koʻradi.',
    // Prays rejimi (pbMode): narx toʻgʻridan-toʻgʻri praysdan
    step_option: 'Karniz / variant',
    pb_search: 'Prays boʻyicha qidirish…',
    pb_new: 'yangi',
    pb_colors_count: '{{count}} rang',
    step_fabric_color: 'Mato rangi',
    step_ribbon_color: 'Lenta rangi',
    step_cornice_color: 'Karniz rangi',
    pb_pick_fabric: 'Praysdan matoni tanlang',
    pb_pick_option: 'Karniz / variantni tanlang',
    pb_without_cornice: 'Karnizsiz',
    pb_without_cornice_hint: 'Faqat mato, karnizsiz — prays boʻyicha arzonroq',
    pb_price_missing_short: 'Narx ko‘rsatilmagan',
    pb_price_incomplete_short: 'Barcha oraliqlar to‘ldirilmagan',
    pb_price_missing_for_option: 'Avval praysda «{{row}}» uchun «{{option}}» narxini ko‘rsating.',
    pb_extra_fabric: 'Mato va rangi {{n}}',
    step_fabric_and_color: 'Mato va rangi',
    pb_extra_fabric_add: 'Yana mato',
    pb_extra_fabric_remove: 'Matoni olib tashlash',
    lamella_layout: 'Joylashuv',
    lamella_pick: 'Tanlash',
    lamella_need_width: 'Avval eni',
    lamella_kind_EDGES: 'Chetlar + oʻrta',
    lamella_kind_ALTERNATE: 'Navbatma-navbat',
    lamella_edges: 'Chetlarda',
    lamella_middle_left: 'oʻrtada: {{n}}',
    lamella_step_q: 'Nechtadan?',
    lamella_step: '{{n}} tadan',
    lamella_swap: 'Ranglarni almashtirish',
    lamella_done: 'Tayyor',
    lamella_text_edges: 'chetlar — {{fabric}}, {{n}} donadan',
    lamella_text_middle: 'oʻrta — {{fabric}}, {{n}} dona',
    lamella_text_alternate: 'navbatma-navbat {{step}} tadan: {{parts}}',
    lamella_text_part: '{{fabric}} — {{n}} dona',
    pb_size_limit_width: 'Eni {{value}} m — maksimal {{max}} m dan katta',
    pb_size_limit_height: 'Boʻyi {{value}} m — maksimal {{max}} m dan katta',
    pb_size_limit_area: 'Maydon {{value}} m² — maksimal {{max}} m² dan katta',
    step_lamella: 'Lamellar',
    step_lamella_color: 'Lamel rangi',
    interglass_system: 'Tizim',
    pb_price_ranges_incomplete: '«{{option}}» uchun barcha oraliqlar to‘ldirilmagan: {{ranges}}.',
    pb_price_calc: 'Narx prays boʻyicha hisoblanmoqda…',
    summary_option: 'Karniz / variant',
    summary_pb_price: 'Prays boʻyicha narx',
    summary_pb_no_cornice: 'karnizsiz',
    // «Chegirma/qoʻshimcha narx» inline-kartochkasi («Hisob-kitob» boʻlimida)
    mdTitle: 'Chegirma / qoʻshimcha narx',
    mdHint: 'Mijoz buni koʻrmaydi',
    mdSwitchTitle: 'Hisob-kitob shartlari oʻzgartirilsinmi?',
    mdSwitchFromMarkup: 'Buyurtmada {{value}} ustama tanlangan. Chegirmaga oʻtish uning oʻrnini bosadi va hisob-kitobning dastlabki maʼlumotlarini oʻzgartiradi.',
    mdSwitchFromDiscount: 'Buyurtmada {{value}} chegirma tanlangan. Ustamaga oʻtish uning oʻrnini bosadi va hisob-kitobning dastlabki maʼlumotlarini oʻzgartiradi.',
    mdSwitchAfterSave: 'Oʻzgarishlar faqat «Saqlash» bosilgandan keyin qoʻllanadi.',
    mdSwitchConfirm: 'Almashtirish',
    mdNone: 'Yoʻq',
    mdSave: 'Saqlash',
    mdSaved: 'Saqlandi',
    mdSaveErr: 'Saqlab boʻlmadi',
    mdLimit: 'Chegirma limitdan katta (maks {{max}}%)',
    mdSubtotal: 'Oraliq summa',
    mdTotal: 'Jami',
    mdEdit: 'Oʻzgartirish',
    mdDelete: 'Oʻchirish',
    mdMarkupBadge: 'Ustama',
    mdDiscountBadge: 'Chegirma',
  },
  payment: {
    title: 'Toʻlovni qabul qilish',
    due_label: 'Toʻlanishi kerak',
    fill_remaining: 'Qoldiqni toʻldirish',
    method: 'Usul',
    pick_method: 'Toʻlov turini tanlang',
    method_required: 'Avval toʻlov turini tanlang',
    account_required: 'Avval tushum hisobini tanlang',
    account_unavailable: 'Bu hisob mavjud emas. Roʻyxatni yangilab, boshqasini tanlang.',
    accounts_loading: 'Tushum hisoblari yuklanmoqda…',
    accounts_load_failed: 'Hisoblarni yuklab boʻlmadi. Qayta urinib koʻring.',
    accounts_offline: 'Tushum hisoblarini yuklash uchun internetga ulaning.',
    payer_inn_invalid: 'STIR — 9 raqam (JShShIR — 14)',
    amount_required: 'Toʻlov summasini kiriting',
    note_placeholder: 'Izoh (ixtiyoriy)',
    accept: 'Qabul qilish',
    saving: 'Saqlanmoqda…',
    accepted: 'Toʻlov qabul qilindi',
    accepted_full: 'Toʻliq toʻlov qabul qilindi',
    recordedAwaitingConfirmation: 'Toʻlov yozildi · tasdiqlanishini kutmoqda',
    savedOfflineTitle: 'Toʻlov oflayn saqlandi',
    pendingRecordedLimit: 'Qoldiqning bir qismi yozilgan va tasdiqlanishni kutmoqda. Yangi toʻlov: koʻpi bilan {{sum}}.',
    failed: 'Bajarib boʻlmadi',
    confirm_title: '{{sum}} qabul qilinsinmi?',
    method_cash: 'Naqd',
    method_card: 'Karta',
    method_uzum: 'UZUM orqali toʻlov',
    method_alif: 'Alif Nasiya orqali toʻlov',
    method_transfer: 'Pul oʻtkazma',
    amount_exceeds_due:
      'Summa buyurtma qoldigʻidan oshib ketdi. Koʻpi bilan {{sum}} qabul qilish mumkin.',
  },
  completeInstall: {
    compactTitle: 'Montajni yakunlash',
    loading: 'Buyurtma yuklanmoqda…',
    progressPhoto: 'Foto',
    progressPay: 'Toʻlov',
    progressSign: 'Imzo',
    photoStep: 'Natija fotosi',
    photoCount: 'Foto qoʻshildi: {{n}}',
    photoOptional: 'Ixtiyoriy — fotosiz yakunlash mumkin',
    hide: 'Yashirish',
    viewPhotos: 'Fotolar',
    add: 'Qoʻshish',
    payFull: 'Toʻliq toʻlangan',
    payRemaining: 'Qabul qilish uchun qoldi {{due}}',
    payDoneHint: 'Buyurtma qarzi yopildi',
    payPartialHint: 'Naqd, karta yoki oʻtkazma orqali bir nechta toʻlov qabul qilish mumkin',
    done: 'Tayyor',
    takePayment: 'Toʻlov qabul qilish',
    signStep: 'Mijoz imzosi',
    signDone: 'Qabul dalolatnomasi imzolandi',
    signRequired: 'Buyurtmani yopish uchun yakuniy dalolatnoma shart',
    signAfterInstall: 'Imzo montaj yakunlangach ochiladi',
    afterInstall: 'Montajdan soʻng',
    completeOrder: 'Buyurtmani yakunlash',
    signTake: 'Dalolatnomani imzolash',
    readiness: 'Yakunlashga tayyorlik',
    blockedDue: 'Qabul qilish uchun {{due}} qoldi',
    signAfterInstallReady: 'Montajni yakunlang — keyin mijoz imzosi ochiladi',
    signRequiredReady: 'Montaj saqlandi. Buyurtmani yopish uchun dalolatnomani imzolang',
    ready: 'Hammasi tayyor',
    doneToast: 'Montaj yakunlandi',
    completeFailed: 'Montajni yakunlab bo\u2018lmadi',
    completing: 'Yakunlanmoqda\u2026',
    completeInstall: 'Montajni yakunlash',
  },
  installIssue: {
    contactTitle: 'Mijoz bilan bogʻlanishda yordam kerak',
    rescheduleTitle: 'Oʻrnatishni koʻchirish kerak',
    productTitle: 'Mahsulot bilan muammo',
    contactEffect: 'Oʻrnatish tayinlangan holda qoladi. Oʻrnatish rahbari mijoz bilan bogʻlanib, tashrifni tasdiqlash vazifasini oladi.',
    rescheduleEffect: 'Joriy vaqt boʻshaydi. Buyurtma oʻrnatish rahbari yangi sanani kelishishini kutadi.',
    contactPending: 'Mijoz bilan aloqa kutilmoqda',
    waitingDate: 'Sana kelishilishi kutilmoqda',
    openBtn: 'Oʻrnata olmadim',
    draftSavedToast: 'Qoralama saqlandi',
    draftRemovedToast: 'Qoralama o‘chirildi',
    title: 'Montajda muammo',
    editTitle: 'Montaj xatolarini aniqlashtirish',
    viewErrorsTitle: 'Montaj xatolari',
    helpButton: 'Maʼlumot',
    helpTitle: 'Bu shakl qanday ishlaydi',
    intro:
      '{{code}} buyurtmasi. Sababni tanlang, vaziyatni aniqlashtiring va xabar yuboring.',
    productOnlyIntro:
      '{{code}} buyurtmasi. Har bir o‘rnatilmagan deraza uchun alohida murojaat yuboring. O‘lchov xatosi, mahsulot yoki mijoz xohishlari bo‘yicha qarorni o‘rnatish rahbari qabul qiladi.',
    lockedDefectIntro:
      '{{code}} buyurtmasi. Bosqich: {{status}}. Bu murojaatni faqat ko‘rish mumkin. Tuzatish kerak bo‘lsa, o‘rnatish rahbariga murojaat qiling.',
    reason: {
      CANT_REACH: {
        title: 'Mijozga qoʻngʻiroq qila olmadim',
        hint: 'Mijoz telefonni koʻtarmayapti',
      },
      CLIENT_ABSENT: {
        title: 'Mijoz joyda yoʻq',
        hint: 'Keldim — hech kim yoʻq / eshik ochilmadi',
      },
      NO_ACCESS: { title: 'Obyektga kirish yoʻq', hint: 'Qurilish, taʼmir, obyekt tayyor emas' },
      CLIENT_ASKED: { title: 'Mijoz koʻchirishni soʻradi', hint: 'Boshqa kunga kelishni soʻraydi' },
      PRODUCT_PROBLEM: {
        title: 'O‘rnatilmaganlik sababi',
        hint: 'O‘lcham, komplektatsiya, sifat, mijozning voz kechishi yoki xohishining o‘zgarishi',
      },
    },
    productDefectBtn: 'O‘rnatilmaganlik sababi',
    affectedItemsTitle: 'Mahsulotni tanlang',
    affectedItemsHint:
      'Mahsulotni oching va vaziyatni tanlang. O‘lchov xatosi bo‘lsa, to‘g‘ri o‘lcham va komplektatsiyani o‘zingiz ko‘rsating.',
    defectCommentScopeHint:
      'Izoh faqat yangi belgilangan guruhga tegishli. Oldingi brak partiyasi va uning izohi yangi guruh bilan aralashtirilmaydi.',
    affectedItemsRequired: 'Kamida bitta mahsulotni tanlang',
    allDefectsWillBeCancelled: 'Barcha brak belgilari olib tashlanadi va ariza bekor qilinadi',
    cancelDefectReport: 'Xato arizani bekor qilish',
    cancelDefectReportTitle: 'Barcha brak belgilari olib tashlansinmi?',
    cancelDefectReportDescription:
      'Brak arizasi bekor qilinadi va partiya montaj bosqichiga qaytadi. Keyin montajni odatdagi tartibda yakunlash mumkin.',
    cancelDefectReportConfirm: 'Ha, arizani bekor qilish',
    itemQuantity: '{{count}} dona',
    partialInstallTitle: 'Montaj qisman yakunlandi',
    notInstalledTitle: 'Montaj bajarilmadi',
    reinstallTitle: 'Qayta ishlashdan keyin takroriy montaj',
    partialInstallSummary: 'O‘rnatildi: {{installed}} · Qayta ishlashda: {{defect}}',
    returnDestination: { WAREHOUSE: 'Brak omboriga', CONTRACTOR: 'Yetkazib beruvchiga' },
    returnTransport: { INSTALLER: 'Oʻrnatuvchi oʻzi olib boradi', DRIVER: 'Haydovchi' },
    installerReturnHint:
      'Murojaatni mahsulotni brak punktiga amalda topshirayotganda yuboring: yuborishning o‘zi topshirilganini qayd etadi. Qaytarishni alohida tasdiqlash shart emas.',
    installedItemsTitle: 'O‘rnatildi va qabul qilindi',
    defectItemsTitle: 'Qayta ishlashga',
    defectDescriptionTitle: 'Qayta ishlanadigan mahsulotlarga izoh',
    partialActDownload: 'Qisman dalolatnomani yuklab olish',
    partialActDownloadFailed: 'Qisman dalolatnomani yuklab bo‘lmadi',
    returningTo: 'Qaytarish: {{destination}}',
    completeReturn: 'Qaytarilganini tasdiqlash',
    returnCompletedToast: 'Qaytarish tasdiqlandi',
    returnCompleteFailed: 'Qaytarishni tasdiqlab boʻlmadi',
    defectCommentTitle: 'Tanlangan mahsulotlarga izoh · {{count}}',
    defectCommentPlaceholder: 'Tanlangan mahsulotlar bo‘yicha murojaat sababi (majburiy)',
    showItemComment: 'Izoh',
    hideItemComment: 'Izohni yashirish',
    commentPlaceholder: 'Sababga izoh (majburiy emas)',
    sendDefect: 'Brakka yuborish',
    viewPartialActButton: 'Qisman dalolatnomani ko‘rish',
    partialActShort: 'Qisman dalolatnoma',
    viewPartialActsButton: 'Montaj dalolatnomalarini ko‘rish',
    partialActsShort: 'Dalolatnomalar',
    partialActsTitle: 'Imzolangan dalolatnomalar arxivi',
    partialActsHint: 'O‘rnatuvchining yakunlangan tashriflari bo‘yicha mijoz imzolagan hujjatlar.',
    partialActItem: '{{date}} dagi dalolatnoma',
    finishInstallShort: 'Yakunlash',
    addErrorsButton: 'Xatolar qo‘shish',
    addErrorsShort: 'Xatolar qo‘shish',
    viewErrorsButton: 'Xatolarni ko‘rish',
    viewErrorsShort: 'Xatolar',
    defectStage: {
      REPORTED: 'Qayd etildi',
      RETURNING_TO_WAREHOUSE: 'Omborga qaytmoqda',
      IN_DEFECT_WAREHOUSE: 'Brak omborida',
      AWAITING_APPROVAL: 'Qaror kutilmoqda',
      IN_RESOLUTION: 'Tuzatilmoqda',
      READY_FOR_REINSTALL: 'Qayta montajga tayyor',
      CLOSED: 'Yopildi',
      CANCELLED: 'Bekor qilindi',
    },
    defectStageShort: {
      REPORTED: 'Qayd etildi',
      RETURNING_TO_WAREHOUSE: 'Omborga qaytmoqda',
      IN_DEFECT_WAREHOUSE: 'Brak omborida',
      AWAITING_APPROVAL: 'Qaror kutilmoqda',
      IN_RESOLUTION: 'Tuzatilmoqda',
      READY_FOR_REINSTALL: 'Montajga tayyor',
      CLOSED: 'Yopildi',
      CANCELLED: 'Bekor qilindi',
    },
    defectCanChange: 'o‘zgartirish mumkin',
    defectChangeLocked: 'o‘zgartirib bo‘lmaydi',
    defectWillBeAdded: 'Yangi partiya',
    defectSeparateFromPrevious: 'oldingisidan alohida',
    defectWillBeRemoved: 'olib tashlanadi',
    partialActSeparateHint:
      '{{code}} buyurtmasidagi brak ro‘yxatga olindi. Endi mijoz faqat joriy tashrif natijasini tasdiqlaydi.',
    partialActSignedToast: 'Qisman dalolatnoma imzolandi',
    partialActSignFailed: 'Qisman dalolatnomani imzolab bo‘lmadi',
    updateDefect: 'Xatolar ro‘yxatini yangilash',
    updatedToast: 'Xatolar ro‘yxati yangilandi',
    continueToPartialAct: 'Qisman dalolatnomaga o‘tish',
    backToIssue: 'Xatoga qaytish',
    signAndSubmit: 'Imzolash va brak haqida xabar berish',
    submit: 'Yuborish',
    sending: 'Yuborilmoqda…',
    doneToast: 'Xabar yuborildi',
    failToast: 'Yuborib boʻlmadi',
  },
  productionIssue: {
    title: 'Ishlab chiqara olmayman',
    intro:
      '{{code}} buyurtmasi. Muammoni yozing — menejer xabar oladi va brakni «Taqsimlash»da hal qiladi.',
    reason: {
      MATERIAL: { title: 'Material braki', hint: 'Nuqsonli material / mato keldi' },
      WRONG_SIZE: {
        title: 'Oʻlcham yoki rang notoʻgʻri',
        hint: 'Oʻlcham/rang buyurtmaga mos emas',
      },
      DAMAGE: { title: 'Ishlab chiqarishda shikastlandi', hint: 'Jarayonda buzildi' },
      OTHER: { title: 'Boshqa', hint: 'Vaziyatni izohda yozing' },
    },
    descriptionPlaceholder: 'Aynan nima notoʻgʻri (majburiy)…',
    submit: 'Brak haqida xabar berish',
    sending: 'Yuborilmoqda…',
    doneToast: 'Brak qayd etildi, menejer xabardor',
    failToast: 'Yuborib boʻlmadi',
  },
  productionDash: {
    title: 'Sex',
    subtitle: 'Tayyorlikni tasdiqlash tarixim',
    scopeLabel: 'Ishlab chiqarish turi',
    scopeAll: 'Barchasi',
    scopeOwn: 'Oʻz sexi',
    scopeContractors: 'Pudratchilar',
    historyTitle: 'Men tasdiqlaganlar',
    historyEmpty: 'Tanlangan davrda tasdiqlash yoʻq',
    historyEmptyHint: 'Field ilovasida «Tayyor» bosgan ishlaringiz shu yerda chiqadi.',
    completedAt: 'Tayyor: {{date}}',
  },
  productionQueue: {
    ownTitle: 'Oʻz sexi',
    ownSubtitle: 'Ichki sex ishlari · {{count}}',
    contractorsTitle: 'Pudratchilar',
    contractorsSubtitle: 'Tashqi ijrochilar ishlari · {{count}}',
    searchPlaceholder: 'Qidirish: buyurtma raqami yoki mijoz',
    searchEmpty: 'Hech narsa topilmadi',
    searchEmptyHint: 'Buyurtma raqami yoki mijoz ismini tekshiring.',
    loadFailed: 'Navbatni yuklab boʻlmadi',
    ownEmpty: 'Ichki sexda faol ish yoʻq',
    contractorsEmpty: 'Pudratchilarda faol ish yoʻq',
    emptyHint: 'Yangi topshiriqlar menejer taqsimlaganidan keyin chiqadi.',
    customerMissing: 'Mijoz koʻrsatilmagan',
    contractor: 'Sex: {{name}}',
    branch: 'Filial: {{name}}',
    branchMissing: 'Filial ko‘rsatilmagan',
    deadline: 'Muddat: {{date}}',
    driver: 'Haydovchi: {{name}}',
    telegramOrField: 'Tayyorlikni shu yerda yoki pudratchi Telegram orqali tasdiqlash mumkin.',
    fieldOnly: 'Tayyorlik faqat shu yerda tasdiqlanadi.',
    waitingLabel: 'Kutmoqda:',
    waitProduction: 'ishlab chiqarish xodimi tasdig‘ini',
    waitContractor: '{{name}} · tayyorlik tasdig‘i',
    waitOtherProduction: '{{names}} · qolgan qismlarning tayyorligi',
    waitRemainingDistribution: 'qolgan tarkib taqsimlanishini',
    waitDriverAssignment: 'haydovchi tayinlanishini',
    waitDriver: 'haydovchi · mahsulotni olib ketish',
    waitDriverNamed: '{{name}} · mahsulotni olib ketish',
    waitWarehouseNamed: '{{names}} · tayyor mahsulot qabuli{{warehouse}}',
    waitWarehouseUnassigned: 'ombor mudiri tayinlanmagan · tayyor mahsulot qabuli{{warehouse}}',
    waitWarehouseRouteMissing: 'filial uchun tayyor mahsulot ombori sozlanmagan',
    markReady: 'Tayyor',
    confirming: 'Tasdiqlanmoqda…',
    confirmTitle: 'Tayyorlik tasdiqlansinmi?',
    confirmHint:
      '{{code}} · {{contractor}}. Ish keyingi bosqichga oʻtadi, bekor qilish faqat masʼul xodimlarda mavjud.',
    confirm: 'Tasdiqlash',
    ownReadyDone: 'Ichki sex tayyorligi tasdiqlandi',
    contractorReadyDone: 'Pudratchi tayyorligi tasdiqlandi',
    actionFailed: 'Tayyorlikni tasdiqlab boʻlmadi',
    status: {
      IN_PRODUCTION: 'Ishda',
      OWN_READY: 'O‘z qismi tayyor',
      READY_NEEDS_DRIVER: 'Tayyor · haydovchi kerak',
      READY_WAITING_DRIVER: 'Haydovchiga ariza',
    },
  },
  productionGate: {
    unassigned: 'Buyurtma hali sexga taqsimlanmagan — menejer tayinlashini kuting.',
    defect: 'Brak qayd etilgan — «Taqsimlash»da menejer qarorini kutmoqda.',
  },
  cashdesk: {
    title: 'Kassa',
    target: {
      title: 'Pul qayerga tushdi',
      account: 'Hisob',
      method: 'Toʻlov usuli',
      date: 'Tushgan sana',
      confirm: 'Tasdiqlash',
      noAccounts: 'Kompaniya hisoblari yoʻq. Ularni admin panelda yarating: Sozlamalar → Moliya → Hisoblar.',
    },
    subtitle: 'Smenalar, tushumlar, arizalar',
    tabShifts: 'Smenalar',
    tabReceipts: 'Tushumlar',
    tabClaims: 'Arizalar',
    filter: { unconfirmed: 'Tasdiqlanmagan', confirmed: 'Tasdiqlangan', all: 'Barchasi' },
    emptyShifts: 'Tasdiqlash uchun smena yoʻq',
    emptyShiftsHint: 'Xodim kassani topshirsa — smena shu yerda paydo boʻladi.',
    emptyReceipts: 'Toʻlovlar yoʻq',
    emptyClaims: 'Arizalar yoʻq',
    emptyClaimsHint: 'Mijozlarning toʻlov arizalari shu yerda chiqadi.',
    loadFailed: 'Maʼlumotlarni yuklab boʻlmadi',
    rowsCappedQueue: 'Solishtirish uchun eng eski 500 ta toʻlov koʻrsatilgan. Keyingilarini koʻrish uchun ularni qayta ishlang.',
    rowsCappedPeriod: 'Faqat 500 ta toʻlov koʻrsatilgan. Qolganlarini koʻrish uchun davrni qisqartiring.',
    shiftTitle: 'Kassa smenasi',
    paymentsN: '{{n}} ta toʻlov',
    approve: 'Tasdiqlash',
    reject: 'Rad etish',
    confirm: 'Tasdiqlash',
    confirmed: 'Tasdiqlandi',
    receivedBy: 'qabul qildi: {{name}}',
    rejectTitle: 'Rad etish sababi',
    rejectPlaceholder: 'Nima notoʻgʻri ekanini yozing…',
    rejectSubmit: 'Rad etish',
    shiftApproved: 'Smena tasdiqlandi',
    receiptConfirmed: 'Toʻlov tasdiqlandi',
    receiptUnconfirmed: 'Tasdiq olib tashlandi',
    claimConfirmed: 'Ariza tasdiqlandi',
    claimConfirmTitle: 'Tushumni tasdiqlaysizmi?',
    claimConfirmHint: 'Pul kelganini bank koʻchirmasi yoki kassa orqali tekshiring. Chek rasmi buning oʻzi uchun yetarli tasdiq emas.',
    confirmArrival: 'Pul kelib tushdi',
    orderReceipts: 'Buyurtma · cheklar: {{count}}',
    claimReceipts: 'Ariza cheklari: {{count}}',
    unlinkedOrderReceipts: 'Bogʻlanmagan buyurtma cheklari: {{count}}',
    noClaimReceipt: 'Ariza cheki yuklanmagan',
    openClaimReceipt: 'Ariza chekini ochish',
    rejected: 'Rad etildi',
    actionFailed: 'Bajarib boʻlmadi',
    status: { PENDING: 'Tekshiruvda', APPROVED: 'Tasdiqlangan', REJECTED: 'Rad etilgan' },
    method: {
      CASH: 'Naqd',
      CARD: 'Karta',
      CARD_P2P: 'Karta P2P',
      CARD_TERMINAL: 'Terminal',
      TRANSFER: 'Pul oʻtkazma',
      UZUM_NASIYA: 'UZUM orqali toʻlov',
      ALIF_NASIYA: 'Alif Nasiya orqali toʻlov',
    },
  },
  accountantDash: {
    title: 'Moliya',
    subtitle: 'Xulosa va tasdiqlash navbatlari',
    shifts: 'Smenalar',
    receipts: 'Tushum.',
    claims: 'Arizalar',
    cashPending: 'Tasdiqlashda',
    cashPendingHint: '{{n}} ta toʻlov kutmoqda',
    receivables: 'Debitorlik',
    receivablesHint: '{{n}} ta qarzdor',
    revenue: 'Tushum',
    revenueHint: 'Davr uchun toʻlangan',
    netProfit: 'Sof foyda',
    netProfitHint: 'Xarajatlardan keyin',
    confirmedReceipts: 'Tasdiqlangan',
    confirmedReceiptsHint: 'Davrdagi toʻlovlar',
    outflows: 'Xarajat va qaytarimlar',
    outflowsHint: 'Davr uchun',
    loadError: 'Barcha moliyaviy maʼlumot yuklanmadi. Sahifani yangilang.',
  },
  measureContact: {
    newDateQuestion: 'Mijoz yangi sanani tasdiqladimi?',
    yes: 'Ha', no: 'Yoʻq',
    open: 'Qoʻngʻiroqni belgilash', confirmed: 'Mijoz tasdiqladi',
    awaitingCall: 'Qoʻngʻiroq kutilmoqda', visitCancelled: 'Oʻlchov bekor qilindi',
    NO_ANSWER: 'Bogʻlana olmadim', RESCHEDULE_REQUESTED: 'Koʻchirish kerak',
    CLIENT_DECLINED: 'Mijoz oʻlchovdan voz kechdi', WRONG_ADDRESS: 'Manzil notoʻgʻri', OTHER: 'Yordam kerak',
  },
  action: {
    start_measure: 'Oʻlchovni boshlash',
    continue_measure: 'Oʻlchovni davom ettirish',
    edit_measure: 'Oʻlchovni oʻzgartirish',
    confirm_measure: 'Oʻlchovga chiqaman',
    confirm_measure_confirm_title: 'Oʻlchovga chiqasizmi?',
    confirm_measure_confirm_desc: 'Mijoz tayyor — jadval boʻyicha boramiz.',
    complete_measure: 'Oʻlchovni yakunlash',
    complete_measure_confirm_title: '«Oʻlchov olindi» deb belgilansinmi?',
    complete_measure_confirm_desc:
      'Oʻlchamlar kiritildi, oʻlchov yopiladi. Buyurtma keyingi bosqichga oʻtadi.',
    formalize: 'Rasmiylashtirildi',
    formalize_confirm_title: 'Buyurtma rasmiylashtirilsinmi?',
    formalize_confirm_desc:
      'Barcha xonalar ishga ketadi, oʻlchov tahriri imkonsiz boʻladi. Rasmiylashtirish faqat minimal avansdan keyin mumkin.',
    not_ordered: 'Fikr-mulohaza',
    plan_reject: 'Mos kelmaydi',
    send_to_production: 'Ishlab chiqarishga',
    send_to_production_confirm_title: 'Ishlab chiqarishga yuborilsinmi?',
    send_to_production_confirm_desc: 'Oʻlchov qayd etiladi, oʻzgartirib boʻlmaydi.',
    accept_install: 'Montajni yakunlash',
    accept_install_confirm_title: 'Montaj yakunlansinmi?',
    reopen_installation: 'Yakunlashni bekor qilish',
    reopen_installation_short: 'Bekor qilish',
    reopen_installation_confirm_title: 'Montaj yakunlanishini bekor qilamizmi?',
    reopen_installation_confirm_desc:
      'Montaj yana faol bo‘ladi. Natijani tuzatib, qayta yakunlash mumkin. To‘lov, rasmlar, dalolatnomalar va belgilangan vaqt saqlanadi.',
    reopen_installation_audit_reason: 'Montajchi xato yakunlagan montajni tuzatish',
    reopen_installation_done: 'Yakunlash bekor qilindi — montaj yana faol',
    reopen_installation_failed: 'Yakunlashni bekor qilib bo‘lmadi',
    client_refusal_title: 'Mijoz rad etgani sababli buyurtma bekor qilinsinmi?',
    client_refusal_description:
      'Buyurtma «Rad etildi» holatiga o‘tadi va faol montajdan yo‘qoladi. To‘lov, rasmlar, hujjatlar va tarix saqlanadi.',
    client_refusal_reason: 'Rad etish sababi',
    client_refusal_placeholder: 'Masalan: mijoz fikridan qaytdi va montajdan voz kechdi…',
    client_refusal_hint: 'Haqiqiy sababni yozing — menejer uni buyurtma tarixida ko‘radi.',
    client_refusal_confirm: 'Rad etishni tasdiqlash',
    client_refusal_done: 'Mijozning rad javobi qayd etildi',
    client_refusal_failed: 'Buyurtmani bekor qilib bo‘lmadi',
    install_no_answer: 'Aloqaga chiqmadi',
    install_no_answer_confirm_title: 'Aloqaga chiqmaganini qayd qilamizmi?',
    install_no_answer_confirm_desc: 'Menejer mijoz montaj boʻyicha javob bermayotganini koʻradi.',
    complete: 'Yakunlash',
    complete_confirm_title: 'Buyurtma yakunlansinmi?',
    complete_confirm_desc: 'Shundan soʻng faqat admin orqali qaytarish mumkin.',
    accept_payment: 'Toʻlovni qabul qilish',
    sign_act: 'Aktni imzolash',
    download_act: 'Aktni yuklab olish',
    mark_produced: 'Tayyor deb belgilash',
    mark_produced_confirm_title: 'Buyurtma tayyormi?',
    mark_produced_confirm_desc:
      'Menejer xabar oladi — haydovchini yuborish va montaj biriktirish mumkin.',
    report_defect: '⚠ Ishlab chiqara olmayman (brak)',
    problem: '⚠ Nimadir notoʻgʻri ketdi',
    problem_install: '⚠ Qaytarish / muammo',
  },
  reject: {
    title: 'Mos kelmaydimi?',
    intro:
      'Nega boʻlmaydi? Boshqa mijozga ketsangiz — kimga ekanini koʻrsating, menejer kun almashishni taklif qiladi.',
    mode_client: 'Boshqa mijozga ketyapman',
    mode_other: 'Boshqa',
    search_client: 'Qidiruv: mijoz, manzil',
    loading: 'Yuklanmoqda…',
    nothing: 'Hech narsa topilmadi',
    placeholder: 'Masalan: shu vaqtda boshqa tumanda uchrashuv bor',
    submit: 'Rad etishni yuborish',
    saving: 'Yuborilmoqda…',
    saved: 'Rad etish menejerga yuborildi',
    failed: 'Yuborib boʻlmadi',
  },
  errors: {
    money_account_required: 'Tushum hisobini tanlang.',
    money_account_not_found: 'Hisob topilmadi. Roʻyxatni yangilab, boshqasini tanlang.',
    money_account_archived: 'Hisob arxivlangan. Roʻyxatni yangilab, boshqasini tanlang.',
    money_account_method_mismatch: 'Hisob bu toʻlov usuliga mos emas. Boshqasini tanlang.',
    money_account_branch_mismatch: 'Buyurtma filialining yoki firmaning umumiy hisobini tanlang.',
    money_account_access_denied: 'Bu hisobga ruxsatingiz yoʻq. Roʻyxatni yangilab, boshqasini tanlang.',
    company_money_account_required: 'Firma hisobini tanlang.',
    invalid_receiving_account: 'Terminal tushum hisobi mavjud emas. Roʻyxatni yangilang yoki boshqa terminalni tanlang.',
    installation_proposal_changed: "O‘rnatuvchi taklifi o‘zgardi. Joriy variantni ochib qayta tekshiring.",
    installation_proposal_review_required: "Avval o‘rnatuvchi taklifini ko‘rib chiqing: tuzatilgan variantni qabul qiling yoki dastlabki komplektatsiyani izoh bilan tasdiqlang.",
    installation_proposal_correction_required: "Avval tuzatilgan komplektatsiyani solishtirib saqlang.",
    installation_proposal_has_correction: "Tuzatilgan variant saqlangan. Qarorni tasdiqlashdan oldin uni tekshiring.",
    installation_customer_change_requires_agreement: "Mijoz bilan kelishilgan shartlar yoki tekshiruv natijasini yozing. Summalar va to‘lovlar alohida kelishiladi.",
    installation_correction_requires_new_manufacture: "O‘lchamni kattalashtirish yoki mahsulot turini almashtirish uchun yangi ishlab chiqarishni tanlang.",
    installation_proposal_replacement_required: "Tuzatilgan variantni kiriting yoki «Qayta o‘lchash kerak»ni tanlang.",
    installation_proposal_photo_invalid: "Surat bu buyurtmada topilmadi. Uni qayta biriktiring.",
    installation_proposal_sign_separately: "Avval xato va tuzatishlarni yuboring, keyin qisman montaj dalolatnomasini imzolang.",
    installation_proposal_requires_product_problem: "Tuzatishlar faqat mahsulot nuqsoni xabariga biriktiriladi.",
    measurement_changed: 'O‘lchov o‘zgargan yoki alohida oynalarga ajratilgan. Kiritilgan ma’lumotlar shaklda yoki navbatda saqlangan; buyurtmani yangilab, kerakli oynani tekshiring.',
    invalid_window_count: '1 dan 999 gacha butun son kiriting.',
    single_window_quantity_locked: 'Saqlangan o‘lchovda bitta oyna bor. Yangi oynalar uchun nusxalashdan foydalaning.',
    measurement_correction_item_changed: "Mahsulot parametrlari o‘zgardi. Shaklni qayta ochib tekshiring.",
    measurement_corrections_not_available: "O‘zgarishlar qaror yoki imzolangan dalolatnoma bilan bloklangan. Rahbarga murojaat qiling.",
    measurement_correction_quantity_locked: "Miqdor ushbu nuqson ishining mahsulotlar soniga mos bo‘lishi kerak.",
    defect_correction_scope_changed: "Tarkib yoki miqdor o‘zgardi. Ma’lumotlarni yangilab mahsulotlarni tekshiring.",
    _unknown: 'Nimadir xato ketdi',
    _fallback: 'Amalni bajarib boʻlmadi. Qayta urinib koʻring yoki menejerga murojaat qiling.',
    insufficient_role: 'Bu amal uchun huquq yetarli emas.',
    daily_close_total_changed: 'Tekshirayotganingizda topshirish summasi oʻzgardi. Yangilab, qayta qabul qiling.',
    not_authenticated: 'Sessiya tugadi — qaytadan kiring.',
    _maxValue: '{{field}}: {{limit}} {{unit}} dan katta boʻlmasin',
    _minValue: '{{field}}: {{limit}} {{unit}} dan kichik boʻlmasin',
    _fieldWidth: 'Eni',
    _fieldHeight: 'Boʻyi',
    _fieldSlat: 'Planka oʻlchami',
    _fieldBort: 'Bort oʻlchami',
    _fieldQty: 'Miqdori',
    _unitM: 'm',
    _unitMm: 'mm',
    _unitPcs: 'dona',
    invalid_credentials: 'Notoʻgʻri telefon yoki parol',
    forbidden: 'Bu amal uchun ruxsat yoʻq',
    no_access: 'Bu boʻlimga kirish yoʻq',
    not_found: 'Topilmadi',
    dev_login_disabled_in_prod: 'Tezkor kirish mavjud emas',
    multibranch_disabled: 'Filiallar boʻlimi oʻchirilgan',
    user_not_found: 'Bunday raqamli xodim topilmadi',
    user_deactivated: 'Kirish yopiq. Administrator bilan bogʻlaning.',
    user_inactive: 'Hisob bloklangan',
    telegram_not_bound: 'Telegram ulanmagan. Admindan taklif havolasini soʻrang.',
    code_invalid: 'Notoʻgʻri kod',
    code_expired: 'Kod muddati tugadi, yangisini soʻrang',
    too_many_attempts: 'Juda koʻp urinish. Yangi kod soʻrang.',
    pin_invalid: 'Notoʻgʻri PIN',
    pin_locked: 'Bir necha muvaffaqiyatsiz urinishdan soʻng PIN 2 daqiqaga bloklandi',
    session_revoked: 'Bu sessiya bekor qilingan. Telefon orqali qayta kiring.',
    session_expired: 'Sessiya muddati tugadi. Qayta kiring.',
    invalid_transition: 'Bu holatni bu yerdan oʻzgartirib boʻlmaydi',
    result_photos_required:
      'Avval tayyor ish suratini yuklang — ularsiz montajni yakunlab boʻlmaydi',
    full_payment_required: 'Yakunlashdan oldin buyurtmaning toʻliq toʻlovini qabul qiling',
    order_not_found: 'Buyurtma topilmadi',
    room_not_found: 'Xona topilmadi',
    room_name_required: 'Xona nomini kiriting',
    room_name_exists: 'Bunday nomli xona allaqachon bor',
    item_not_found: 'Mahsulot topilmadi',
    photo_not_found: 'Surat topilmadi',
    order_in_final_status: 'Buyurtma allaqachon yopilgan — oʻzgartirib boʻlmaydi',
    completed_installation_not_reopenable:
      'Bu montajni endi ishga qaytarib bo‘lmaydi. Kartani yangilang.',
    completed_installation_assigned_installer_only:
      'Yakunlashni faqat tayinlangan o‘rnatuvchi bekor qilishi mumkin.',
    installation_assigned_installer_only:
      'Rad etishni faqat tayinlangan o‘rnatuvchi qayd qilishi mumkin.',
    installation_client_refusal_reason_required: 'Mijozning rad etish sababini kiriting.',
    order_calculation_locked: 'Bu buyurtma hisobi oʻzgartirish uchun yopiq',
    order_discount_locked: 'Chegirma va ustamani buyurtma yakunlanib, dalolatnoma imzolanguncha oʻzgartirish mumkin',
    discount_below_paid_amount: 'Buyurtmaning jami summasi qabul qilingan toʻlovdan kam boʻlishi mumkin emas',
    pb_cell_empty: 'Tanlangan variant uchun narx ko‘rsatilmagan.',
    pb_option_incomplete_ranges:
      'To‘g‘ri hisoblash uchun barcha oraliqlarning narxi ko‘rsatilishi kerak.',
    items_required: 'Kamida bitta mahsulot qoʻshing',
    address_required: 'Manzilni kiriting',
    customer_name_and_phone_required: 'Mijoz ismi va telefonini kiriting',
    customer_not_found: 'Mijoz topilmadi',
    refusal_code_required: 'Sababni tanlang',
    comment_required: 'Izoh qoʻshing',
    defect_reason_required: 'Brak sababini kiriting',
    qc_defect_items_required: 'Kamida bitta aniq brak mahsulotni tanlang.',
    qc_defect_items_only_for_defect: 'Mahsulotlarni faqat “Brak” qarori uchun tanlash mumkin.',
    qc_defect_item_duplicated: 'Bitta mahsulot bir necha marta tanlangan.',
    qc_defect_item_outside_assignment: 'Tanlangan mahsulot bu pudratchi topshiruviga kirmaydi.',
    defect_item_not_in_physical_unit:
      'Tanlangan mahsulot tekshirilayotgan buyurtma yoki partiyaga kirmaydi.',
    installer_defect_comment_required: 'Oʻrnatuvchi aniqlagan nuqsonni yozing (kamida 3 ta belgi).',
    installer_defect_group_locked: 'Bu guruh tuzatishga yuborilgan va endi o‘zgartirilmaydi.',
    partial_installation_act_not_found:
      'Qisman dalolatnoma topilmadi yoki allaqachon bekor qilingan.',
    partial_installation_act_installer_only:
      'Imzoni faqat dalolatnomani rasmiylashtirgan o‘rnatuvchi bekor qilishi mumkin.',
    partial_installation_act_locked:
      'Brak bo‘yicha qaror boshlangan — Field orqali imzoni bekor qilib bo‘lmaydi.',
    partial_installation_act_stale:
      'Ish ma’lumotlari o‘zgargan. Kartani yangilang va qayta tekshiring.',
    acceptance_act_required: 'Mijoz imzolagan qabul dalolatnomasi kerak',
    acceptance_act_not_signed: 'Mijoz hali qabul dalolatnomasini imzolamagan',
    invalid_measurer: 'Bu xodimni oʻlchovchi etib tayinlab boʻlmaydi',
    measurer_has_open_order: 'Xodimda yopilmagan oʻlchov bor',
    measure_assignment_horizon: 'O‘lchovchi faqat bugun yoki ertaga tayinlanadi. Keyingi sanaga o‘lchovni ijrochisiz saqlang.',
    measure_assignment_date_required: 'Avval o‘lchov sanasini kiriting.',
    measurer_cash_unsettled: 'O‘tgan kunlar kassasi hali qabul qilinmagan. Ertangi yangi o‘lchov kassa adminkada qabul qilingandan keyin tayinlanadi.',
    client_quotes_paused: 'Ilova orqali narx hisoblash soʻrovlari vaqtincha toʻxtatilgan.',
    measurer_has_unpaid_order: 'Xodimda toʻlovi yopilmagan buyurtma bor',
    earlier_install_not_closed: 'Avval oldingi montajni yoping',
    cross_branch_assignment: 'Boshqa filial xodimini tayinlab boʻlmaydi',
    invalid_width: 'Notoʻgʻri kenglik',
    invalid_height: 'Notoʻgʻri balandlik',
    invalid_quantity: 'Notoʻgʻri miqdor',
    invalid_amount: 'Notoʻgʻri summa',
    amount_must_be_positive: 'Summa noldan katta boʻlishi kerak',
    payment_exceeds_order_due:
      'Summa buyurtma qoldigʻidan oshib ketdi. Ortiqcha pulni qabul qilib boʻlmaydi.',
    amount_must_be_non_negative: 'Summa manfiy boʻlishi mumkin emas',
    order_not_billable:
      'Avansni faqat oʻlchov yakunlangandan keyin qabul qilish mumkin. Avval «Oʻlchovni yakunlash» tugmasini bosing.',
    invalid_discount: 'Notoʻgʻri chegirma',
    discount_exceeds_limit: 'Chegirma ruxsat etilgan chegaradan oshib ketdi',
    invalid_price: 'Notoʻgʻri narx',
    no_file: 'Fayl tanlanmagan',
    not_an_image: 'Faqat rasm yuklash mumkin',
    invalid_extension: 'Qoʻllab-quvvatlanmaydigan fayl formati',
    invalid_kind: 'Notoʻgʻri surat turi',
    code_taken: 'Bunday kod allaqachon ishlatilgan',
    code_already_used: 'Bunday kod allaqachon ishlatilgan',
    name_required: 'Nomni kiriting',
    name_already_used: 'Bunday nom allaqachon mavjud',
    name_already_used_in_city: 'Bu shaharda bunday nom allaqachon bor',
    has_dependencies: 'Oʻchirib boʻlmaydi: bogʻliq yozuvlar bor',
    in_use: 'Oʻchirib boʻlmaydi: yozuv ishlatilmoqda',
    cannot_unset_default: 'Boshqa filial tayinlamasdan «asosiy»ni olib boʻlmaydi',
    branch_not_found: 'Filial topilmadi',
    city_not_found: 'Shahar topilmadi',
    expense_not_found: 'Xarajat topilmadi',
    dealer_not_found: 'Diler topilmadi',
    defect_case_not_found: 'Nuqson ishi topilmadi.',
    defect_warehouse_item_not_found: 'Bu nuqson uchun ombor yozuvi topilmadi.',
    qc_verdict_requires_qc_or_installation_case:
      'OTK izohi faqat OTK yoki montajda topilgan brak uchun mavjud.',
    qc_verdict_already_confirmed: 'OTK izohi o‘rnatish rahbariga allaqachon yuborilgan.',
    qc_verdict_required_first: 'Avval OTK mahsulotni qabul qilib, izoh qoldirishi kerak.',
    qc_role_required: 'Bu amal faqat OTK xodimi uchun mavjud.',
    manager_resolution_must_be_rework_or_new_order:
      'Hozirgi mahsulotni qayta ishlash yoki yangi buyurtma yaratishni tanlang.',
    manager_resolution_requires_qc_or_installation_case:
      'Bu qaror faqat OTK yoki montajda topilgan brak uchun mavjud.',
    manager_resolution_already_confirmed:
      'Sabab va yo‘nalish o‘rnatish rahbari tomonidan allaqachon tasdiqlangan.',
    installation_manager_role_required: 'Bu amal faqat o‘rnatish rahbari uchun mavjud.',
    resolution_due_at_must_be_in_future: 'Qayta ishlash muddati kelajakda bo‘lishi kerak.',
    driver_and_due_date_only_for_rework:
      'Haydovchi va muddat faqat hozirgi mahsulotni qayta ishlash uchun ko‘rsatiladi.',
    defect_item_already_processed: 'Mahsulot keyingi bosqichga allaqachon o‘tkazilgan.',
    defect_item_must_be_received_first: 'Avval mahsulotni nuqsonlar omboriga qabul qiling.',
    defect_item_already_received: 'Mahsulot nuqsonlar omboriga allaqachon qabul qilingan.',
    defect_item_not_received: 'Mahsulot nuqsonlar omboriga hali qabul qilinmagan.',
    defect_item_must_be_in_warehouse: 'Amal faqat nuqsonlar omboridagi mahsulot uchun mavjud.',
    defect_storage_location_required: 'Mahsulotning amaldagi saqlash joyini kiriting.',
    defect_handover_at_invalid: 'Mahsulot topshirilgan sana va vaqtni to‘g‘ri kiriting.',
    defect_handover_at_in_future: 'Topshirish vaqti kelajakda bo‘lishi mumkin emas.',
    defect_transferor_required: 'Mahsulotni topshirgan xodimni tanlang.',
    defect_transferor_invalid: 'Tanlangan xodim bu mahsulotni topshira olmaydi.',
    defect_receipt_forbidden:
      'Brakni faqat o‘rnatish rahbari yoki administrator qabul qilishi mumkin.',
    defect_receipt_operator_invalid: 'Qabulni o‘tkazayotgan xodimni aniqlab bo‘lmadi.',
    warehouse_manager_required: 'Mahsulotni qabul qilgan xodimni tanlang.',
    warehouse_manager_invalid: 'Tanlangan xodim mahsulotni qabul qila olmaydi.',
    defect_disposition_already_decided:
      'Yo‘nalish allaqachon o‘tkazilgan. O‘zgartirish uchun avval o‘tkazishni bekor qiling.',
    defect_driver_return_already_accepted:
      'Haydovchi mahsulotni qabul qilgan. Yuborishni bekor qilish yoki o‘zgartirish mumkin emas.',
    defect_driver_return_already_reassigned: 'Vazifa allaqachon haydovchiga biriktirilgan. Ro‘yxatni yangilang.',
    defect_driver_return_not_waiting: 'Qaytarish endi haydovchini kutmayapti. Ro‘yxatni yangilang.',
    defect_driver_return_not_available: 'Bu qaror uchun haydovchiga qayta yuborish mavjud emas.',
    return_driver_required: 'Mahsulotni pudratchiga qaytarish uchun haydovchini tanlang.',
    return_driver_not_found:
      'Tanlangan haydovchi mavjud emas. Ro‘yxatni yangilab, boshqasini tanlang.',
    resolution_due_at_required: 'Qayta ishlashning rejalashtirilgan tayyorlik sanasini kiriting.',
    original_contractor_required: 'Mahsulotning dastlabki pudratchisi ko‘rsatilmagan.',
    original_contractor_not_found: 'Dastlabki pudratchi topilmadi yoki o‘chirilgan.',
    target_contractor_required: 'Yangi pudratchini tanlang.',
    target_contractor_not_found: 'Tanlangan pudratchi topilmadi yoki o‘chirilgan.',
    target_contractor_must_be_different:
      'Yangi pudratchi dastlabki pudratchidan farq qilishi kerak.',
    defect_case_has_no_stage_to_rollback: 'Bu ishda bekor qilinadigan amal yo‘q.',
    defect_stage_rollback_forbidden: 'Joriy bosqichni bekor qilish huquqingiz yo‘q.',
    defect_case_already_approved: 'Qaror allaqachon tasdiqlangan.',
    defect_case_not_approved: 'Avval nuqson bo‘yicha qarorni tasdiqlang.',
    defect_case_must_be_approved: 'Avval nuqson bo‘yicha qarorni tasdiqlang.',
    defect_case_not_ready_to_close:
      'Ishni hali yopib bo‘lmaydi: majburiy bosqichlar tugallanmagan.',
    defect_cause_not_classified: 'Avval nuqson sababi va javobgarlarni ko‘rsating.',
    defect_resolution_missing: 'Avval mahsulot yo‘nalishini tanlang.',
    defect_resolution_already_started: 'Qaror allaqachon bajarishga yuborilgan.',
    defect_product_already_manufactured:
      'Mahsulot allaqachon tayyorlangan, o‘zgartirish kiritib bo‘lmaydi.',
    warehouse_disposition_not_selected:
      'O‘rnatish rahbari mahsulotning keyingi harakatini hali tanlamagan.',
    write_off_must_be_completed_first: 'Avval mahsulotni jismonan hisobdan chiqaring.',
    write_off_not_selected: 'Mahsulot yo‘nalishida hisobdan chiqarish tanlanmagan.',
    resolution_not_selected: 'Avval mahsulotning keyingi harakatini tanlang.',
    install_return_not_waiting_driver:
      'Ariza o‘zgargan va endi haydovchini kutmayapti. Ro‘yxatni yangilang.',
    install_return_not_in_transit:
      'Qaytarish yakunlangan yoki haydovchi hali qabul qilmagan. Ro‘yxatni yangilang.',
    install_return_assignee_only: 'Bu qaytarishni faqat tayinlangan xodim tasdiqlashi mumkin.',
    invalid_driver: 'Haydovchi rolidagi faol xodimni tanlang.',
    invalid_warehouse_manager: 'Omborning faol xodimini tanlang.',
    client_rejection_assessment_required:
      'Mijoz rad etganda reglament tekshiruvi natijasini ko‘rsating.',
    client_rejection_company_responsibility_required:
      'Reglament bajarilgan bo‘lsa, javobgarlik kompaniyaga biriktirilishi kerak.',
    client_rejection_measurer_responsibility_required:
      'Mijoz noto‘g‘ri xabardor qilingan bo‘lsa, javobgarlik o‘lchovchiga biriktirilishi kerak.',
    contractor_and_due_date_required: 'Pudratchi va rejalashtirilgan tayyorlik sanasini kiriting.',
    defect_execution_snapshot_invalid:
      'Qarorni ishga tushirish ma’lumotlari buzilgan. Administratorga murojaat qiling.',
    defect_execution_snapshot_missing:
      'Ishga tushirishning dastlabki ma’lumotlarini tiklab bo‘lmadi. Administratorga murojaat qiling.',
    installer_not_found: 'O‘rnatuvchi topilmadi yoki mavjud emas.',
    manufacturing_responsible_required:
      'Ishlab chiqarish nuqsoni uchun javobgar pudratchi yoki kompaniyani ko‘rsating.',
    original_item_must_be_written_off_first:
      'Avval dastlabki mahsulotni to‘liq hisobdan chiqaring.',
    other_cause_description_required: 'Boshqa sabab uchun batafsil izoh kiriting.',
    own_workshop_manufacturing_company_full_responsibility_required:
      'O‘z seximizdagi nuqsonni kompaniya to‘liq qoplashi kerak.',
    own_workshop_qc_responsibility_not_applicable:
      'O‘z seximizdagi nuqson uchun SNTning alohida javobgarligi qo‘llanmaydi.',
    qc_fixed_penalty_required: 'SNT javobgarligi uchun belgilangan jarimani kiriting.',
    qc_responsibility_requires_qc_employee: 'SNT javobgarligi uchun SNT rolidagi xodimni tanlang.',
    replacement_order_must_belong_to_same_customer:
      'Almashtirish buyurtmasi shu mijozga tegishli bo‘lishi kerak.',
    replacement_order_required: 'Bog‘langan almashtirish buyurtmasini tanlang yoki yarating.',
    responsible_contractor_not_found: 'Javobgar pudratchi topilmadi yoki o‘chirilgan.',
    responsible_user_not_found: 'Javobgar xodim topilmadi yoki o‘chirilgan.',
    settlement_amount_must_be_positive: 'Hisoblash summasi noldan katta bo‘lishi kerak.',
    use_correct_decision_for_approved_case:
      'Tasdiqlangan qarorni «Qarorni tuzatish» amali orqali o‘zgartiring.',
  },
};
const initial = persistent.getLanguage();
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
      uz: { translation: uz },
    },
    lng: initial,
    fallbackLng: 'ru',
    supportedLngs: ['ru', 'uz'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'jb:lang',
      caches: ['localStorage'],
    },
  });
export function getLang(): Lang {
  return (i18n.language as Lang) || 'ru';
}
export function setLang(l: Lang): void {
  void i18n.changeLanguage(l);
  persistent.setLanguage(l);
}
export { i18n };
